/**
 * game-store.ts — Zustand UI store for WEB.4 hot-seat board.
 *
 * Sub-phase history (most recent first):
 *
 * WEB.4.UI.9b — Toll-on-landing query:
 *  - rollDice: drawer pays toll when landing on opponent's facility (clamped at balance)
 *  - confirmTilePick Teleport: same toll block (codex R1 MED — exploit otherwise)
 *  - computeTollOnLanding helper in aura-tick.ts
 *
 * WEB.4.UI.9a — Turn-end Energy / Pollution tick:
 *  - endTurn accrues Energy + Pollution from facilities owned by ending seat
 *  - aura-tick.ts: computeEnergyTick / computePollutionTick / computeTurnEndDeltas
 *  - buildFacility now writes ownedBy[tileIdx] = seat (codex R1 HIGH — was filter mismatch)
 *
 * WEB.4.UI.8c — Visible aura side-panel: AuraPanel component lists all active auras
 *
 * WEB.4.UI.8b — BuildCost aura query: BuildMenu cost preview reflects active auras;
 *  - aura-build-cost.ts: computeBuildCostBreakdown helper
 *  - buildFacility deducts effective cost (post-aura), not baseCost
 *
 * WEB.4.UI.8a — Persistent AuraManagerState:
 *  - auraState added to GameState; threaded through playCard / confirmTarget pipeline
 *  - CIS card-instance minted per play (so AuraApply resolver sees cardInstanceId > 0)
 *  - buyCost debit pre-pipeline (codex R2 HIGH — paid AuraApply policies were FREE)
 *  - Same-scope multi-AuraApply deferral (codex R2 HIGH — Net_Zero half-applied)
 *
 * WEB.4.UI.7 — TilePicker (overlay HUD on HexBoard):
 *  - tilePickerOpen / tilePickerFilter / tilePickerMode / tilePickerCardId /
 *    selectedPickerTile / tilePickerIntent state
 *  - openTilePicker / closeTilePicker / selectPickerTile / confirmTilePick actions
 *  - confirmTarget chains into TilePicker for FacilityTransfer (Smog_Emergency),
 *    Destroy (Mercenaries), Decommission (Climate_Protest)
 *  - rollDice: Teleport tile landing opens TilePicker
 *
 * WEB.4.UI.6 — TargetPicker (opponent selector for OpponentChosen cards):
 *  - targetPickerCard / targetPickerVictimSeat state
 *  - openTargetPicker / closeTargetPicker / confirmTarget actions
 *  - playCard routes OpponentChosen cards through openTargetPicker
 *
 * WEB.4.UI.4 — BuildMenu + PrimaryActionZone:
 *  - turnPhase 'rolled' renamed to 'landed'
 *  - buildMenuOpen / builtFacilities state
 *  - openBuildMenu / closeBuildMenu / buildFacility actions
 *  - rollDice auto-opens BuildMenu on buildable tile types
 *
 * WEB.4.UI.3 — HandPanel + CardDetailModal + runCard wiring:
 *  - CardInHand type + hands per seat (max MAX_HAND_SIZE)
 *  - drawCard / openCardDetail / closeCardDetail / flipReveal / playCard / toggleHandPanel
 *  - Tile-landing flow in rollDice: Event tile → drawCard + auto-open modal
 *
 * WEB.4.UI.2 — rules-engine wiring:
 *  - Per-seat resource arrays (tokens, energy, pollution)
 *  - Round / era tracking + seeded RNG dice roll
 *  - rollDice / adjustTokens / adjustEnergy / adjustPollution / resetGame
 *  - endTurn: clears lastDiceRoll + turnPhase, increments round on seat wrap
 *
 * RNG strategy: seed is stored in state (JSON-safe). The Rng instance lives in a
 * module-private `_rng` variable — NOT in state — because Rng contains functions
 * which are not JSON-serializable. resetGame(seed) regenerates _rng.
 */

// ---------------------------------------------------------------------------
// TilePicker — re-export TileFilter from filter.ts for convenience
// ---------------------------------------------------------------------------
export type { TileFilter } from "../components/TilePicker/filter.js";
import type { TileFilter } from "../components/TilePicker/filter.js";

import {
  // DC-1 Bug #7: DrawCard pipeline wiring.
  CardZone,
  MAX_HAND_SIZE,
  addFacility,
  createActionLog,
  createAuraManagerState,
  createCardInstanceState,
  createEnergyState,
  createFacilityOwnershipState,
  createForSeat,
  createImmunityState,
  createPendingEffectQueueState,
  createPollutionState,
  createRng,
  createTokenState,
  discardAura,
  getByOwner as getCISByOwner,
  getCategory as getFacilityCategory,
  initializeDeck,
  makeEffectContext,
  onBuildExecuted,
  registerAura,
  runCard,
  setEnergyCubes,
  setTokenBalance,
} from "@gdg/rules-engine";
import type {
  AuraManagerState,
  CardDeckState,
  CountProvider,
  FacilityOwnershipState,
  ImmunityState,
  PendingEffect,
  PendingEffectQueueState,
  PipelineServices,
  Rng,
} from "@gdg/rules-engine";
import {
  consumeImmunityIfMatches,
  enqueue as enqueuePending,
  peekIncludingGlobal,
  tryConsume as tryConsumePending,
} from "@gdg/rules-engine";
// WEB.4.R3.8 — takeover engine functions
import {
  takeoverFacility as _takeoverFacilityEngine,
  computeTakeoverAmounts,
} from "@gdg/rules-engine";
import type { CardDef, CardEffect, FacilityCategory, FacilityType, PendingKind } from "@gdg/shared";
import { create } from "zustand";
import tilePositionsData from "../../../../data/board/tile-positions.json";
import cardsJson from "../../../../data/cards.json";
import headlinesJson from "../../../../data/headlines.json";
import quizBankJson from "../../../../data/quiz-bank.json";
import scoutTablesJson from "../../../../data/scout-tables.json";
import {
  clearSessions as _clearAnalyticsSessions,
  exportSessions as _exportAnalyticsSessions,
  recordEvent as _recordAnalyticsEvent,
} from "../analytics/analytics.js";
import type { Archetype as AnalyticsArchetype } from "../analytics/analytics.js";
import {
  bgmKeyForEraRound,
  crossfadeBGM,
  loadPersistedMute,
  loadPersistedVolume,
  persistVolume,
  resetAudioForNewGame,
} from "../audio/audio.js";
import { getCutsceneGroup as _getCutsceneGroup } from "../cutscene/cutscene-data.js";
import { loadPersistedLang, persistLang } from "../i18n/i18n.js";
import { decayPassingStartAuras, didPassStart } from "./aura-decay.js";
import { computeTollOnLanding, computeTurnEndDeltas } from "./aura-tick.js";
import { FACILITIES } from "./facilities.js";
import {
  computeProportionalPollutionReduction,
  detectGameEnd,
  seatsInGame,
  wouldGoBankrupt,
} from "./game-end.js";
import { pickThresholdCard, thresholdTierRank, tierForPollution } from "./threshold-events.js";

// ---------------------------------------------------------------------------
// Seat colors — matches DesignTokens / tailwind.config.ts
// ---------------------------------------------------------------------------

export const SEAT_COLORS = ["#EA5656", "#4D9EE0", "#5BC07A", "#E8C547"] as const;
export type SeatIndex = 0 | 1 | 2 | 3;

/**
 * Player-count helpers (2|3|4 support). DEFAULT-4 byte-identical: with numPlayers=4
 * every seat is in-game, so these collapse to the old hardcoded behavior. Seats with
 * index >= numPlayers are "not in game" — they retain INITIAL values and must be
 * EXCLUDED from all GAMEPLAY logic. Use these everywhere instead of hardcoded 4 /
 * [0,1,2,3] in gameplay loops.
 */
/** Is `seat` part of the game (present, INCLUDES bankrupt)? `seat < numPlayers`. */
export function seatInGame(numPlayers: 2 | 3 | 4, seat: SeatIndex): boolean {
  return seat < numPlayers;
}
/** Is `seat` present AND not bankrupt (i.e. an active participant right now)? */
export function seatActive(
  state: { numPlayers: 2 | 3 | 4; lostSeats: ReadonlySet<SeatIndex> },
  seat: SeatIndex,
): boolean {
  return seat < state.numPlayers && !state.lostSeats.has(seat);
}

// R15 Builder-AW — colorblind-safe seat symbols (poker suits, ASCII-safe).
// P1=♠ P2=♥ P3=♦ P4=♣
// Rendered as aria-hidden <span> next to color pips so colorblind users
// can distinguish seats without relying on hue alone.
export const SEAT_SYMBOLS = ["♠", "♥", "♦", "♣"] as const;

/** Returns the colorblind-safe symbol for the given seat index. */
export function seatSymbolFor(seat: SeatIndex): string {
  // R15 Codex-AW MED: runtime fallback for invalid SeatIndex casts.
  return SEAT_SYMBOLS[seat] ?? "?";
}

// ---------------------------------------------------------------------------
// Card hand types (WEB.4.UI.3)
// ---------------------------------------------------------------------------

export type CardInHand = {
  /** Unique machine ID — matches cards.json `name` field (NOT `cardName`).
   *  `cardName` is the display label and may be duplicated across cards
   *  (e.g. UN_GREEN_PACT_SUPPORT_I / _II / _III all share cardName).
   *  `name` is the unique key for lookup. */
  id: string;
  /** Unique per draw — generated via _rng for determinism.
   *  Distinguishes two CardInHand entries with the same `id` in the same hand. */
  instanceId: string;
};

// ---------------------------------------------------------------------------
// cards.json lookup helper (read-only — no new card edits)
// ---------------------------------------------------------------------------

const _allCards: ReadonlyArray<CardDef> = cardsJson.cards as unknown as CardDef[];

/** Find a card definition by unique `name` field (NOT `cardName` display label). */
export function getCardById(id: string): CardDef | undefined {
  return _allCards.find((c) => c.name === id);
}

/** Back-compat alias — DO NOT USE for new lookups (returns first match of display name only). */
export function getCardByName(displayName: string): CardDef | undefined {
  return _allCards.find((c) => c.cardName === displayName);
}

/**
 * Filter cards by draw POOL — mirrors rules-engine `derivePool`:
 *   Event → Event pool
 *   Policy / Tech / Mitigate → Policy pool  (single bucket; not exact-cardType)
 *
 * Previously this was an exact `c.cardType === type` filter, which made
 * Mitigate cards (Enhanced_Measures, Infrastructure_Bailout, etc.) unreachable
 * from Policy-tile draws even though the rules-engine considers them part of
 * the Policy pool. Codex P1.1 R1 HIGH fix.
 *
 * R8 #3 — Era filter: only cards with era <= currentEra are eligible.
 * Era 1 → only era-1 cards. Era 4 → all cards. This prevents era-4 cards
 * from appearing in era-1 draws (cumulative era model: history persists).
 */
function _cardsByType(type: string, currentEra?: number): CardDef[] {
  const era = currentEra ?? useGameStore.getState().era;
  if (type === "Event") return _allCards.filter((c) => c.cardType === "Event" && c.era <= era);
  // Policy pool = everything non-Event (Policy + Tech + Mitigate).
  return _allCards.filter((c) => c.cardType !== "Event" && c.era <= era);
}

// ---------------------------------------------------------------------------
// R5 #5/#14 — Aura auto-apply helpers.
// R9 #6/#10 — Expanded passive-card detection.
// ---------------------------------------------------------------------------

/**
 * Passive-effect kinds that auto-fire on hand entry and must NOT have a
 * Use button in the CDM footer. Keep in sync with isAuraCard() in
 * CardDetailModal.tsx.
 *
 * - AuraApply       : aura registration (Carbon_Capture_System, Net_Zero, etc.)
 * - BuildCostMod    : PEQ enqueue (Industrial_Revolution, Silicon_Valley_Startup,
 *                     Climate_Adaptation_Strategy) — next-build-free / halve-loss.
 * - Immunity        : registers seat shield (UN_Green_Pact_Support, Microgrids).
 * - CancelDisaster  : registers single-charge disaster block (Disaster_Preparedness_Fun).
 */
const PASSIVE_EFFECT_KINDS = new Set<string>([
  "AuraApply",
  "BuildCostMod",
  "Immunity",
  "CancelDisaster",
]);

function _isAuraCard(cardDef: CardDef): boolean {
  // Any effect with a passive kind, OR card that stays in hand indefinitely
  // (discardMode=Whenever) is treated as a passive/aura card.
  return (
    cardDef.effects.some((e) => PASSIVE_EFFECT_KINDS.has(e.kind)) ||
    cardDef.discardMode === "Whenever"
  );
}
function _autoRegisterCardAuras(
  auraState: AuraManagerState,
  cardDef: CardDef,
  ownerSeat: number,
  instanceIdStr: string,
): AuraManagerState {
  const log = createActionLog();
  const instId = Number.isFinite(Number(instanceIdStr))
    ? Math.abs(Math.floor(Number(instanceIdStr)))
    : 1;
  let state = auraState;
  for (const e of cardDef.effects) {
    if (e.kind !== "AuraApply") continue;
    const aura = {
      instanceId: instId,
      ownerSeat,
      sourceCardName: cardDef.name,
      effect: e,
      scope: (e.auraScope ?? "BuildCost") as "BuildCost" | "Energy" | "Pollution" | "Toll",
      isPassive: true,
      categoryFilter: (e.categoryFilter?.[0] ?? null) as string | null | undefined,
      facilityFilter: null as string | null | undefined,
    };
    const { newState } = registerAura(state, log, aura as Parameters<typeof registerAura>[2]);
    state = newState;
  }
  return state;
}
function _autoRemoveCardAuras(
  auraState: AuraManagerState,
  instanceIdStr: string,
): AuraManagerState {
  const log = createActionLog();
  const instId = Number.isFinite(Number(instanceIdStr))
    ? Math.abs(Math.floor(Number(instanceIdStr)))
    : 1;
  let state = auraState;
  let didRemove = true;
  while (didRemove) {
    const result = discardAura(state, log, instId);
    didRemove = result.removed;
    state = result.newState;
  }
  return state;
}

// ---------------------------------------------------------------------------
// Bug #14 — Whenever-mode BuildCostMod PEQ helpers.
//
// Whenever-mode BuildCostMod cards (Solar_RD_Grant, Industrial_Revolution,
// Nuclear_Treaty_Signed, etc.) are SKIPPED by effect-pipeline.ts at play-time
// (guard: "DeferredToBuild"). They should be wired via PEQ enqueue on hand
// entry and PEQ dequeue on discard/consume.
//
// _enqueueWheneverBuildCostMod: enqueues one PEQ entry per BuildCostMod effect
//   that has discardMode=Whenever. ownerSeat = the CURRENT hand owner (strict
//   per-seat; never -1). instanceId stored so dequeue can match by card instance.
//
// _dequeueWheneverBuildCostMod: removes matching PEQ entries by instanceId when
//   the card leaves hand (discard / consume-on-build). Filters only the seat's
//   own bucket.
// ---------------------------------------------------------------------------

/** Canonical FacilityCategory values (mirrors _facility-enum.ts). */
const _FACILITY_CATEGORY_VALUES: ReadonlyArray<FacilityCategory> = Object.freeze([
  "Fossil",
  "Renewable",
  "Nuclear",
  "Geothermal",
  "Waste",
]);
/** Canonical FacilityType values (mirrors _facility-enum.ts). */
const _FACILITY_TYPE_VALUES: ReadonlyArray<FacilityType> = Object.freeze([
  "Coal",
  "Hydro",
  "Biomass",
  "Oil",
  "OffshoreDrilling",
  "WasteToEnergy",
  "Geothermal",
  "Nuclear",
  "Wind",
  "Solar",
  "FloatingSolar",
  "Tidal",
]);
function _parseFacilityEnumIgnoreCase(raw: string): {
  catFilter: FacilityCategory | null;
  ftFilter: FacilityType | null;
} {
  const lower = raw.toLowerCase();
  for (const v of _FACILITY_CATEGORY_VALUES) {
    if (v.toLowerCase() === lower) return { catFilter: v, ftFilter: null };
  }
  for (const v of _FACILITY_TYPE_VALUES) {
    if (v.toLowerCase() === lower) return { catFilter: null, ftFilter: v };
  }
  return { catFilter: null, ftFilter: null };
}

/**
 * Enqueue PEQ entries for all Whenever-mode BuildCostMod effects on cardDef.
 * Must be called when the card enters a seat's hand.
 * ownerSeat MUST be the actual winner seat (never -1).
 */
function _enqueueWheneverBuildCostMod(
  peqState: PendingEffectQueueState,
  cardDef: CardDef,
  ownerSeat: number,
  instanceIdStr: string,
): PendingEffectQueueState {
  if (cardDef.discardMode !== "Whenever") return peqState;
  const instId = Number.isFinite(Number(instanceIdStr))
    ? Math.abs(Math.floor(Number(instanceIdStr)))
    : 0;
  let state = peqState;
  const log = createActionLog();
  for (const e of cardDef.effects) {
    if (e.kind !== "BuildCostMod") continue;
    if (e.pendingKind == null) continue;
    let catFilter: FacilityCategory | null = null;
    let ftFilter: FacilityType | null = null;
    const rawFilters = e.categoryFilter;
    if (rawFilters != null && rawFilters.length > 0) {
      const raw = rawFilters[0];
      if (raw != null && raw.length > 0) {
        const parsed = _parseFacilityEnumIgnoreCase(raw);
        catFilter = parsed.catFilter;
        ftFilter = parsed.ftFilter;
      }
    }
    const pe: PendingEffect = {
      ownerSeat,
      kind: e.pendingKind,
      valueInt: e.valueInt ?? 0,
      sourceCardName: cardDef.cardName.length > 0 ? cardDef.cardName : cardDef.name,
      instanceId: instId,
      categoryFilter: catFilter,
      facilityFilter: ftFilter,
    };
    const { newState } = enqueuePending(state, log, pe);
    state = newState;
  }
  return state;
}

/**
 * Remove all PEQ entries whose instanceId matches the card's instanceId from
 * the given seat's bucket. Called when a Whenever-mode card leaves hand.
 */
function _dequeueWheneverBuildCostMod(
  peqState: PendingEffectQueueState,
  cardDef: CardDef,
  ownerSeat: number,
  instanceIdStr: string,
): PendingEffectQueueState {
  if (cardDef.discardMode !== "Whenever") return peqState;
  const instId = Number.isFinite(Number(instanceIdStr))
    ? Math.abs(Math.floor(Number(instanceIdStr)))
    : 0;
  const key = String(ownerSeat);
  const q = peqState.queues[key] ?? Object.freeze([]);
  const filtered = Object.freeze(q.filter((pe) => pe.instanceId !== instId));
  if (filtered.length === q.length) return peqState; // nothing removed
  const newQueues = Object.freeze({ ...peqState.queues, [key]: filtered });
  return Object.freeze({ queues: newQueues });
}

// ---------------------------------------------------------------------------
// WEB.4.UI.16 — HalveNextTokenLoss application helper.
//
// Climate_Adaptation_Strategy_E1/E3 enqueues a PEQ entry of kind
// `HalveNextTokenLoss` for its owner seat. When that seat suffers a token
// LOSS (toll-on-landing, Smog drawer payment, threshold-card negative
// tokenDelta), this helper consumes the entry and halves the loss
// (ceiling-divided per the historicalNote on the card).
//
// Returns: { halvedLoss, newPeqState, consumed }. `consumed === true` means a
// PEQ entry was actually decremented (used for log attribution). Pure
// passthrough when `loss === 0` or no matching PEQ entry exists.
// ---------------------------------------------------------------------------
function _applyHalveNextTokenLoss(
  peqState: PendingEffectQueueState,
  seat: number,
  loss: number,
): { halvedLoss: number; newPeqState: PendingEffectQueueState; consumed: boolean } {
  if (loss <= 0) return { halvedLoss: loss, newPeqState: peqState, consumed: false };
  const log = createActionLog();
  // kind=HalveNextTokenLoss, no category/facility filter.
  const result = tryConsumePending(peqState, log, seat, "HalveNextTokenLoss", null, null);
  if (!result.ok) return { halvedLoss: loss, newPeqState: peqState, consumed: false };
  return {
    halvedLoss: Math.ceil(loss / 2),
    newPeqState: result.newState,
    consumed: true,
  };
}

// ---------------------------------------------------------------------------
// WEB.4.UI.PT-bonus — Dynamic bail price computation.
//
// R3 Bug 5 spec: Math.max(1, Math.round(meanAllPlayers × bailMultiplier[seat])).
// "All players" = all 4 seats (including bankrupt, as per spec formula).
// R6 #4 (reverted): bailMultiplier starts at 0.25 (25% of mean tokens).
// ---------------------------------------------------------------------------
function computeBailPrice(
  s: {
    tokens: readonly number[];
    bailMultiplier: readonly number[];
    lostSeats: ReadonlySet<SeatIndex>;
    playerPerks?: readonly ("engineer" | "scientist" | "activist" | "politician")[];
    numPlayers?: 2 | 3 | 4;
  },
  seat: SeatIndex,
): number {
  // ALL players (spec: "mean total tokens across ALL players") — includes bankrupt,
  // but excludes absent seats (index >= numPlayers): those keep INITIAL_TOKENS=100
  // and would inflate the mean (3× bail overcharge) in 2/3-player games.
  const allSeats = seatsInGame(s.numPlayers);
  const total = allSeats.reduce<number>((sum, i) => sum + (s.tokens[i] ?? 0), 0);
  const mean = total / allSeats.length;
  // Per-seat bailMultiplier starts at 0.25 (R6 baseline)
  const base = Math.max(1, Math.round(mean * (s.bailMultiplier[seat] ?? 0.25)));
  // R9 perk: Politician bail × 1.5
  if (s.playerPerks?.[seat] === "politician") return Math.max(1, Math.ceil(base * 1.5));
  return base;
}

// ---------------------------------------------------------------------------
// WEB.4.UI.10a — Bankruptcy helper.
//
// Returns a new Set with `seat` added to the lost set. Caller passes result
// to detectGameEnd to check elimination.
// ---------------------------------------------------------------------------
function bankruptSeat(s: { lostSeats: ReadonlySet<SeatIndex> }, seat: SeatIndex): Set<SeatIndex> {
  return new Set([...s.lostSeats, seat]);
}

// ---------------------------------------------------------------------------
// WEB.4.UI.15 (gap #3.C) — Facility-trigger dispatch helper.
//
// Disaster_Relief_Fund_E1 / _E3 carry Trigger effects with triggerOn ∈
// {AfterFacilityDestroyedMine, AfterFacilityDecommissionedMine}. They sit in
// the victim's hand as defensive insurance; when their tile is destroyed or
// decommissioned, the trigger response fires and the card is consumed.
//
// Scope: handles `TokenDelta` + `target: Self` responses (Disaster_Relief_Fund
// + most simple per-seat triggers). Other response kinds are no-ops with a
// warning — caller chains may need richer dispatch later.
//
// Returns: { newHands, tokenGain, firedCardNames }. Caller credits tokenGain
// to the victim seat AFTER the destruction-deltas land so triggers cannot mask
// bankruptcy.
// ---------------------------------------------------------------------------
function _dispatchFacilityTrigger(
  hands: [CardInHand[], CardInHand[], CardInHand[], CardInHand[]],
  victimSeat: SeatIndex,
  triggerEvent: "AfterFacilityDestroyedMine" | "AfterFacilityDecommissionedMine",
): {
  newHands: [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
  tokenGain: number;
  firedCardNames: ReadonlyArray<string>;
} {
  const seatHand = hands[victimSeat];
  let tokenGain = 0;
  const fired: string[] = [];
  const keep: CardInHand[] = [];
  for (const c of seatHand) {
    const def = getCardById(c.id);
    if (def === undefined) {
      keep.push(c);
      continue;
    }
    const trig = def.effects.find((e) => e.kind === "Trigger" && e.triggerOn === triggerEvent);
    if (trig === undefined || trig.triggerResponse === undefined) {
      keep.push(c);
      continue;
    }
    let gain = 0;
    for (const r of trig.triggerResponse) {
      if (r.kind === "TokenDelta" && r.target === "Self" && typeof r.valueInt === "number") {
        gain += r.valueInt;
      } else {
        console.warn(
          `[Trigger:${triggerEvent}] ${def.name} response kind=${r.kind} target=${r.target} not yet wired — skipping.`,
        );
      }
    }
    tokenGain += gain;
    fired.push(def.name);
    // Card discarded (AfterUse) — do NOT keep.
  }
  const newHands = [...hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
  newHands[victimSeat] = keep;
  return { newHands, tokenGain, firedCardNames: fired };
}

// ---------------------------------------------------------------------------
// WEB.4.UI.18 — FacilityOwnership seeding + diff extraction helpers.
//
// Multi-tile resolvers (RngBranch affectsAll, Decommission affectsAll) operate
// on `PipelineServices.facilityOwnership`. The store's facility state lives in
// `builtFacilities`, `ownedBy`, `builtFacilityMeta` — three separate Records.
// These helpers bridge between those two representations.
// ---------------------------------------------------------------------------

/** Map from FacilityDef.id to the shared FacilityType enum value. */
const FACILITY_ID_TO_TYPE: Readonly<Record<string, FacilityType>> = {
  coal: "Coal",
  oil: "Oil",
  hydro: "Hydro",
  wind: "Wind",
  solar: "Solar",
  nuclear: "Nuclear",
  biomass: "Biomass",
  geothermal: "Geothermal",
  wastetoenergy: "WasteToEnergy",
  offshoredrilling: "OffshoreDrilling",
  floatingsolar: "FloatingSolar",
  tidal: "Tidal",
};

/**
 * Build a FacilityOwnershipState seeded from the store's facility maps.
 * Entries with missing owner, facilityId, or unknown facilityType are skipped.
 */
function _seedFacilityOwnership(s: {
  builtFacilities: Record<number, string | undefined>;
  ownedBy: Record<number, number | undefined>;
  builtFacilityMeta: Record<number, { builtCost?: number } | undefined>;
}): FacilityOwnershipState {
  let state = createFacilityOwnershipState();
  const log = createActionLog();
  for (const tileStr of Object.keys(s.builtFacilities)) {
    const tileIndex = Number(tileStr);
    const facilityId = s.builtFacilities[tileIndex];
    const ownerSeat = s.ownedBy[tileIndex];
    if (facilityId === undefined || ownerSeat === undefined) continue;
    const facilityType = FACILITY_ID_TO_TYPE[facilityId];
    if (facilityType === undefined) continue;
    const builtCost = s.builtFacilityMeta[tileIndex]?.builtCost;
    const buildCostPaid = builtCost ?? FACILITIES.find((f) => f.id === facilityId)?.baseCost ?? 0;
    try {
      const r = addFacility(state, log, { tileIndex, ownerSeat, facilityType, buildCostPaid });
      state = r.newState;
    } catch {
      // Defensive: skip duplicate or invalid entries.
    }
  }
  return state;
}

// ---------------------------------------------------------------------------
// DC-1 Bug #11 — real CountProvider from FacilityOwnershipState.
//
// The stub countProvider (all-zero) broke every predicate-gated card:
//   • ownedFossil<=2  (Paris_Climate_Agreement) — always evaluates to 0<=2=true,
//     accidentally passing for Paris but returning wrong counts for others.
//   • countOwnedOil   (1973_Oil_Crisis) — always 0 → -1 * 0 = 0 energy deducted.
//   • ownedRenewable>=3 (International_Green_Funding) — always fails (0>=3=false).
//   • ownedFossil>3   (Smog_Fog_Crisis) — always fails (0>3=false).
//   • countOwnedFossil (Dirty_Industry_Surge) — always 0 → +5 * 0 = 0 tokens.
//   • majorityFossil/majorityRenewable (Green_Bills_Enact) — always false.
//
// Fix: derive real counts from FacilityOwnershipState.
// (CountProvider imported at top-level via @gdg/rules-engine type import.)
// ---------------------------------------------------------------------------

function _buildCountProvider(ownership: FacilityOwnershipState): CountProvider {
  return {
    getOwnedByCategory(seat, category) {
      return ownership.facilities.filter(
        (f) => f.ownerSeat === seat && getFacilityCategory(f.facilityType) === category,
      ).length;
    },
    getOwnedByType(seat, facilityType) {
      return ownership.facilities.filter(
        (f) => f.ownerSeat === seat && f.facilityType === facilityType,
      ).length;
    },
    getTotalOwned(seat) {
      return ownership.facilities.filter((f) => f.ownerSeat === seat).length;
    },
  };
}

// ---------------------------------------------------------------------------
// DC-1 Bug #7 — seed a CardDeckState for the DrawCard resolver.
//
// The DrawCard resolver returns Skipped when deck is undefined. Supply a
// single-pass Policy pool seeded from spentPolicyIds (already-drawn cards go
// into the discard pile so they can still be reshuffled into DrawCard draws).
//
// Uses initializeDeck which shuffles with the provided Rng. Because _rng is a
// deterministic PRNG, the order here is reproducible across replays.
// ---------------------------------------------------------------------------

function _buildDeckForPlayCard(spentPolicyIds: ReadonlyArray<string>): CardDeckState {
  const spentSet = new Set(spentPolicyIds);
  // _allCards is ReadonlyArray<CardDef> (typed above); initializeDeck accepts
  // ReadonlyArray<CardDef> so the cast is safe.
  // Put unspent cards in draw pile, spent cards in discard. initializeDeck
  // buckets them by pool (Event/Policy) then shuffles — we override with our
  // pre-split approach using createEmptyDeckState + Object.freeze gymnastics.
  //
  // Simpler approach: build the deck as if starting fresh then move spent cards
  // to discard. initializeDeck already shuffles; we just need the Policy pool
  // to contain Mitigate-type cards so DrawCard(cardPoolFilter=["Mitigate"]) finds one.
  const deck = initializeDeck(_allCards, _rng);
  // Mark spent policy cards as in discard.
  const policyPool = deck.pools.Policy;
  const draw = policyPool.draw.filter((c) => !spentSet.has(c.name));
  const discard = [...policyPool.discard, ...policyPool.draw.filter((c) => spentSet.has(c.name))];
  return Object.freeze({
    pools: Object.freeze({
      ...deck.pools,
      Policy: Object.freeze({
        draw: Object.freeze(draw),
        discard: Object.freeze(discard),
      }),
    }),
  });
}

/**
 * Return the set of tile indices that were present in `before` but absent in `after`.
 */
function _extractRemovedTiles(
  before: FacilityOwnershipState,
  after: FacilityOwnershipState,
): number[] {
  const afterTiles = new Set(after.facilities.map((f) => f.tileIndex));
  return before.facilities.filter((f) => !afterTiles.has(f.tileIndex)).map((f) => f.tileIndex);
}

/**
 * Bug #12 fix — extract tiles whose ownerSeat changed between before and after.
 * Used to propagate StealFacility (Trade_War) transfers back to the store's ownedBy map.
 * Returns array of { tileIndex, newOwner } for each tile whose owner changed.
 */
function _extractTransferredTiles(
  before: FacilityOwnershipState,
  after: FacilityOwnershipState,
): { tileIndex: number; newOwner: number }[] {
  const beforeMap = new Map(before.facilities.map((f) => [f.tileIndex, f.ownerSeat]));
  const result: { tileIndex: number; newOwner: number }[] = [];
  for (const f of after.facilities) {
    const prevOwner = beforeMap.get(f.tileIndex);
    if (prevOwner !== undefined && prevOwner !== f.ownerSeat) {
      result.push({ tileIndex: f.tileIndex, newOwner: f.ownerSeat });
    }
  }
  return result;
}

// ---------------------------------------------------------------------------
// WEB.4.PT.17 — single-target helpers
// ---------------------------------------------------------------------------

/**
 * WEB.4.PT.17 — loop-distance helper for NearestPlayer target resolution.
 * 33-tile loop. Distance = min(|a-b|, TILE_COUNT - |a-b|).
 */
function _tileLoopDistance(a: number, b: number): number {
  const d = Math.abs(a - b);
  return Math.min(d, TILE_COUNT - d);
}

/**
 * WEB.4.PT.17 — find nearest non-drawer, non-lost seat to drawer's tile.
 * Tie-break: lowest seat index. Returns -1 if no other active seats.
 */
function _resolveNearestPlayerSeat(
  playerTiles: readonly number[],
  drawerSeat: SeatIndex,
  lostSeats: ReadonlySet<SeatIndex>,
  numPlayers: 2 | 3 | 4 = 4,
): SeatIndex {
  const drawerTile = playerTiles[drawerSeat] ?? 0;
  let bestSeat: SeatIndex = -1 as SeatIndex;
  let bestDist = Number.POSITIVE_INFINITY;
  for (const s of [0, 1, 2, 3] as SeatIndex[]) {
    if (s >= numPlayers) continue; // not-in-game seat (default 4 → none skipped)
    if (s === drawerSeat) continue;
    if (lostSeats.has(s)) continue;
    const d = _tileLoopDistance(drawerTile, playerTiles[s] ?? 0);
    if (d < bestDist) {
      bestDist = d;
      bestSeat = s;
    }
  }
  return bestSeat;
}

/**
 * WEB.4.PT.17 — find a single facility owned by `ownerSeat`.
 * Scans builtFacilities for the first facility (lowest tile key) owned by ownerSeat.
 * Honors categoryFilter (PascalCase types matched lowercase against facilityId).
 * Returns -1 if no matching facility owned.
 */
function _findFirstFacilityTileFor(
  ownerSeat: SeatIndex,
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  categoryFilter: ReadonlyArray<string> | undefined,
): number {
  const filterSet =
    categoryFilter !== undefined && categoryFilter.length > 0
      ? new Set(categoryFilter.map((c) => c.toLowerCase()))
      : null;
  const tiles = Object.keys(builtFacilities)
    .map(Number)
    .sort((a, b) => a - b);
  for (const tile of tiles) {
    const facilityId = builtFacilities[tile];
    if (facilityId === undefined) continue;
    if (ownedBy[tile] !== ownerSeat) continue;
    if (filterSet !== null && !filterSet.has(facilityId)) continue;
    return tile;
  }
  return -1;
}

/**
 * R18-FB-6 — Combined nearest-player-with-facility resolver.
 *
 * Sorts non-drawer active seats by tile-loop distance (ascending), iterates in
 * order, and returns the first seat that owns at least one matching facility.
 * Returns null if no active opponent has a matching facility.
 *
 * Solves the Tsunami "NEAREST PLANT DESTROYED" bug: the old two-step
 * (_resolveNearestPlayerSeat → _findFirstFacilityTileFor) returned -1 when the
 * nearest player had 0 facilities, skipping seats farther away that DID have
 * facilities.
 */
function _resolveNearestPlayerSeatWithFacility(
  playerTiles: readonly number[],
  drawerSeat: SeatIndex,
  lostSeats: ReadonlySet<SeatIndex>,
  builtFacilities: Record<number, string | undefined>,
  ownedBy: Record<number, number | undefined>,
  categoryFilter: ReadonlyArray<string> | undefined,
  numPlayers: 2 | 3 | 4 = 4,
): { seat: SeatIndex; tileIndex: number } | null {
  const drawerTile = playerTiles[drawerSeat] ?? 0;
  // Build sorted list of active opponent seats by ascending tile-loop distance.
  const candidates: Array<{ seat: SeatIndex; dist: number }> = [];
  for (const s of [0, 1, 2, 3] as SeatIndex[]) {
    if (s >= numPlayers) continue; // not-in-game seat (default 4 → none skipped)
    if (s === drawerSeat) continue;
    if (lostSeats.has(s)) continue;
    candidates.push({ seat: s, dist: _tileLoopDistance(drawerTile, playerTiles[s] ?? 0) });
  }
  candidates.sort((a, b) => a.dist - b.dist || a.seat - b.seat);

  for (const { seat } of candidates) {
    const tileIndex = _findFirstFacilityTileFor(seat, builtFacilities, ownedBy, categoryFilter);
    if (tileIndex >= 0) return { seat, tileIndex };
  }
  return null;
}

/**
 * WEB.4.UI.18 — Playcard effect support gate.
 *
 * Returns true when a single CardEffect is supported by the playCard pipeline.
 * Extends SUPPORTED_KINDS for multi-tile sweep effects (affectsAll=true, target=Global).
 * WEB.4.PT.17: also extends for single-target Destroy/Decommission/RngBranch with
 * target in {Self, NearestPlayer}.
 */
function _isEffectSupportedInPlayCard(e: CardEffect): boolean {
  const SUPPORTED_KINDS = new Set([
    "TokenDelta",
    "EnergyDelta",
    "PollutionDelta",
    "StealEnergy",
    "AuraApply",
    "Immunity",
    "CancelDisaster",
    "BuildCostMod",
    // DC-1 Bug #7: DrawCard now fully wired — countProvider seeded, deck supplied.
    // Sync with SUPPORTED_EFFECT_KINDS in CardDetailModal.tsx.
    "DrawCard",
    // R20-GD-1: TurnBlock sets hydroBlockTurns[seat] = valueInt. Handled inline
    // in playCard (does NOT go through rules-engine pipeline).
    "TurnBlock",
    // R20-GD-7: GiveCard@OpponentChosen (Policy_Exchange swap arm). Routed via
    // confirmTarget:StealCard which sets pendingGiveCard; pipeline not used.
    "GiveCard",
    // R19-GC (existing): DiscardHand, Trigger already handled by pipeline.
    "DiscardHand",
    "Trigger",
  ]);
  if (SUPPORTED_KINDS.has(e.kind)) return true;
  // Multi-tile global sweeps: RngBranch / Decommission with affectsAll + Global.
  // These are fully handled by resolvers once facilityOwnership is seeded.
  // NOTE: Destroy is intentionally EXCLUDED — `resolveDestroyMultiTile` does not
  // exist (only resolveDecommissionMultiTile / resolveRngBranchMultiTile). A future
  // Destroy+affectsAll card would pass this gate, run as Skipped, and be consumed
  // silently. Add to this allowlist only after a multi-tile Destroy resolver lands.
  if (
    (e.kind === "RngBranch" || e.kind === "Decommission") &&
    e.affectsAll === true &&
    e.target === "Global"
  ) {
    return true;
  }
  // WEB.4.PT.17 — single-target variants for Destroy/Decommission/RngBranch/StealFacility
  // with target in {Self, NearestPlayer}. Resolvers exist; web-client computes
  // targetTileIndices + victimSeat for ctx before pipeline.
  // Bug #12 fix: StealFacility@NearestPlayer (Trade_War) added here so the card
  // is no longer deferred/unconsumed when played.
  if (
    (e.kind === "Destroy" ||
      e.kind === "Decommission" ||
      e.kind === "RngBranch" ||
      e.kind === "StealFacility") &&
    e.affectsAll !== true &&
    (e.target === "Self" || e.target === "NearestPlayer")
  ) {
    return true;
  }
  return false;
}

// ---------------------------------------------------------------------------
// Era names (placeholder — confirm with user in WEB.4.UI.3+)
// ---------------------------------------------------------------------------

export const ERA_NAMES = [
  "Industrial Dawn",
  "Oil Rush",
  "Nuclear & Early Renewables",
  "Sustainable Future",
] as const;

/**
 * WEB.4.UI.11b.1 — Era auto-advance by round.
 *   Round 1-6   → Era 1 (Industrial Dawn)
 *   Round 7-13  → Era 2 (Oil Rush)
 *   Round 14-19 → Era 3 (Nuclear & Early Renewables)
 *   Round 20+   → Era 4 (Sustainable Future) — game ends after round 30
 *                                              per ROUND_LIMIT in game-end.ts.
 *
 * CF-1 expansion: boundaries scaled to 30-round game (user spec: Era 4 starts round 20).
 */
export function computeEraForRound(round: number): 1 | 2 | 3 | 4 {
  if (round < 7) return 1;
  if (round < 14) return 2;
  if (round < 20) return 3;
  return 4;
}

// ---------------------------------------------------------------------------
// R9 — Player-driven era advancement trigger.
//
// Called from endTurn after round increments. Returns the NEW era if an
// advancement fires, or the unchanged current era. Player-driven triggers
// supersede the old round-only schedule when energy/facility conditions
// are met early — but round thresholds also apply as a fallback.
// ---------------------------------------------------------------------------

function _sumFossilEnergy(
  builtFacilities: Record<number, string>,
  meta: Record<
    number,
    | {
        energyPerTurn: number;
        pollutionPerTurn: number;
        tollPerLand: number;
        builtCost: number;
        builtInEra: number;
        builtOnTerrain: string;
      }
    | undefined
  >,
): number {
  let total = 0;
  for (const [tileIdxStr, facilityId] of Object.entries(builtFacilities)) {
    if (facilityId === undefined) continue;
    const def = FACILITIES.find((f) => f.id === facilityId);
    if (def?.category !== "Fossil") continue;
    const tileMeta = meta[Number(tileIdxStr)];
    // Use meta energyPerTurn if available, else FACILITIES fallback
    total += tileMeta?.energyPerTurn ?? def.energyPerTurn;
  }
  return total;
}

function _countNuclearOrOffshoreWind(builtFacilities: Record<number, string>): number {
  let count = 0;
  for (const facilityId of Object.values(builtFacilities)) {
    if (facilityId === "nuclear" || facilityId === "offshoredrilling" || facilityId === "tidal") {
      count++;
    }
  }
  return count;
}

function _countRenewable(builtFacilities: Record<number, string>): number {
  let count = 0;
  for (const facilityId of Object.values(builtFacilities)) {
    const def = FACILITIES.find((f) => f.id === facilityId);
    if (def?.category === "Renewable") count++;
  }
  return count;
}

function _countAllBuiltNonEmpty(builtFacilities: Record<number, string>): number {
  return Object.values(builtFacilities).filter((v) => v !== undefined && v !== "").length;
}

/**
 * R9 — Player-driven era advancement (facility-trigger early unlock).
 *
 * Returns the new era if a facility trigger fires EARLY (before the
 * round-based schedule). Round-based advancement is handled separately by
 * computeEraForRound; the caller takes max(this, computeEraForRound).
 *
 * Early facility triggers:
 *   Era 1 → 2: total fossil energy >= 40
 *   Era 2 → 3: nuclear/offshore/tidal built >= 2
 *   Era 3 → 4: renewable ratio >= 0.5
 *
 * If no facility trigger fires, returns s.era unchanged (round threshold
 * handled by computeEraForRound in the caller).
 */
export function _shouldAdvanceEra(s: {
  era: 1 | 2 | 3 | 4;
  round: number;
  builtFacilities: Record<number, string>;
  builtFacilityMeta: Record<
    number,
    | {
        energyPerTurn: number;
        pollutionPerTurn: number;
        tollPerLand: number;
        builtCost: number;
        builtInEra: 1 | 2 | 3 | 4;
        builtOnTerrain: string;
      }
    | undefined
  >;
}): 1 | 2 | 3 | 4 {
  if (s.era === 1) {
    const fossilEnergy = _sumFossilEnergy(
      s.builtFacilities,
      s.builtFacilityMeta as Record<
        number,
        | {
            energyPerTurn: number;
            pollutionPerTurn: number;
            tollPerLand: number;
            builtCost: number;
            builtInEra: number;
            builtOnTerrain: string;
          }
        | undefined
      >,
    );
    if (fossilEnergy >= 40) return 2;
  }
  if (s.era === 2) {
    const advancedBuilt = _countNuclearOrOffshoreWind(s.builtFacilities);
    if (advancedBuilt >= 2) return 3;
  }
  if (s.era === 3) {
    const totalBuilt = _countAllBuiltNonEmpty(s.builtFacilities);
    const renewable = _countRenewable(s.builtFacilities);
    const ratio = totalBuilt > 0 ? renewable / totalBuilt : 0;
    if (ratio >= 0.5) return 4;
  }
  return s.era;
}

// ---------------------------------------------------------------------------
// Initial state constants — shared by store init + resetGame
// ---------------------------------------------------------------------------

const INITIAL_SEED = 0xdeadbeef >>> 0; // 3735928559 — used only as the dev/test fallback seed

/**
 * Generates a fresh unpredictable seed for each new game.
 * Mixes wall-clock time (ms) with a Math.random() 30-bit value so that even
 * two games started in the same millisecond get different seeds.
 * The result is forced into an unsigned 32-bit integer range.
 */
export function freshGameSeed(): number {
  return (Date.now() ^ Math.floor(Math.random() * 0x40000000)) >>> 0;
}
const INITIAL_TOKENS: [number, number, number, number] = [100, 100, 100, 100];
const INITIAL_ENERGY: [number, number, number, number] = [0, 0, 0, 0];
const INITIAL_POLLUTION: [number, number, number, number] = [0, 0, 0, 0];
const INITIAL_PLAYER_TILES: [number, number, number, number] = [0, 0, 0, 0];
const TILE_COUNT = 33;

// WEB.4.PT.R2: TILE_DRAW_POOL removed — card draw now gated by NPC interaction only
// (Bug 6 fix rollDice; Teleport branch also removed per PT NPC-only-interact rule).

// WEB.4.PT.R2: BUILDABLE_TILE_TYPES removed — BuildMenu no longer auto-opens on
// landing in rollDice or confirmTilePick Teleport. Gated by Builder NPC E-interact only.

// ---------------------------------------------------------------------------
// T-1 Universal Scout — constants + helpers
// ---------------------------------------------------------------------------

/** The 9 terrain tile types that get a Scout NPC. */
export const SCOUT_TERRAIN_TYPES = new Set([
  "Plains",
  "Forest",
  "Coast",
  "River",
  "Tundra",
  "Mountain",
  "Desert",
  "Volcanic",
  "Urban",
]);

// ---------------------------------------------------------------------------
// N-1 Energy Dilemma constants (R16 Builder-BX — user-locked)
// ---------------------------------------------------------------------------

/** One option in the Energy Dilemma choice table. */
export type EnergyDilemmaOption = {
  label: string;
  /** Token delta applied to currentSeat (positive = gain). */
  tokens: number;
  /** Pollution delta applied to currentSeat (positive = add, negative = reduce). */
  pollution: number;
  /** Energy delta applied to currentSeat (positive = gain). */
  energy: number;
  sdgFact: string;
};

/**
 * R21-GF-7: All valid energy dilemma scenario keys (9 total).
 * Config values are era-1 baselines; era scaling applied at submit time via _eraScale.
 * Dirty: tokens ≤ 4, pollution > 0. Clean: tokens ≤ 3, pollution ≤ 0. Transition: mid-mid.
 */
export type EnergyDilemmaScenarioKey =
  | "scarcity"
  | "abundance"
  | "grid_strain"
  | "subsidy_debate"
  | "carbon_pricing"
  | "grid_modernization"
  | "coal_jobs_vs_solar_jobs"
  | "trade_tariff"
  | "coastal_vs_inland_transmission";

export const ENERGY_DILEMMA_SCENARIO_KEYS: readonly EnergyDilemmaScenarioKey[] = [
  "scarcity",
  "abundance",
  "grid_strain",
  "subsidy_debate",
  "carbon_pricing",
  "grid_modernization",
  "coal_jobs_vs_solar_jobs",
  "trade_tariff",
  "coastal_vs_inland_transmission",
] as const;

/**
 * N-1 choice table keyed by scenario (9 scenarios × 3 options).
 * R20-GF-11 rebalance baselines preserved; R21-GF-7 adds 6 new scenarios.
 * Era scaling is applied at submit time (via _eraScale), not baked into config.
 */
export const ENERGY_DILEMMA_OPTIONS: Record<
  EnergyDilemmaScenarioKey,
  [EnergyDilemmaOption, EnergyDilemmaOption, EnergyDilemmaOption]
> = {
  // ── Original 3 scenarios (R20-GF-11 baselines) ──────────────────────────
  scarcity: [
    {
      // Clean: demand management — moderate tokens, slight pollution reduction
      label: "Ration power — stabilize the grid",
      tokens: 2,
      pollution: -1,
      energy: 1,
      sdgFact: "SDG 7: Energy efficiency cuts demand without sacrificing access.",
    },
    {
      // Dirty: emergency diesel — small token gain, heavy pollution (1000 g CO2/kWh diesel)
      label: "Emergency diesel — keep lights on",
      tokens: 4,
      pollution: 7,
      energy: 2,
      sdgFact: "SDG 13: Fossil emergency backup raises short-term carbon costs.",
    },
    {
      // Mid: demand-response — clean coordination, no pollution premium
      label: "Demand-response deal — share the pain",
      tokens: 3,
      pollution: 0,
      energy: 1,
      sdgFact: "SDG 11: Demand-response programs help cities balance peak load.",
    },
  ],
  abundance: [
    {
      // Dirty: export surplus — tokens from trade, minor grid-loss pollution
      label: "Export surplus to neighbours",
      tokens: 4,
      pollution: 4,
      energy: -1,
      sdgFact: "SDG 7: Regional energy trade spreads clean power benefits.",
    },
    {
      // Clean: grid-scale batteries — lower short-term return, locks in clean surplus
      label: "Store in grid-scale batteries",
      tokens: 2,
      pollution: 0,
      energy: 3,
      sdgFact: "SDG 9: Battery storage unlocks full value of renewables.",
    },
    {
      // Clean: EV charging — public infrastructure investment, clean demand shift
      label: "Accelerate EV charging infrastructure",
      tokens: 2,
      pollution: -1,
      energy: 0,
      sdgFact: "SDG 11: EV charging shifts transport emissions toward cleaner grids.",
    },
  ],
  grid_strain: [
    {
      // Mid-clean: load-shed — no new pollution but equity cost
      label: "Load-shed low-priority zones",
      tokens: 1,
      pollution: 0,
      energy: 2,
      sdgFact: "SDG 10: Load-shedding affects marginalised areas most — plan equitably.",
    },
    {
      // Dirty: peak-price reserve burn — best short-term tokens, heavy pollution
      label: "Pay peak-price top-up from reserves",
      tokens: 4,
      pollution: 8,
      energy: 2,
      sdgFact: "SDG 1: Energy poverty spikes when price volatility hits households.",
    },
    {
      // Clean: community micro-grid — resilience investment, clean
      label: "Trigger community micro-grid",
      tokens: 3,
      pollution: -1,
      energy: 1,
      sdgFact: "SDG 11: Micro-grids improve resilience in dense urban networks.",
    },
  ],

  // ── R21-GF-7: 6 new scenarios ────────────────────────────────────────────

  subsidy_debate: [
    {
      // Dirty: fossil-fuel tax credits — high short-term cash, locks in emissions
      label: "Direct subsidies to fossil-fuel tax credits",
      tokens: 4,
      pollution: 6,
      energy: 1,
      sdgFact: "SDG 13: Fossil subsidies delay the clean-energy transition globally.",
    },
    {
      // Clean: renewable rebates — modest gain, reduces pollution long-term
      label: "Redirect to renewable-energy rebates",
      tokens: 2,
      pollution: -1,
      energy: 2,
      sdgFact: "SDG 7: Renewable rebates accelerate household clean-energy adoption.",
    },
    {
      // Transition: energy-efficiency retrofits — modest, low pollution
      label: "Fund building energy-efficiency retrofits",
      tokens: 3,
      pollution: 0,
      energy: 1,
      sdgFact: "SDG 11: Retrofitting buildings cuts urban energy demand by up to 30%.",
    },
  ],

  carbon_pricing: [
    {
      // Dirty: low carbon tax — tokens preserved, emissions unchecked
      label: "Set a low carbon tax to protect industry",
      tokens: 4,
      pollution: 5,
      energy: 0,
      sdgFact: "SDG 13: Low carbon pricing fails to shift investment away from fossil fuels.",
    },
    {
      // Clean: high carbon tax — modest retained tokens, strong pollution reduction
      label: "Implement a high carbon tax",
      tokens: 2,
      pollution: -1,
      energy: 0,
      sdgFact: "SDG 13: Carbon pricing above $50/tCO2 measurably cuts emissions.",
    },
    {
      // Transition: cap-and-trade — middle ground, some pollution reduction
      label: "Launch a cap-and-trade market",
      tokens: 3,
      pollution: 0,
      energy: 1,
      sdgFact: "SDG 17: Cap-and-trade creates market incentives to cut emissions cost-effectively.",
    },
  ],

  grid_modernization: [
    {
      // Dirty: defer infrastructure — small token gain, pollution risk from outages
      label: "Defer — use reserves for short-term spending",
      tokens: 3,
      pollution: 5,
      energy: -1,
      sdgFact: "SDG 9: Deferred grid investment raises blackout risk in extreme weather.",
    },
    {
      // Clean: modernize transmission lines — modest token cost, future-proof clean
      label: "Upgrade high-voltage transmission lines",
      tokens: 1,
      pollution: -1,
      energy: 2,
      sdgFact: "SDG 9: Modern grids cut transmission losses and enable more renewables.",
    },
    {
      // Transition: hybrid microgrids — balanced, innovative
      label: "Deploy hybrid microgrids in key districts",
      tokens: 2,
      pollution: 0,
      energy: 2,
      sdgFact: "SDG 11: Microgrids give communities resilience against centralised grid failures.",
    },
  ],

  coal_jobs_vs_solar_jobs: [
    {
      // Dirty: protect coal jobs — high tokens short term, high pollution
      label: "Protect coal mining jobs — halt closures",
      tokens: 4,
      pollution: 8,
      energy: 1,
      sdgFact: "SDG 8: Coal-sector protection delays just transition for 10M+ workers globally.",
    },
    {
      // Clean: train solar workforce — modest tokens, clean long-term
      label: "Invest in solar-workforce retraining",
      tokens: 2,
      pollution: -1,
      energy: 1,
      sdgFact: "SDG 8: Solar employs 3× more workers per MWh than coal over 20 years.",
    },
    {
      // Transition: phased buyout — balanced between community and clean future
      label: "Phased buyout — pay coal workers to transition",
      tokens: 3,
      pollution: 0,
      energy: 0,
      sdgFact: "SDG 10: Phased buyout programs cut social inequality during energy transitions.",
    },
  ],

  trade_tariff: [
    {
      // Dirty: high tariff protecting domestic fossil — short-term cash, pollution locked in
      label: "High tariff — protect domestic fossil industry",
      tokens: 4,
      pollution: 6,
      energy: 0,
      sdgFact:
        "SDG 13: Import tariffs on renewables slow decarbonisation by raising clean-tech costs.",
    },
    {
      // Clean: zero tariff — cheap clean imports, modest tokens, low pollution
      label: "Zero tariff — open market for cheap clean tech",
      tokens: 2,
      pollution: -1,
      energy: 1,
      sdgFact: "SDG 7: Eliminating solar tariffs cut US module prices by 40% in one year.",
    },
    {
      // Transition: moderate tariff — balanced
      label: "Moderate tariff — level the playing field",
      tokens: 3,
      pollution: 0,
      energy: 0,
      sdgFact:
        "SDG 17: Moderate trade policy balances domestic industry support with clean imports.",
    },
  ],

  coastal_vs_inland_transmission: [
    {
      // Clean: coastal corridor for offshore wind — modest token cost, clean
      label: "Build coastal corridor for offshore wind",
      tokens: 2,
      pollution: -1,
      energy: 2,
      sdgFact: "SDG 7: Offshore wind generates 3× more energy per km of coastline than onshore.",
    },
    {
      // Transition: inland connecting nuclear hubs — modest cost, neutral pollution
      label: "Inland corridor linking nuclear plants",
      tokens: 3,
      pollution: 0,
      energy: 2,
      sdgFact: "SDG 7: Nuclear base-load smooths the intermittency of variable renewables.",
    },
    {
      // Dirty-ish: decentralized rural mesh — lower cost but slow rollout, some fossil bridging
      label: "Decentralized rural mesh — lowest cost",
      tokens: 4,
      pollution: 4,
      energy: 1,
      sdgFact:
        "SDG 10: Rural electrification via decentralized grids reaches the hardest-to-serve communities.",
    },
  ],
};

/**
 * R21-GF-7: RNG-driven scenario picker.
 * Picks from the 9-scenario pool, biased to exclude the last 3 seen (recentEnergyDilemmaScenarios).
 * Falls back to full pool if all scenarios have been seen recently.
 * Uses the module-private _rng (same source of truth as all other game RNG).
 */
export function _pickEnergyDilemmaScenario(recent: readonly string[]): EnergyDilemmaScenarioKey {
  const pool = ENERGY_DILEMMA_SCENARIO_KEYS;
  const filtered = pool.filter((k) => !recent.includes(k));
  const candidates = filtered.length > 0 ? filtered : pool;
  const idx = _rng.nextInt(0, candidates.length - 1);
  return candidates[idx] as EnergyDilemmaScenarioKey;
}

/**
 * @deprecated Use _pickEnergyDilemmaScenario() instead.
 * Retained for backward compatibility with any external callers.
 * Still uses the old deterministic formula so existing callers get stable results
 * without advancing _rng. The store's trigger paths all call _pickEnergyDilemmaScenario.
 */
export function energyDilemmaScenarioKey(tileIdx: number, seat: number): EnergyDilemmaScenarioKey {
  // Deterministic round-robin across all 9 scenario keys by (tileIdx + seat) mod 9.
  return ENERGY_DILEMMA_SCENARIO_KEYS[
    (tileIdx + seat) % ENERGY_DILEMMA_SCENARIO_KEYS.length
  ] as EnergyDilemmaScenarioKey;
}

// ---------------------------------------------------------------------------
// T-3 Trade Window constants (R16 Builder-BY — user-locked)
// ---------------------------------------------------------------------------

/** Tokens deducted from the offerSeat when the responder rejects. */
export const TRADE_REJECT_PENALTY = 5;

// T-4 Pollution Tax constants (R16 Builder-BZ — user-locked)
// ---------------------------------------------------------------------------

/** Token cost drawer pays when they accept the pollution tax. */
export const POLLUTION_TAX_AMOUNT = 30;
/** Total pollution points removed across all seats when tax is paid. */
export const POLLUTION_TAX_REDUCTION = 40;
/** Probability (0–1) that the tax fires on any given policy-tile landing (era ≥ 3). */
export const POLLUTION_TAX_TRIGGER_CHANCE = 0.1;

/** Era-scaled scout fee (user-locked). */
export function scoutCostForEra(era: 1 | 2 | 3 | 4): number {
  return ([5, 8, 12, 15] as const)[era - 1] as number;
}

/**
 * R21-GF-6: Era scaling multiplier for scout outcomes + energy dilemma rewards.
 * Era 1 = ×1.0, Era 2 = ×1.5, Era 3 = ×2.0, Era 4 = ×3.0.
 * Apply at delta-application time; config values stay at era-1 baseline.
 */
export function _eraScale(era: 1 | 2 | 3 | 4): number {
  return ([1.0, 1.5, 2.0, 3.0] as const)[era - 1] as number;
}

// Scout-table row shapes.
type ScoutOutcomeNothing = "nothing";
type ScoutOutcomeEffect = {
  tokens?: number;
  pollution?: number;
  opponentPenalty?: number;
  queueDiscount?: string;
  tourismBonus?: number;
  policyDiscount?: number;
  text: string;
  sdgFact: string;
};
type ScoutOutcomeRow = ScoutOutcomeNothing | ScoutOutcomeEffect;

/**
 * Look up the outcome row for a terrain + d6 roll (1-6).
 * The JSON keys use "1-2", "3-4", "5", "6" notation.
 */
function _scoutOutcomeForRoll(terrain: string, roll: number): ScoutOutcomeRow {
  const table = (scoutTablesJson as Record<string, Record<string, ScoutOutcomeRow>>)[terrain];
  if (table === undefined) return "nothing";
  if (roll <= 2) return table["1-2"] ?? "nothing";
  if (roll <= 4) return table["3-4"] ?? "nothing";
  if (roll === 5) return table["5"] ?? "nothing";
  return table["6"] ?? "nothing";
}

type TileEntry = { index: number; type: string; x: number; y: number };
const _tileTypeByIndex: Record<number, string> = Object.fromEntries(
  (tilePositionsData.tiles as TileEntry[]).map((t) => [t.index, t.type]),
);

// R9 #11 — NPC file by tile type for the leave-tile gate and markNpcInteracted wiring.
const _npcFileByTileType: Record<string, string> = {
  Event: "newsgirl-e1.png",
  Policy: "bureaucrat-e1.png",
};

/**
 * R9 #11 — Mark the card-draw NPC as "completed" for the current seat's tile.
 * Called after any card-flow terminal action (accept, buy, discard) on
 * Event/Policy tiles so the leave-tile gate knows the draw has been resolved.
 *
 * Note: safe to call even if the tile has no draw-NPC (no-op for other tile types).
 */
function _markCardDrawNpcCompleted(
  npcInteractionState: Record<string, "fresh" | "completed">,
  seat: number,
  playerTiles: readonly number[],
): Record<string, "fresh" | "completed"> {
  const tileIdx = playerTiles[seat] ?? 0;
  const tileType = _tileTypeByIndex[tileIdx] ?? "";
  const npcFile = _npcFileByTileType[tileType];
  if (npcFile === undefined) return npcInteractionState; // non-draw tile — no-op
  const key = `${tileIdx}:${npcFile}`;
  if (npcInteractionState[key] === "completed") return npcInteractionState; // already done
  return { ...npcInteractionState, [key]: "completed" as const };
}

// ---------------------------------------------------------------------------
// Module-private Rng instance (NOT in Zustand state — functions not JSON-safe)
// ---------------------------------------------------------------------------

let _rng: Rng = createRng(INITIAL_SEED);

// R21-GD-9: module-level handles for instant-resolve (isImmediate+Instantly) auto-ack timers.
// Each timer auto-dismisses pendingEffectAck after 2s. Cleared on resetGame.
const _instantAckTimers: number[] = [];

// ---------------------------------------------------------------------------
// R9 #7 — Game-input block guard.
//
// Returns true when any of: cutscene playing, post-cutscene dialogue chain
// running, or grand-transition bloom active. ALL input-driven turn actions
// (rollDice, endTurn, openBuildMenu, jail-warp) must early-return when true.
// ---------------------------------------------------------------------------

export function _isGameInputBlocked(s: {
  cutsceneGroupId: string | null;
  pendingPostCutsceneDialogue: object | null;
  grandTransitionActive: object | null;
}): boolean {
  return (
    s.cutsceneGroupId !== null ||
    s.pendingPostCutsceneDialogue !== null ||
    s.grandTransitionActive !== null
  );
}

/** Reinitialise the module-private Rng from a seed. Called on store init + resetGame. */
function _reinitRng(seed: number): void {
  _rng = createRng(seed);
}

// ---------------------------------------------------------------------------
// State shape
// ---------------------------------------------------------------------------

type GameState = {
  // Tile interaction
  hoveredTile: number | null;
  /** Cursor position captured at hex mouseEnter — null when not hovering. */
  hoverPos: { x: number; y: number } | null;
  activeTile: number | null;
  selectedTile: number | null;

  // Player count (2|3|4) — immutable after game start; DEFAULT 4.
  // Seats with index >= numPlayers are "not in game": they keep INITIAL values and
  // are excluded from all gameplay logic. ALL 4-tuple arrays below stay [T,T,T,T] so
  // a default-4 game is byte-identical.
  numPlayers: 2 | 3 | 4;

  // Hot-seat (up to 4 players)
  currentSeat: SeatIndex;
  playerTiles: [number, number, number, number];

  // Ownership: tileIndex → seatIndex (0..3) or undefined
  ownedBy: Record<number, SeatIndex | undefined>;

  // WEB.4.UI.2 — per-seat resources
  rngSeed: number;
  tokens: [number, number, number, number];
  energy: [number, number, number, number];
  pollution: [number, number, number, number];

  // WEB.4.UI.2 — game progress
  round: number;
  era: 1 | 2 | 3 | 4;
  lastDiceRoll: number | null;
  turnPhase: "idle" | "landed";

  // WEB.4.UI.4 — build menu
  buildMenuOpen: boolean;
  /** tileIndex → facilityId (machine key, e.g. "coal", "solar") — one per tile. */
  builtFacilities: Record<number, string>;

  /**
   * R5 #6 — Build-anim gate.
   * Set of tile indices whose construction animation has already played in this
   * game session. SidescrollerSceneInner checks this on mount — if the tile is
   * present, facility is rendered in settled state (no dust/scaffold/reveal).
   * Reset on resetGame() so a fresh game replays anims.
   */
  facilityBuildAnimPlayedTiles: Set<number>;
  /** Mark that the build animation for tileIdx has played. Called by buildFacility. */
  markFacilityBuildAnimPlayed: (tileIdx: number) => void;

  /**
   * WEB.4.UI.11c — per-tile snapshot of the FacilityRule used at build time.
   * Captures energy/pollution/toll/cost so downstream queries (aura-tick,
   * toll-on-landing, decommission refund) use era-correct values rather than
   * static FACILITIES.ts fallbacks. Codex UI.11b R1 HIGH 1 fix.
   */
  builtFacilityMeta: Record<
    number,
    | {
        energyPerTurn: number;
        pollutionPerTurn: number;
        tollPerLand: number;
        builtCost: number;
        builtInEra: 1 | 2 | 3 | 4;
        builtOnTerrain: string;
      }
    | undefined
  >;

  // WEB.4.UI.6 — target picker
  /** Card awaiting opponent selection (OpponentChosen effects). Null when picker closed. */
  targetPickerCard: CardInHand | null;
  /** Currently highlighted seat in picker; null = nothing selected yet. */
  targetPickerVictimSeat: SeatIndex | null;

  // WEB.4.UI.7 — tile picker
  tilePickerOpen: boolean;
  tilePickerFilter: TileFilter | null;
  tilePickerMode: "select" | "destroy" | "protect" | "heal" | "teleport";
  tilePickerCardId: string | null;
  selectedPickerTile: number | null;
  /**
   * What to do on confirmTilePick — encodes the pending intent so the picker
   * is fully driven from store state (no closures held in component).
   */
  tilePickerIntent:
    | { kind: "Teleport" }
    | { kind: "Smog"; victimSeat: SeatIndex; cardInstanceId: string }
    | { kind: "MercenariesDestroy"; victimSeat: SeatIndex; cardInstanceId: string }
    | { kind: "ClimateProtestDecom"; victimSeat: SeatIndex; cardInstanceId: string }
    // Bug #12 fix: DiceOutcome@OpponentChosen (Mercenaries Policy / Spy_Sabotage_II)
    // — player picks opponent tile; pipeline runs with targetTileIndices+victimSeat.
    | { kind: "DiceOutcomeAttack"; victimSeat: SeatIndex; cardInstanceId: string; cardId: string }
    | null;

  /**
   * WEB.4.UI.10a — Game-end state.
   *
   * status: "playing" = active; "ended" = game over (one of 4 reasons).
   * reason: which ending fired (bankruptcy-solo-survivor / global-warming /
   *   green-future / round-limit).
   * lostSeats: seats marked bankrupt (out of play).
   * winnerSeat / winnerSeats: who won. For all-win (green-future) winnerSeats
   *   lists active seats; for solo (bankruptcy-survivor / round-limit)
   *   winnerSeat is the sole winner.
   */
  gameStatus: "playing" | "ended";
  /** R9 expanded end-reason set (old strings kept as aliases for save compat). */
  endReason:
    | "bankruptcy-solo-survivor"
    | "tipping-point"
    | "mutual-destruction"
    | "renewable-pathway"
    | "economic-hegemony"
    | "round-limit"
    // Legacy aliases (pre-R9) — kept for save-compat; components accept them.
    | "global-warming"
    | "green-future"
    | null;
  lostSeats: ReadonlySet<SeatIndex>;
  winnerSeat: SeatIndex | null;
  winnerSeats: ReadonlyArray<SeatIndex>;

  /**
   * WEB.4.UI.12 — most-recent StealCard transfer. Drives a transient toast
   * notification ("P3 stole Carbon_Capture_System from P1") then auto-clears.
   * Null when no recent steal or after dismiss.
   */
  lastStolenCard: { from: SeatIndex; to: SeatIndex; cardId: string } | null;
  /** Action — clears the transient stolen-card toast. */
  dismissStolenCardToast: () => void;

  /**
   * R20-GD-1 — Drought: per-seat counter of remaining Hydro-block turns.
   * Set to 3 when a seat plays the Drought card (TurnBlock effect, categoryFilter=[Hydro]).
   * Decremented at endTurn (start of each of that seat's turns).
   * While > 0, buildFacility rejects any Hydro facility build with a toast.
   */
  hydroBlockTurns: [number, number, number, number];

  /**
   * WEB.4.UI.13 — Jail tile mechanic. Per-seat counter of remaining jail turns.
   * Set to 3 when a seat lands on the Jail tile. Decremented at endTurn.
   * While > 0, the seat skips dice roll and BuildMenu. Can pay 10 tokens to
   * leave immediately (clears counter, ends turn normally).
   */
  jailTurnsRemaining: [number, number, number, number];
  /** WEB.4.UI.PT-bonus — per-seat bail multiplier for dynamic jail pricing. Defaults 0.10 of mean-tokens (R5 #3: lowered from 0.25), doubles on each failed escape: 0.10 → 0.20 → 0.40. */
  bailMultiplier: [number, number, number, number];
  /** WEB.4.UI.PT-bonus — escape attempts this jailing. 0 default, increments per attempt, cap 2. */
  jailEscapeAttempts: [number, number, number, number];
  /** WEB.4.PT codex R1 MED — per-seat flag: did this seat attempt escape this turn? Resets at endTurn. Prevents back-to-back attempts in same turn. */
  jailEscapeUsedThisTurn: [boolean, boolean, boolean, boolean];
  /** Pay dynamic bail (mean-tokens × multiplier) to leave jail immediately. No-op if not in jail or insufficient. */
  payToLeaveJail: () => void;
  /** Attempt a chance-based jail escape. 25% on first try, 20% on second. On failure, increases turns + bail multiplier. No-op if not in jail or attempts exhausted. */
  attemptEscapeJail: () => void;
  /**
   * R3 Bug 1 — transient 2s toast shown after successfully paying bail.
   * "Paid {amount}T bail — leaving jail." Null when no recent bail-pay or after dismiss.
   */
  bailPaidToast: { amount: number } | null;
  /** Dismiss the bail-paid toast. */
  dismissBailPaidToast: () => void;

  /**
   * DD Bug#10b — transient 1.5s toast shown when any facility is destroyed or
   * decommissioned via a card effect (Mercenaries, Coal Lobby Influence, etc.).
   * "P{N}'s {facilityId} destroyed!" Null when no recent destroy or after dismiss.
   */
  lastFacilityDestroyed: {
    seat: SeatIndex;
    facilityId: string;
    tileIdx: number;
  } | null;
  /** Clears the facility-destroyed toast. */
  clearFacilityDestroyedToast: () => void;

  /**
   * R20-GD-9 — Chernobyl Incident impact modal.
   * Set by playCard when Chernobyl_Incident fires; shows per-seat nuclear-plant
   * decommission results ("N plants decommissioned, M survived").
   * Dismissed by dismissChernobylImpact().
   */
  pendingChernobylImpact: {
    perSeat: Array<{ seat: SeatIndex; lost: number; refunded: number }>;
    totalLost: number;
    totalRefunded: number;
  } | null;
  /** Dismiss the Chernobyl impact modal (clears pendingChernobylImpact). */
  dismissChernobylImpact: () => void;

  /** R4 — Prison Guard modal open flag. E-press / click on guard NPC opens it. */
  prisonGuardModalOpen: boolean;
  openPrisonGuardModal: () => void;
  closePrisonGuardModal: () => void;
  /** R4 — Inmate Escape modal open flag. E-press / click on inmate NPC opens it. */
  inmateEscapeModalOpen: boolean;
  openInmateEscapeModal: () => void;
  closeInmateEscapeModal: () => void;
  /** R4 — result of the most-recent escape attempt. Null until attempt; success/fail drives flash toast. */
  jailEscapeResult: { success: boolean; roll: number; threshold: number } | null;
  dismissJailEscapeResult: () => void;

  /**
   * WEB.4.UI.13 / R4 Bug 9 — Trading prompt state.
   * Set when a seat lands on the Trading tile. Drawer chooses how many energy
   * to trade via a slider (0..currentEnergy). Ratio: 1 energy → (8 − era) tokens.
   * One-way only: tokens cannot be converted back to energy.
   */
  tradingPromptOpen: boolean;
  /**
   * Execute a trade of `energyAmount` units (0..currentEnergy).
   * Ratio per era: Era1=7, Era2=6, Era3=5, Era4=4 tokens per energy.
   * Respects TradeBonus auras stacked on top of base era rate.
   * 0 is valid (closes prompt without trading).
   */
  executeTrade: (energyAmount: number) => void;
  /** Skip trade — close prompt, no resource change. */
  skipTrade: () => void;
  /** @deprecated Use executeTrade(1). Kept for legacy compat. */
  confirmTrade: () => void;

  /**
   * WEB.4.UI.14 — most-recently fired pollution-threshold event. Banner UI
   * displays it for a few seconds then auto-dismisses. Null when no event
   * fired or after dismiss. Drives ThresholdEventBanner.
   */
  lastThresholdEvent: {
    tier: "medium" | "high" | "critical";
    cardId: string;
    cardName: string;
    description: string;
  } | null;
  dismissThresholdEvent: () => void;

  /**
   * WEB.4.UI.14 R1 HIGH 1 (Codex) — highest threshold tier already fired this
   * game. Used to gate threshold-card firing on UPWARD crossings only — same
   * tier or lower never re-fires. Reset on resetGame. Cannot be derived from
   * `lastThresholdEvent` because dismiss clears that.
   */
  highestThresholdTierFired: "medium" | "high" | "critical" | null;

  /**
   * R11.2.5 fix #8 — round at which the era last advanced. Used to enforce
   * a 2-round cooldown between era changes so transition cutscenes do not
   * back-to-back. Initialized to 0 so the first era 1→2 transition can fire
   * as soon as the schedule/facility trigger fires.
   */
  lastEraAdvanceRound: number;

  /**
   * WEB.4.UI.8a — Persistent AuraManager state.
   *
   * AuraApply effects (Carbon_Capture_System, Energy_Subsidy, etc.) register
   * AuraInstance entries here via the rules-engine resolver. State persists
   * across playCard calls (was previously re-created every play, dropping
   * effects on the floor).
   *
   * UI.8a scope: REGISTER only. Aura queries (BuildCostMod into BuildMenu,
   * Toll/Energy/Pollution-tick) are deferred to UI.8b + UI.9 (tick system).
   * Decay logic for "Whenever" / "PassingStart" discardMode also deferred —
   * for now auras accumulate. Permanent durationMode auras never decay.
   */
  auraState: AuraManagerState;

  /**
   * WEB.4.UI.15 (gap #3.A) — Persistent ImmunityState.
   *
   * UN_Green_Pact_Support, Microgrids, and similar Mitigate/Policy cards register
   * Immunity entries (per-seat shields against Destroy/Decommission/StealCard/etc.).
   * Previously every playCard call passed `createImmunityState()` and discarded
   * the result, so registered immunities never reached the next attack. This
   * field captures the post-pipeline immunity state so registered shields
   * persist until consumed.
   */
  immunityState: ImmunityState;

  /**
   * WEB.4.UI.16 — Persistent PendingEffectQueue state.
   *
   * Carries one-shot pending effects across card plays — currently used for
   * `HalveNextTokenLoss` (Climate_Adaptation_Strategy_E1/E3): the card enqueues
   * a PEQ entry; the next token-loss event on that seat consumes the entry and
   * halves the loss (ceiling-divided). Previously the pipeline created a fresh
   * `createPendingEffectQueueState()` per play and discarded the result.
   */
  peqState: PendingEffectQueueState;

  /**
   * R22-GD-7 — most-recent HalveNextTokenLoss consumption. Drives a 2-second
   * UI feedback toast (via showAuraTriggeredToast or dedicated HalvedLossToast).
   * Null when no recent consume or after 2s auto-clear.
   */
  lastHalveNextTokenLossUse: {
    seat: SeatIndex;
    originalAmount: number;
    halvedAmount: number;
    sourceContext: string;
  } | null;
  clearLastHalveNextTokenLossUse: () => void;

  /**
   * WEB.4.UI.15 (gap #3.B) — most-recent immunity interception. Drives a
   * transient toast/banner so the attacker sees their attack was blocked.
   * Null when no recent block or after dismiss.
   */
  lastImmunityBlock: {
    defenderSeat: SeatIndex;
    attackerSeat: SeatIndex;
    effectKind: string;
    sourceCardName: string;
  } | null;
  dismissImmunityBlock: () => void;

  /**
   * WEB.4.SH (P0.1, 2026-05-14) — Hot-seat seat-handoff modal.
   *
   * `seatHandoffEnabled` is the hot-seat-mode toggle. Default TRUE so any
   * `endTurn` automatically opens the modal between turns; MainMenu (future
   * P0.3) will set this true on hot-seat game start (and false on
   * solo / future multiplayer modes). Component / store tests can override.
   *
   * `seatHandoffOpen` gates the full-screen modal that hides the new player's
   * hand until they click Continue. Cleared by `acknowledgeSeatHandoff`.
   *
   * `seatHandoffFromSeat` is the seat that just ended its turn — shown in the
   * "P{N} → P{M}" header so both players see whose turn just ended.
   *
   * Not opened when: gameStatus === "ended" (final modal owns the screen);
   * active-seat count <= 1 (no one to hand off to).
   */
  seatHandoffEnabled: boolean;
  seatHandoffOpen: boolean;
  seatHandoffFromSeat: SeatIndex | null;
  setSeatHandoffEnabled: (enabled: boolean) => void;
  acknowledgeSeatHandoff: () => void;

  /**
   * WEB.4.PM (P0.2, 2026-05-14) — In-game pause menu.
   *
   * `paused` gates the PauseMenu overlay. Toggled by Esc key (when no other
   * modal is open) and by Resume / Restart buttons. While paused, gameplay
   * keyboard handlers (SidescrollerScene WASD / jump / NPC E) should also
   * ignore input; component-level effects gate on `s.paused`.
   *
   * `restartGame()` resets the entire game state via `resetGame()` (same
   * rngSeed → deterministic replay until MainMenu P0.3 surfaces a seed picker).
   */
  paused: boolean;
  openPauseMenu: () => void;
  closePauseMenu: () => void;
  restartGame: () => void;

  /**
   * WEB.4.P1.5 (2026-05-14) — Language selection (en/th).
   *
   * `lang` is the active UI language. `setLang` persists to localStorage so
   * the user's choice survives page reloads. Card-text + every-component
   * sweep deferred; P1.5 wires the P0 menu/pause/handoff surfaces only.
   */
  lang: "en" | "th";
  setLang: (lang: "en" | "th") => void;

  /**
   * WEB.4.P2.1 (2026-05-14) — Global audio mute. Persisted to localStorage
   * via the `useAudio` hook. Defaults to `false` (audio on). Switches every
   * Howler channel via `Howler.mute()` in the hook's effect.
   */
  audioMuted: boolean;
  setAudioMuted: (muted: boolean) => void;

  /**
   * WEB.4.AR (2026-05-22) — Volume controls. All three 0..1, persisted to
   * localStorage. masterVolume gates the global Howler volume; bgmVolume and
   * sfxVolume are kept in store for future per-channel hooks. Sliders use
   * integer step 5 mapped to 0..1.
   */
  masterVolume: number;
  bgmVolume: number;
  sfxVolume: number;
  setMasterVolume: (v: number) => void;
  setBgmVolume: (v: number) => void;
  setSfxVolume: (v: number) => void;

  /**
   * WEB.4.CS (2026-05-14) — Cutscene player state.
   *
   * `cutsceneGroupId` is the active sequence (intro / transition-N /
   * ending-X) or null when no cutscene is playing. `cutsceneFrameIdx` is
   * the 0-based index into the group's `frameIds` array. `showCutscenes`
   * is the user's preference (default true; settings toggle when audio
   * polish lands).
   *
   * Frame data lives in `src/cutscene/cutscene-data.ts`. Image assets are
   * NOT in the repo yet — the player falls back to narration-only over an
   * era-palette gradient when imgSrc 404s.
   */
  cutsceneGroupId:
    | "intro"
    | "transition-1"
    | "transition-2"
    | "transition-3"
    | "ending-A"
    | "ending-B"
    | "ending-C"
    | "ending-D"
    | null;
  cutsceneFrameIdx: number;
  showCutscenes: boolean;
  setShowCutscenes: (show: boolean) => void;
  playCutscene: (groupId: NonNullable<GameState["cutsceneGroupId"]>) => void;
  advanceCutsceneFrame: () => void;
  skipCutscene: () => void;

  /**
   * WEB.4.MM (P0.3, 2026-05-14) — Main menu / game-phase router.
   *
   * `gamePhase` gates the top-level screen:
   *   - "menu"        → MainMenu visible; gameplay UI hidden
   *   - "char-select" → CharSelect stub; shown between menu and playing
   *   - "playing"     → MainMenu hidden; gameplay UI active
   *
   * Default is "menu" so cold-start lands on the splash. `resetGame()`
   * does NOT touch `gamePhase` (orthogonal concern); `startGame()` /
   * `returnToMainMenu()` are the explicit transitions. Existing tests that
   * never render `<App />` are unaffected by this default.
   */
  gamePhase: "menu" | "char-select" | "playing";
  /**
   * Gate-first sequencing (2026-06-13): true once AssetLoadingGate has
   * dismissed for the current game (100% loaded OR Start-anyway). Board-side
   * BGM (HexBoard era crossfade) waits for this so NO audio stream competes
   * with the critical-asset fetches on slow devices. Reset on beginGame /
   * resetGame / returnToMainMenu. Client-UI-only (never authoritative).
   */
  assetGateDone: boolean;
  setGamePhase: (phase: "menu" | "char-select" | "playing") => void;
  startGame: () => void;
  /**
   * R9 — Called by CharSelect "Begin": advances char-select → playing.
   * Does NOT start the intro cutscene — that is deferred to startIntroAfterGate()
   * which AssetLoadingGate calls once the critical assets are ready (gate-first
   * sequencing fix, 2026-06-13). This prevents the intro BGM from racing the
   * asset preload fetches on cold-cache iPad.
   *
   * `numPlayers` (2|3|4, DEFAULT 4) selects how many humans play. It is written into
   * the authoritative state here (the game-start commit point) and is immutable after.
   */
  beginGame: (numPlayers?: 2 | 3 | 4) => void;
  /**
   * Gate-first sequencing (2026-06-13): called by AssetLoadingGate when it
   * dismisses (100% loaded OR "Start anyway"). Starts the intro cutscene
   * (playCutscene("intro")) if showCutscenes is true; no-ops otherwise.
   * Does NOT transition gamePhase — that already happened in beginGame().
   * Second-game path: resetGame() clears cutsceneGroupId; the gate always
   * fires this on dismiss, so no special casing needed.
   */
  startIntroAfterGate: () => void;
  returnToMainMenu: () => void;

  // R16 /goal Fix B — facilitator export of analytics data for pilot
  // sessions. Wraps analytics.ts pure functions in store actions so the UI
  // can call them without importing the module directly.
  /** Returns JSONL string of all stored sessions, or empty string if storage unavailable. */
  exportAnalyticsData: () => string;
  /** Clears all stored analytics sessions. */
  clearAnalyticsData: () => void;

  // R9 — per-seat archetype perks.
  // Default assignment: P1=engineer, P2=scientist, P3=activist, P4=politician.
  // R14 — char-select picker allows seat-by-seat override; perks must remain
  // unique across all 4 seats (no duplicate archetypes).
  playerPerks: [
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
  ];

  /**
   * R14 — char-select picker: set a single seat's archetype.
   *
   * Enforces uniqueness across seats: if `archetype` is already held by another
   * seat, that seat is automatically swapped to the archetype `seat` was holding
   * (mutual swap). Prevents duplicates without forcing manual unset on the other
   * seat.
   */
  setPlayerPerk: (
    seat: SeatIndex,
    archetype: "engineer" | "scientist" | "activist" | "politician",
  ) => void;

  // Actions (tile)
  setHoveredTile: (idx: number | null, pos?: { x: number; y: number } | null) => void;
  setActiveTile: (idx: number | null) => void;
  selectTile: (idx: number | null) => void;
  movePlayer: (seat: SeatIndex, tileIdx: number) => void;
  setOwnership: (tileIdx: number, seat: SeatIndex | undefined) => void;

  // Actions (turn)
  rollDice: () => void;
  endTurn: () => void;

  /**
   * R10 #6 — pending jail warp.
   * Set by endTurn when the incoming seat is jailed BUT an era-transition cutscene
   * is about to fire. Instead of immediately setting turnPhase="landed" (which
   * would mount SidescrollerScene while the cutscene is playing), we store the seat
   * here and apply the warp in dismissGrandTransition after the full
   * cutscene+dialogue+grand-transition sequence completes.
   * Null when no deferred warp is pending.
   */
  pendingJailWarpSeat: SeatIndex | null;

  /**
   * R10 #11 — Mayor / Start tile land bonus.
   * Called when a player E-interacts with the Mayor NPC on the Start tile.
   * Awards +20 tokens to currentSeat (once per landing; NPC marks completed).
   */
  claimStartBonus: () => void;

  // Actions (resources)
  adjustTokens: (seat: SeatIndex, delta: number) => void;
  adjustEnergy: (seat: SeatIndex, delta: number) => void;
  adjustPollution: (seat: SeatIndex, delta: number) => void;

  // WEB.4.UI.3 — hand / card state
  hands: [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
  detailModalCard: CardInHand | null;
  modalRevealState: "cover" | "revealed";
  handPanelCollapsed: boolean;
  /** R3 Bug 9b — true when CDM was opened from HandPanel (card already in hand). */
  detailModalCardFromHand: boolean;
  /** R3 Bug 6 — CardEffectAckModal payload. null = no pending ack. */
  pendingEffectAck: {
    cardName: string;
    deltas: { energy: number; tokens: number; pollution: number; reputation: number };
    /** True when card had predicate-gated effects but none fired (e.g. Green_Bills_Enact tie). */
    conditionsNotMet?: boolean;
    /**
     * Non-numeric effect explanations (e.g. Drought's Hydro build-block duration
     * + when the player can build again). Rendered as note lines in the ack modal
     * so a delta-less card never reads as "did nothing".
     */
    notes?: string[];
  } | null;
  dismissEffectAck: () => void;
  setPendingEffectAck: (payload: {
    cardName: string;
    deltas: { energy: number; tokens: number; pollution: number; reputation: number };
    conditionsNotMet?: boolean;
    notes?: string[];
  }) => void;

  // WEB.4.R2.CF — Card-flow rework (PT R2 bugs 5/7/8)
  /**
   * Card drawn from NPC interact that has buyCost > 0 and awaits Buy/Discard decision.
   * NOT yet in any hand. CDM opens in "draw-buy" mode showing Buy+Discard footer.
   * Set by handleNpcCardDraw when a paid card is drawn.
   */
  pendingDrawCard: CardInHand | null;
  /**
   * R5 #4 — Free non-Mitigate card awaiting player acknowledge (Take Card / Discard).
   * NOT yet in any hand. CDM opens in "draw-free-acknowledge" mode.
   * Set by handleNpcCardDraw Case C (free card, hand not full).
   */
  pendingFreeDrawCard: CardInHand | null;
  /**
   * Card drawn from NPC interact that would overflow the hand (hand=5/5).
   * NOT in hand. HandFullModal shows to let player replace a slot or discard.
   */
  handFullPendingCard: CardInHand | null;
  /**
   * Transient toast: "Drew: {cardName}" shown for ~2s when a free card is auto-added.
   */
  cardDrawnToast: { cardName: string } | null;

  /**
   * R13 Bug #10 — policy deck depletion tracking.
   * Tracks policy card IDs that have been drawn this game. When a policy card
   * is drawn it is added here. drawCard filters the policy pool against this set
   * so the same policy card cannot be drawn twice. When all policies are spent
   * the set resets (deck reshuffles) so the game never deadlocks.
   */
  spentPolicyIds: string[];
  /**
   * R18-FB-1 — event deck depletion tracking.
   * Mirrors spentPolicyIds for the event pool. Ensures no event card is drawn
   * twice within a single pass; resets (deck reshuffles) when pool exhausted.
   */
  spentEventIds: string[];

  // WEB.4.UI.3 — hand / card actions
  /**
   * Draw a random card of `pool` type (event/policy) from cards.json.
   * Uses module-private _rng for determinism.
   * - If hand is full: sets handFullPendingCard (NOT added to hand), returns the card.
   * - If hand not full: adds to hand, returns the card.
   * - Returns null only if gameStatus=ended or no cards in pool.
   * Policy pool depleted via spentPolicyIds (R13 Bug #10 fix).
   */
  drawCard: (seat: SeatIndex, pool: "event" | "policy") => CardInHand | null;
  /**
   * Full NPC card-draw orchestrator (PT R2 card-flow rework).
   * Called by SidescrollerScene on E-interact with newsgirl/bureaucrat.
   * Branches on card type + buyCost:
   *   A. Event isImmediate → adds to hand, opens CDM (Acknowledge-only)
   *   B. buyCost > 0 → sets pendingDrawCard, opens CDM (Buy+Discard)
   *   C. free, hand not full → adds to hand, sets cardDrawnToast
   *   D. free, hand full → sets handFullPendingCard, skips toast
   */
  handleNpcCardDraw: (seat: SeatIndex, pool: "event" | "policy") => void;
  /** Commit Buy: debit buyCost from pendingDrawCard, add to hand (or show HandFullModal if full). */
  buyPendingCard: () => void;
  /** Discard pending: clear pendingDrawCard without adding to hand. No cost charged. */
  discardPendingCard: () => void;
  /** R5 #4: Accept free-draw card — adds pendingFreeDrawCard to hand, registers auras, closes CDM. */
  acceptFreeDrawCard: () => void;
  /** R5 #5/#14: Discard a card from hand — removes from hand and removes its auras. */
  discardHandCard: (card: CardInHand) => void;
  /** Resolve hand-full: replaceIdx = slot index to evict, or "discard" to drop new card. */
  resolveHandFull: (action: number | "discard") => void;
  /** Dismiss hand-full modal without action (same as discard). */
  dismissHandFull: () => void;
  /** Dismiss the "Drew: X" auto-toast. */
  dismissCardDrawnToast: () => void;

  // R5 #11 — Toll-paid modal (set by Builder-T rollDice toll-on-landing flow)
  pendingTollPayment: {
    tileIdx: number;
    facilityId: string;
    ownerSeat: 0 | 1 | 2 | 3;
    amount: number;
    newLanderBalance: number;
    /** R19-GA-5: owner's token balance AFTER receiving the toll (for modal display). */
    newOwnerBalance?: number;
  } | null;
  dismissTollPayment: () => void;

  /**
   * R21-GE-8 — Card review beat: when an OR-choice card is drawn, CDM opens in
   * read-only mode. Player clicks "MAKE DECISION" button when ready.
   * True from OR-choice draw until player clicks the button.
   */
  cardReviewBeatActive: boolean;
  /** Clear the review beat and close CDM — called by the CDM "MAKE DECISION" button click. */
  clearCardReviewBeat: () => void;

  /**
   * Bug #12 — OR-choice card pending player decision.
   * Set by handleNpcCardDraw when an isImmediate Event has branchId-annotated effects.
   * Null = no pending choice.
   */
  pendingOrChoice: {
    card: CardInHand;
    /** Human-readable label for the pay/safe branch */
    payLabel: string;
    /** Human-readable label for the risk/consequence branch */
    riskLabel: string;
    payBranchId: string;
    riskBranchId: string;
    cardName: string;
  } | null;
  /**
   * Resolve the OR-choice modal: execute `playCard` pipeline with the chosen branchId,
   * consuming the card. Called by OrChoiceModal on player button click.
   */
  resolveOrChoice: (branchId: string) => void;
  dismissOrChoice: () => void;

  openCardDetail: (card: CardInHand, fromHand?: boolean) => void;
  closeCardDetail: () => void;
  /** Toggle cover → revealed. Second call reverts back to cover. */
  flipReveal: () => void;
  /**
   * Play a card from hand.
   *
   * Supported effect kinds (SUPPORTED_KINDS in playCard):
   *   - TokenDelta / EnergyDelta / PollutionDelta / StealEnergy — fully wired via runCard.
   *   - AuraApply (UI.8a) — registers into persistent auraState; queries land in UI.8b/9a/9b.
   *
   * Gates:
   *   - OpponentChosen target → opens TargetPicker (UI.6).
   *   - Unsupported effect kinds → card NOT consumed, modal closes with warn.
   *   - Same-scope multi-AuraApply (Net_Zero_Movement) → deferred (UI.8a codex R2 HIGH 2).
   *   - Insufficient buyCost balance → card NOT consumed, no debit (UI.8a codex R2 HIGH 1).
   *
   * On success: debits buyCost, applies pipeline effects, removes card from hand,
   * persists pipelineResult.services.aura back to store, closes modal.
   */
  playCard: (card: CardInHand) => void;
  toggleHandPanel: () => void;

  // WEB.4.UI.4 — build menu actions
  openBuildMenu: () => void;
  closeBuildMenu: () => void;
  /**
   * Build a facility on the current player's current tile.
   * Deducts cost from currentSeat tokens (must be pre-validated by caller).
   * Sets builtFacilities[tileIdx] = facilityId. Closes build menu.
   *
   * WEB.4.UI.11c: optional `meta` snapshot captures era-specific FacilityRule
   * stats (energy/pollution/toll) at build time so downstream queries don't
   * need to re-resolve era/terrain. When meta is undefined, falls back to
   * static FACILITIES.ts (legacy callers).
   *
   * R11.2.6: optional `chosenDiscountKind` — when set, the store calls
   * onBuildExecuted to drain that PEQ entry after deducting tokens.
   * null = no discount consumed (bumps still drain via onBuildExecuted).
   */
  buildFacility: (
    facilityId: string,
    cost: number,
    meta?: {
      energyPerTurn: number;
      pollutionPerTurn: number;
      tollPerLand: number;
      builtInEra: 1 | 2 | 3 | 4;
      builtOnTerrain: string;
    },
    chosenDiscountKind?: PendingKind | null,
  ) => void;

  // WEB.4.SS.5 — Voluntary demolish own facility (50% refund). No-op if no
  // facility on current tile or tile not owned by current seat.
  demolishOwnFacility: () => void;

  // WEB.4.UI.6 — target picker actions
  /** Open TargetPicker for a card with OpponentChosen effects. Closes CardDetailModal. */
  openTargetPicker: (card: CardInHand) => void;
  /** Cancel: close picker, card stays in hand (NOT consumed). */
  closeTargetPicker: () => void;
  /**
   * Confirm selection: resume playCard with the chosen victimSeat.
   * Applies all card effects, removes card from hand, closes picker.
   * WEB.4.UI.7: FacilityTransfer/Destroy/Decommission chain into TilePicker.
   */
  confirmTarget: (victimSeat: SeatIndex) => void;

  // WEB.4.UI.7 — tile picker actions
  openTilePicker: (params: {
    filter: TileFilter;
    mode: "select" | "destroy" | "protect" | "heal" | "teleport";
    cardId: string | null;
    intent: GameState["tilePickerIntent"];
  }) => void;
  closeTilePicker: () => void;
  selectPickerTile: (tileIdx: number | null) => void;
  /**
   * Confirm the tile selection. Resolves the pending intent:
   * - Teleport: move currentSeat to chosen tile, re-trigger landing.
   * - Smog: transfer ownership of chosen tile at half buildCost.
   * - MercenariesDestroy: remove facility from builtFacilities.
   * - ClimateProtestDecom: remove facility, refund 50% to owner.
   * Always consumes the card from hand and closes the picker.
   */
  confirmTilePick: () => void;

  /**
   * WEB.4.UI.9c (refactor) — manually discard a specific aura the current seat owns.
   *
   * Identifies the exact aura by `(instanceId, scope, categoryFilter, facilityFilter)`.
   * Multi-scope cards (Oil_Boom_Prosperity: Energy + Pollution on same id) need
   * scope. Same-scope multi-filter cards (Net_Zero_Movement: Toll Fossil + Toll
   * Solar) need ALSO categoryFilter / facilityFilter to disambiguate.
   * (Codex review: Net_Zero engine fix HIGH 2.)
   *
   * Enforces store-side invariants:
   *   - Aura must exist with matching tuple
   *   - ownerSeat must === currentSeat
   *   - sourceCardName.discardMode must be "Whenever"
   */
  manualDiscardAura: (
    auraInstanceId: number,
    scope: string,
    categoryFilter: string | null,
    facilityFilter: string | null,
  ) => void;

  // Actions (game lifecycle)
  resetGame: (seed?: number, numPlayers?: 2 | 3 | 4) => void;

  // WEB.4.SS.1 — SidescrollerScene player position (active seat only)
  /** Current player x,y within the sidescroller tile viewport. Reset on remount via key. */
  scenePos: { x: number; y: number };
  /** Update player scene position (called every RAF frame while WASD held). */
  setScenePos: (x: number, y: number) => void;

  // WEB.4.SS.5 — Leave-scene request counter. Incremented by PrimaryActionZone
  // "Leave Tile" click. SidescrollerScene watches and triggers walking-out →
  // warp-out → endTurn() on increment. Counter (not boolean) ensures useEffect
  // fires on every request even if seen before.
  sceneLeaveRequestId: number;
  requestLeaveScene: () => void;

  // WEB.4.R3.8 — Takeover mechanic
  takeoverConfirmOpen: boolean;
  takeoverTileIdx: number | null;
  takeoverPrevOwner: SeatIndex | null;
  takeoverAmounts: { totalCost: number; prevOwnerCredit: number; burned: number } | null;
  openTakeoverConfirm: () => void;
  closeTakeoverConfirm: () => void;
  confirmTakeover: () => void;

  /**
   * R7 #8 — Insufficient-funds toast for takeover.
   * Shown for 2.5s when player clicks Take Over but cannot afford it.
   * Null when no active toast.
   */
  insufficientFundsToast: { needed: number; have: number; action: "takeover" } | null;
  showInsufficientFundsToast: (needed: number, have: number, action: "takeover") => void;
  dismissInsufficientFundsToast: () => void;

  /**
   * R9 #10 — AuraTriggeredToast: shown for 3s when a passive/aura effect fires
   * mid-gameplay (toll halved by Climate_Adaptation_Strategy, build made free
   * by Industrial_Revolution, etc.).
   * Null when no active toast.
   */
  auraTriggeredToast: {
    message: string;
    /** Source card name for attribution ("Climate Adaptation halved your toll") */
    sourceCardName: string;
  } | null;
  showAuraTriggeredToast: (message: string, sourceCardName: string) => void;
  dismissAuraTriggeredToast: () => void;

  // WEB.4.R3.9 — NPC re-interact state
  npcInteractionState: Record<string, "fresh" | "completed">;
  markNpcInteracted: (tileIdx: number, npcFile: string) => void;
  getNpcInteractionKey: (tileIdx: number, npcFile: string) => string;
  /** Bug 3: clear all NPC interaction state for a tile on landing (mount-only reset). */
  resetNpcInteractionsForTile: (tileIdx: number) => void;

  // R8 #2 — HandSlotDrawer: expandable hand tray inside active PlayerHUD slot
  /** True when the drawer is expanded (only meaningful during turnPhase=landed). */
  handSlotDrawerOpen: boolean;
  toggleHandSlotDrawer: () => void;

  // R11.1 fix #1 — tile-by-tile board movement gate. True while HexBoard is
  // walking the player token through intermediate tiles. SidescrollerScene
  // gates its mount on this so the in-tile UI does not flash while the token
  // is still visually mid-walk.
  boardAnimationActive: boolean;
  setBoardAnimationActive: (active: boolean) => void;

  // ---------------------------------------------------------------------------
  // N-2 — Climate Crisis Vote
  // ---------------------------------------------------------------------------

  /**
   * Pending climate treaty vote payload.
   * Non-null from trigger until resolution.
   * `votes`: per-seat vote — null = not yet voted, "yes" / "no" chosen.
   * `proposedAt`: round when triggered.
   * `deadline`: wall-clock ms timestamp (Date.now() + 15000).
   */
  pendingClimateVote: {
    proposedAt: number;
    deadline: number;
    votes: [null | "yes" | "no", null | "yes" | "no", null | "yes" | "no", null | "yes" | "no"];
  } | null;
  /** True after vote fires once per game — gates re-trigger. Reset on resetGame. */
  climateVoteFired: boolean;
  /** Cast or toggle a seat's vote. No-op when input is blocked. */
  setClimateVote: (seat: SeatIndex, vote: "yes" | "no") => void;
  /** Resolve the vote (called on deadline or all-4-voted). Applies effects + clears state. */
  resolveClimateVote: () => void;

  // ---------------------------------------------------------------------------
  // N-3 — News Headline Bonus
  // ---------------------------------------------------------------------------

  /**
   * Headline to display this round. Set by endTurn round-wrap (round >= 2).
   * Cleared by dismissHeadline (auto-dismiss after 5s from NewsTickerStrip).
   */
  currentHeadline: {
    id: string;
    title: string;
    body: string;
    effect:
      | "reward_renewables"
      | "reward_low_pollution"
      | "punish_fossils"
      | "global_bonus"
      | "flavor";
    era: number;
  } | null;
  /** Dismiss the current headline strip (called after auto-dismiss timer fires). */
  dismissHeadline: () => void;

  // R8 #6 — Post-cutscene dialogue chain + grand transition
  /**
   * Non-null while the post-cutscene dialogue sequence is playing.
   * `queue` is the remaining seats yet to speak (head = current speaker).
   * Null when no dialogue is active.
   */
  pendingPostCutsceneDialogue: {
    groupId: NonNullable<GameState["cutsceneGroupId"]>;
    era: 1 | 2 | 3 | 4;
    queue: SeatIndex[];
  } | null;
  /**
   * Non-null while the grand-transition bloom overlay is animating
   * (1.5s window after the dialogue chain completes).
   */
  grandTransitionActive: { era: 1 | 2 | 3 | 4 } | null;
  /** Begin the post-cutscene dialogue chain for the given cutscene group + era. */
  startPostCutsceneDialogue: (
    groupId: NonNullable<GameState["cutsceneGroupId"]>,
    era: 1 | 2 | 3 | 4,
  ) => void;
  /** Advance to the next speaker; when queue empties, clears dialogue and fires grand transition. */
  advancePostCutsceneDialogue: () => void;
  /** Dismiss the grand-transition overlay (called by the overlay on animation end). */
  dismissGrandTransition: () => void;

  // ---------------------------------------------------------------------------
  // M-1 SDG Trivia Quiz (minigame)
  // ---------------------------------------------------------------------------

  /**
   * M-1: Pending quiz offer — non-null when the newsgirl has offered a quiz
   * for this landing and the player hasn't accepted, answered, or skipped yet.
   * `questionIdx` is the index into quiz-bank.json already selected.
   * Cleared on answerQuiz / skipQuiz.
   */
  pendingQuiz: { questionIdx: number } | null;

  /**
   * M-1: Per-seat rate-limit — tracks which seats already took a quiz this
   * round. Cleared at endTurn round-wrap (when round increments). Cleared on
   * resetGame. Using a plain object keyed by seat index (JSON-safe alternative
   * to Set<SeatIndex>).
   */
  quizCompletedThisRound: Record<string, boolean>;

  /**
   * M-1: Per-game used question indices — prevents the same question repeating
   * within a single game session. Reset on resetGame.
   */
  quizUsedIndices: ReadonlySet<number>;

  /**
   * M-1: Offer the quiz to the current seat (called by newsgirl interaction
   * on Event tile when seat has not yet taken a quiz this round). No-op if
   * already offered, completed this round, or boardAnimationActive.
   */
  offerQuiz: () => void;

  /**
   * M-1: Player answers question at `choiceIdx`.
   * Awards +15 tokens to currentSeat when correct.
   * Marks quizCompletedThisRound for currentSeat.
   * Clears pendingQuiz.
   */
  answerQuiz: (choiceIdx: number) => void;

  /**
   * M-1: Player skips the quiz offer. Clears pendingQuiz.
   * Does NOT mark quizCompletedThisRound (player can be offered again if they
   * somehow re-trigger before endTurn — safe since offerQuiz checks pendingQuiz).
   */
  skipQuiz: () => void;

  /**
   * R13 Bug #9: trigger the normal newsgirl event-card draw for currentSeat.
   * Called from QuizModal "Draw News Card" button (after answer reveal OR skip).
   * Equivalent to the SidescrollerScene newsgirl branch: handleNpcCardDraw(seat, "event").
   */
  triggerEventCardDraw: () => void;

  // ---------------------------------------------------------------------------
  // T-2 Policy Auction (minigame)
  // ---------------------------------------------------------------------------

  /**
   * Per-seat counter: incremented on every policy-tile landing. Auction triggers
   * when (auctionLandingCount[seat] % 3 === 0) after the increment — i.e. every
   * 3rd landing (3rd, 6th, 9th …). Reset on resetGame.
   */
  auctionLandingCount: [number, number, number, number];

  /**
   * Active auction payload. Non-null from triggerAuctionIfDue until closeAuction.
   *
   * `tier`         — era (1–4) used to select the card pool.
   * `cards`        — 3 randomly drawn policy cards shown face-up.
   * `deadline`     — wall-clock ms (Date.now() + 30000) for the shared 30s timer.
   * `drawerSeat`   — seat that triggered the auction (tie-break winner).
   * `activeBidder` — seat currently entering their bid (0→1→2→3 sequential).
   * `bids`         — per-seat: null = not yet submitted, { amount } = confirmed
   *                  bid, { amount: -1 } = skipped (skipped never wins even if
   *                  highest — resolved to null in revealAuction).
   * `revealed`     — true after revealAuction fires; drives reveal phase UI.
   */
  pendingAuction: {
    tier: 1 | 2 | 3 | 4;
    /** DD Bug#9a: spec says 1 card — all players bid on the same single card. */
    cards: [CardInHand];
    deadline: number;
    drawerSeat: SeatIndex;
    activeBidder: SeatIndex;
    bids: [
      { amount: number } | null,
      { amount: number } | null,
      { amount: number } | null,
      { amount: number } | null,
    ];
    revealed: boolean;
  } | null;

  /**
   * Increment auctionLandingCount[seat] and trigger a new auction when count
   * hits a multiple of 3. No-op when boardAnimationActive, input blocked, or
   * another auction/quiz/climate-vote is already pending.
   * Called from SidescrollerScene bureaucrat interaction BEFORE the normal draw.
   */
  triggerAuctionIfDue: (seat: SeatIndex) => boolean;

  /** Active bidder submits their bid amount (0 = valid zero-bid; wins only if no
   *  one else bid more; loses to tie with drawer). Records bid only — does NOT
   *  advance activeBidder (caller must invoke passAuctionTurn for hot-seat pass). */
  submitAuctionBid: (amount: number) => void;

  /** Active bidder skips (forfeit). Does NOT advance activeBidder — caller must
   *  invoke passAuctionTurn to hand device to next seat. */
  skipAuctionBid: () => void;

  /** Bidder clicks "Pass Device" after submitting/skipping. Advances activeBidder
   *  to the next non-jailed seat (or wraps). Fires privacy beat for next seat. */
  passAuctionTurn: () => void;

  /** Resolve the auction: compute winner, debit tokens, add card to winner's hand
   *  (or trigger HandFullModal if full). Sets revealed=true. */
  revealAuction: () => void;

  /** Dismiss the auction (called after player acknowledges result). */
  closeAuction: () => void;

  /**
   * R20-GE-10 — Auction hand-full: when winner's hand is full, set this instead
   * of forfeit. HandFullModal renders using this as its alternate pending source.
   * winnerSeat may differ from currentSeat (hot-seat auction winner).
   */
  pendingAuctionHandFull: {
    winnerSeat: SeatIndex;
    wonCard: CardInHand;
    bidAmount: number;
  } | null;
  /**
   * Commit auction hand-full resolution.
   * replaceIdx = slot index to evict (swap won card in), or "forfeit" (no card, no debit).
   */
  commitAuctionHandFull: (action: number | "forfeit") => void;

  // ---------------------------------------------------------------------------
  // T-1 Universal Scout-the-Tile minigame
  // ---------------------------------------------------------------------------

  /**
   * T-1: Pending scout offer — non-null when the Scout NPC has been interacted
   * with on a terrain tile and the player hasn't paid/cancelled yet.
   * `tileIdx`: board tile index. `terrain`: tile type string (e.g. "Plains").
   * After `payScoutFee()` fires: `roll` (1-6) and `outcome` are set so the
   * modal can display the real result before `applyScoutOutcome()` fires.
   */
  pendingScout: {
    tileIdx: number;
    terrain: string;
    /** Set after fee paid + die rolled (payScoutFee). Absent/null before roll. */
    roll?: number | null;
    /** Resolved outcome row after roll. Absent/null before roll. */
    outcome?: ScoutOutcomeRow | null;
  } | null;

  /**
   * T-1: Per-landing one-shot gate.
   * Key format: `${tileIdx}:${seat}`. Cleared on endTurn turn-advance.
   * JSON-safe plain object (Set not JSON-serialisable).
   */
  scoutCompletedThisLanding: Record<string, boolean>;

  /**
   * T-1: Mountain 6 tourism bonus.
   * Per-seat remaining-landing counter. Grants +3T on landing while > 0.
   * Decremented by rollDice on each landing for the relevant seat.
   */
  tourismBonusTilesRemaining: [number, number, number, number];

  /**
   * T-1: Offer the Scout mini to the current seat on a terrain tile.
   * No-op if: boardAnimationActive, input blocked, already scouted this landing,
   * pendingScout already active, or tile is not a terrain tile.
   */
  offerScout: () => void;

  /**
   * T-1: Step 1 of 2 — deducts the era-scaled fee immediately and rolls d6.
   * Stores roll + resolved outcome into pendingScout (does NOT apply effects).
   * pendingScout remains non-null so the modal stays open for the reveal.
   * No-op if pendingScout is null, input blocked, or insufficient tokens.
   */
  payScoutFee: () => void;

  /**
   * T-1: Step 2 of 2 — applies the resolved outcome (tokens/pollution/etc.)
   * from pendingScout.outcome, then clears pendingScout and marks
   * scoutCompletedThisLanding. Called by ScoutModal Continue button.
   * No-op if pendingScout is null or outcome has not been rolled yet.
   */
  applyScoutOutcome: () => void;

  /**
   * T-1: Compatibility wrapper — calls payScoutFee then applyScoutOutcome
   * in one shot. Existing tests and callers that use the old single-step
   * contract continue to work unchanged.
   * New UI code should call payScoutFee + applyScoutOutcome separately.
   */
  executeScout: () => void;

  /** T-1: Cancel scout offer without paying. Clears pendingScout. */
  cancelScout: () => void;

  // ---------------------------------------------------------------------------
  // R16 pre-pass scaffold — minigame pending-state fields. Per-minigame inner
  // type defs + actions are added by builders BX/BY/BZ. These declarations
  // exist now so the 4 existing trigger guards (offerQuiz / triggerAuctionIfDue
  // / offerScout / triggerClimateVoteIfDue) can reference them without merge
  // conflicts across the 3 parallel builders.
  // ---------------------------------------------------------------------------

  /** N-1 (R16): pending energy dilemma. Builder-BX fills inner shape. */
  pendingEnergyDilemma: { tileIdx: number; terrain: string; optionsKey: string } | null;
  /** N-1 (R16): per-landing one-shot gate. Key: `${tileIdx}:${seat}`. */
  energyDilemmaShownThisLanding: Record<string, boolean>;
  /** R21-GF-7: last 3 seen scenario keys for RNG exclusion. Cleared on resetGame. */
  recentEnergyDilemmaScenarios: string[];

  /** T-3 (R16): pending trade negotiation. Builder-BY fills inner shape. */
  pendingTrade: {
    offerSeat: SeatIndex;
    responderSeat: SeatIndex | null;
    offeredInstanceIds: [string, string] | null;
    requestedInstanceIds: [string, string] | null;
    phase: "select" | "respond" | "resolved";
    decision: "accept" | "reject" | null;
  } | null;
  /** T-3 (R16): per-game one-shot per seat. */
  tradeOfferedThisGame: [boolean, boolean, boolean, boolean];

  // ---------------------------------------------------------------------------
  // T-3 Trade Window actions (R16 Builder-BY)
  // ---------------------------------------------------------------------------

  /**
   * T-3 (R16): Open the Trade Window "select" phase for currentSeat.
   * One-shot per game per drawer (tradeOfferedThisGame[seat]).
   *
   * Gates: boardAnimationActive → _isGameInputBlocked → gameStatus=!ended
   *        → turnPhase=landed → tile=Trading → drawer-hand>=2
   *        → at-least-one-eligible-opponent (hand>=2 & not bankrupt)
   *        → no pending* → not already traded this game (tradeOfferedThisGame[seat]).
   */
  offerTrade: () => void;

  /**
   * T-3 (R16): Drawer commits their card selection and picks a responder.
   * Transitions phase "select" → "respond".
   * `offered` — two instanceIds from the drawer's hand.
   * `responderSeat` — the opponent who will respond.
   * `requested` — two instanceIds from the responder's hand.
   */
  commitTradeOffer: (
    offered: [string, string],
    responderSeat: SeatIndex,
    requested: [string, string],
  ) => void;

  /**
   * T-3 (R16): Responder accepts the trade.
   * Swaps the 4 card instances across hands[] arrays.
   * Transitions phase "respond" → "resolved" with decision="accept".
   * Marks tradeOfferedThisGame[offerSeat]=true.
   * No-op if pendingTrade is null or phase !== "respond".
   */
  acceptTrade: () => void;

  /**
   * T-3 (R16): Responder rejects the trade.
   * Deducts TRADE_REJECT_PENALTY tokens from the offerSeat (floors at 0).
   * Transitions phase "respond" → "resolved" with decision="reject".
   * Marks tradeOfferedThisGame[offerSeat]=true.
   * No-op if pendingTrade is null or phase !== "respond".
   */
  rejectTrade: () => void;

  /**
   * T-3 (R16): Dismiss the resolved modal.
   * Clears pendingTrade.
   * No-op if pendingTrade is null or phase !== "resolved".
   */
  dismissTrade: () => void;

  /**
   * R19-GC-2 — human-readable reason why the last `offerTrade` call was silently
   * rejected. Null when no rejection has occurred or after a successful offer /
   * close of the TradingPrompt. Displayed as a red inline message below the
   * "Offer Card Swap" button in TradingPrompt.
   *
   * Set to a distinct non-null string by each guard branch in `offerTrade`.
   * Cleared to null on:
   *   - successful `offerTrade` (pendingTrade opened)
   *   - `tradingPromptOpen` toggling true (fresh prompt open)
   *   - `executeTrade`, `skipTrade`, `acceptTrade`, `rejectTrade`, `dismissTrade`
   */
  tradeOfferRejectionReason: string | null;

  /**
   * R19-GC Policy_Exchange give-arm: pending give-card picker.
   * Set after StealCard arm fires for Policy_Exchange — drawer must pick one
   * card from their own hand to give to the same opponent.
   * Null when no give-card step is pending.
   */
  pendingGiveCard: {
    victimSeat: SeatIndex;
    /** instanceId of the Policy_Exchange card that triggered the SWAP.
     *  Already removed from drawer's hand by the StealCard arm. */
    sourceCardId: string;
  } | null;

  /**
   * R19-GC: Drawer picks which of their own cards to give to `victimSeat`.
   * Transfers `giveInstanceId` from drawer's hand to victimSeat's hand.
   * If victimSeat's hand is full, the give is FORFEIT (no error).
   * Clears pendingGiveCard regardless.
   * No-op if pendingGiveCard is null.
   */
  commitGiveCard: (giveInstanceId: string) => void;

  /**
   * R19-GC: Cancel the give-card step without transferring any card.
   * Clears pendingGiveCard.
   */
  cancelGiveCard: () => void;

  /** T-4 (R16): pending pollution tax. */
  pendingPollutionTax: {
    drawerSeat: SeatIndex;
    taxAmount: number;
    reductionAmount: number;
    proportionalCuts: [number, number, number, number];
  } | null;

  /**
   * T-4 (R16): Roll the 10% chance and open the PollutionTaxModal when it
   * passes. Returns `true` if the modal opened (caller should suppress the
   * normal auction path); `false` if gates failed or the roll missed.
   *
   * Gates: boardAnimationActive → _isGameInputBlocked → gameStatus → turnPhase
   *        === "landed" → era >= 3 → no pending* → drawer can afford taxAmount
   *        → Math.random() < POLLUTION_TAX_TRIGGER_CHANCE.
   */
  triggerPollutionTaxIfRolled: (seat: SeatIndex) => boolean;

  /**
   * T-4 (R16): Drawer pays POLLUTION_TAX_AMOUNT tokens; per-seat pollution
   * reduced proportionally by POLLUTION_TAX_REDUCTION. Clears pendingPollutionTax.
   * No-op if pendingPollutionTax is null.
   */
  payPollutionTax: () => void;

  /**
   * T-4 (R16): Dismiss the tax modal with no cost and no effect.
   * Clears pendingPollutionTax.
   */
  declinePollutionTax: () => void;

  // ---------------------------------------------------------------------------
  // N-1 Energy Dilemma actions (R16 Builder-BX)
  // ---------------------------------------------------------------------------

  /**
   * N-1 (R16): Non-set helper — reads store state, checks all conflict gates,
   * and calls `useGameStore.setState` directly to open the dilemma.
   * Gates: boardAnimationActive → _isGameInputBlocked → gameStatus=!ended
   *        → turnPhase=landed → era>=2 → terrain∈SCOUT_TERRAIN_TYPES
   *        → no pending* → not already shown this landing.
   * Called from rollDice post-set and confirmTilePick Teleport post-set.
   */
  triggerEnergyDilemmaIfEligible: (seat: SeatIndex) => void;

  /**
   * N-1 (R16): Open the Energy Dilemma modal for currentSeat.
   * Alias entry-point for internal use; triggerEnergyDilemmaIfEligible is the
   * canonical caller — do not call this directly.
   */
  offerEnergyDilemma: () => void;

  /**
   * N-1 (R16): Drawer picks option 0, 1, or 2.
   * Applies token/pollution/energy deltas for currentSeat.
   * Marks energyDilemmaShownThisLanding. Clears pendingEnergyDilemma.
   * No-op if pendingEnergyDilemma is null.
   */
  submitEnergyDilemma: (choiceIdx: 0 | 1 | 2) => void;

  // ---------------------------------------------------------------------------
  // CS.4 — Mae Cameo cute layer (R15 Builder-AU)
  // ---------------------------------------------------------------------------

  /**
   * CS.4: Pending Mae cameo payload. Non-null while cameo is displayed.
   * `mood`  — selects the sprite variant (idle / joyful / sad).
   * `line`  — 1-line SDG fact shown in the speech bubble.
   * Queue depth 1: if a second setMaeCameo arrives while one is pending, it
   * overwrites (the newer beat is more relevant than the stale one).
   */
  pendingMaeCameo: { mood: "idle" | "joyful" | "sad"; line: string } | null;

  /**
   * CS.4: One-shot gate — true after the first quiz cameo fires per game.
   * Prevents the same cameo repeating on every subsequent quiz offer.
   * Reset on resetGame.
   */
  firstQuizShown: boolean;

  /**
   * CS.4: One-shot gate — true after the first climate-vote cameo fires per game.
   * Prevents the same cameo repeating on subsequent (theoretically impossible, but
   * defensive) climate-vote triggers.
   * Reset on resetGame.
   */
  firstClimateVoteShown: boolean;

  /**
   * CS.4: Show a Mae cameo. Overwrites any currently pending cameo (queue depth 1).
   * No-op when boardAnimationActive is true — the cameo will be deferred by the
   * component itself (it renders null while boardAnimationActive).
   */
  setMaeCameo: (mood: "idle" | "joyful" | "sad", line: string) => void;

  /**
   * CS.4: Dismiss the current Mae cameo (called by MaeCameo on auto-timer or click).
   */
  dismissMaeCameo: () => void;
};

// ---------------------------------------------------------------------------
// Store
// ---------------------------------------------------------------------------

export const useGameStore = create<GameState>((set, get) => ({
  // Tile interaction
  hoveredTile: null,
  hoverPos: null,
  activeTile: null,
  selectedTile: null,

  // Hot-seat
  // DEFAULT 4 — a default game is byte-identical to pre-numPlayers behavior.
  numPlayers: 4,
  currentSeat: 0,
  playerTiles: [...INITIAL_PLAYER_TILES] as [number, number, number, number],
  ownedBy: {},

  // Resources
  rngSeed: INITIAL_SEED,
  tokens: [...INITIAL_TOKENS] as [number, number, number, number],
  energy: [...INITIAL_ENERGY] as [number, number, number, number],
  pollution: [...INITIAL_POLLUTION] as [number, number, number, number],

  // Game progress
  round: 1,
  era: 1,
  lastDiceRoll: null,
  turnPhase: "idle",

  // WEB.4.UI.4 — build menu
  buildMenuOpen: false,
  builtFacilities: {} as Record<number, string>,
  // R5 #6 — Build-anim gate: empty on init (no anims played yet).
  facilityBuildAnimPlayedTiles: new Set<number>(),
  markFacilityBuildAnimPlayed: (tileIdx) =>
    set((s) => ({
      facilityBuildAnimPlayedTiles: new Set([...s.facilityBuildAnimPlayedTiles, tileIdx]),
    })),
  // WEB.4.UI.11c — per-tile FacilityRule snapshot (set on build)
  builtFacilityMeta: {} as Record<
    number,
    | {
        energyPerTurn: number;
        pollutionPerTurn: number;
        tollPerLand: number;
        builtCost: number;
        builtInEra: 1 | 2 | 3 | 4;
        builtOnTerrain: string;
      }
    | undefined
  >,

  // WEB.4.UI.6 — target picker
  targetPickerCard: null,
  targetPickerVictimSeat: null,

  // WEB.4.UI.7 — tile picker
  tilePickerOpen: false,
  tilePickerFilter: null,
  tilePickerMode: "select" as const,
  tilePickerCardId: null,
  selectedPickerTile: null,
  tilePickerIntent: null,

  // WEB.4.UI.3 — hand / card state
  hands: [[], [], [], []] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]],
  detailModalCard: null,
  modalRevealState: "cover" as const,
  handPanelCollapsed: false,
  // R3 Bug 9b/6 — from-hand flag + effect ack
  detailModalCardFromHand: false,
  pendingEffectAck: null,
  dismissEffectAck: () => set({ pendingEffectAck: null }),
  setPendingEffectAck: (payload) => set({ pendingEffectAck: payload }),
  // WEB.4.R2.CF — card-flow rework
  pendingDrawCard: null,
  pendingFreeDrawCard: null, // R5 #4
  handFullPendingCard: null,
  cardDrawnToast: null,
  // R13 Bug #10 — policy deck depletion (no initial spent cards).
  spentPolicyIds: [] as string[],
  // R18-FB-1 — event deck depletion (no initial spent events).
  spentEventIds: [] as string[],
  // R5 #11 — Toll-paid modal (stub; full wiring by Builder-T)
  pendingTollPayment: null,
  dismissTollPayment: () => set({ pendingTollPayment: null }),

  // Bug #12 — OR-choice modal for isImmediate Event cards with player branch decision
  cardReviewBeatActive: false,
  // R21-GE-8: clearCardReviewBeat is now button-driven (no setTimeout).
  // Closes CDM, clears beat flag → OrChoiceModal renders via existing pendingOrChoice gate.
  clearCardReviewBeat: () => {
    set({ cardReviewBeatActive: false, detailModalCard: null, modalRevealState: "cover" as const });
  },
  pendingOrChoice: null,
  dismissOrChoice: () => set({ pendingOrChoice: null }),

  // R11.1 fix #1 — board movement gate (see field doc above).
  boardAnimationActive: false,
  setBoardAnimationActive: (active: boolean) => set({ boardAnimationActive: active }),

  // N-2 — Climate Crisis Vote initial state
  pendingClimateVote: null,
  climateVoteFired: false,

  // N-3 — News Headline initial state
  currentHeadline: null,
  dismissHeadline: () => set({ currentHeadline: null }),

  // WEB.4.UI.8a — persistent aura state
  auraState: createAuraManagerState(),

  // WEB.4.UI.15 — persistent immunity state (gap #3.A)
  immunityState: createImmunityState(),
  // WEB.4.UI.16 — persistent PEQ state (Climate_Adaptation_Strategy)
  peqState: createPendingEffectQueueState(),
  // R22-GD-7 — HalveNextTokenLoss consume notification
  lastHalveNextTokenLossUse: null,
  clearLastHalveNextTokenLossUse: () => set({ lastHalveNextTokenLossUse: null }),
  // WEB.4.UI.15 (gap #3.B) — immunity-block notification
  lastImmunityBlock: null,
  dismissImmunityBlock: () => set({ lastImmunityBlock: null }),

  // WEB.4.UI.10a — game-end state
  gameStatus: "playing" as const,
  endReason: null,
  lostSeats: new Set<SeatIndex>(),
  winnerSeat: null,
  winnerSeats: [] as ReadonlyArray<SeatIndex>,

  // WEB.4.UI.12 — transient stolen-card toast
  lastStolenCard: null,
  dismissStolenCardToast: () => set({ lastStolenCard: null }),

  // DD Bug#10b — transient facility-destroyed toast
  lastFacilityDestroyed: null as GameState["lastFacilityDestroyed"],
  clearFacilityDestroyedToast: () => set({ lastFacilityDestroyed: null }),

  // R20-GD-1 — Drought: per-seat Hydro-block turn counter
  hydroBlockTurns: [0, 0, 0, 0] as [number, number, number, number],

  // R20-GD-9 — Chernobyl Incident impact modal
  pendingChernobylImpact: null as GameState["pendingChernobylImpact"],
  dismissChernobylImpact: () => set({ pendingChernobylImpact: null }),

  // WEB.4.UI.13 — Jail + Trading state
  jailTurnsRemaining: [0, 0, 0, 0] as [number, number, number, number],
  bailMultiplier: [0.25, 0.25, 0.25, 0.25] as [number, number, number, number],
  jailEscapeAttempts: [0, 0, 0, 0] as [number, number, number, number],
  jailEscapeUsedThisTurn: [false, false, false, false] as [boolean, boolean, boolean, boolean],
  // R3 Bug 1 — bail-paid toast (2s transient)
  bailPaidToast: null as { amount: number } | null,
  dismissBailPaidToast: () => set({ bailPaidToast: null }),
  // R4 — dramatic jail interaction modals
  prisonGuardModalOpen: false,
  openPrisonGuardModal: () => set({ prisonGuardModalOpen: true }),
  closePrisonGuardModal: () => set({ prisonGuardModalOpen: false }),
  inmateEscapeModalOpen: false,
  openInmateEscapeModal: () => set({ inmateEscapeModalOpen: true }),
  closeInmateEscapeModal: () => set({ inmateEscapeModalOpen: false }),
  jailEscapeResult: null as { success: boolean; roll: number; threshold: number } | null,
  dismissJailEscapeResult: () => set({ jailEscapeResult: null }),
  pendingJailWarpSeat: null as SeatIndex | null,

  // WEB.4.SH (P0.1) — seat-handoff modal state. Enabled by default for hot-seat.
  seatHandoffEnabled: true,
  seatHandoffOpen: false,
  seatHandoffFromSeat: null,
  setSeatHandoffEnabled: (enabled) =>
    set((s) => {
      if (enabled === s.seatHandoffEnabled) return {};
      // Close any open modal if we're disabling (otherwise a tester or solo
      // mode would be locked behind a non-dismissable overlay).
      return enabled
        ? { seatHandoffEnabled: true }
        : { seatHandoffEnabled: false, seatHandoffOpen: false, seatHandoffFromSeat: null };
    }),
  acknowledgeSeatHandoff: () =>
    set((s) => {
      if (!s.seatHandoffOpen) return {};
      return { seatHandoffOpen: false, seatHandoffFromSeat: null };
    }),

  // WEB.4.PM (P0.2) — pause menu state + actions
  paused: false,
  openPauseMenu: () =>
    set((s) => {
      if (s.paused) return {};
      return { paused: true };
    }),
  closePauseMenu: () =>
    set((s) => {
      if (!s.paused) return {};
      return { paused: false };
    }),
  restartGame: () => {
    // Restart uses the existing rngSeed for deterministic replay until
    // MainMenu (P0.3) surfaces a seed picker. resetGame already clears every
    // per-game slice; paused is cleared explicitly so the user lands in idle.
    const s = useGameStore.getState();
    s.resetGame(s.rngSeed);
    set({ paused: false });
  },

  // WEB.4.P1.5 — i18n language
  lang: loadPersistedLang(),
  setLang: (lang) =>
    set((s) => {
      if (lang === s.lang) return {};
      persistLang(lang);
      return { lang };
    }),

  // WEB.4.P2.1 — audio mute (persisted via useAudio hook)
  audioMuted: loadPersistedMute(),
  setAudioMuted: (muted) =>
    set((s) => {
      if (muted === s.audioMuted) return {};
      return { audioMuted: muted };
    }),

  // WEB.4.AR — volume controls (persisted; best-effort swallows errors)
  masterVolume: loadPersistedVolume("gdg.masterVolume", 1),
  bgmVolume: loadPersistedVolume("gdg.bgmVolume", 0.8),
  sfxVolume: loadPersistedVolume("gdg.sfxVolume", 0.8),
  setMasterVolume: (v) =>
    set((s) => {
      // R14 Codex-C MED fix: reject NaN / non-finite — Math.max/min passes them through.
      if (typeof v !== "number" || !Number.isFinite(v)) return {};
      const clamped = Math.max(0, Math.min(1, v));
      if (clamped === s.masterVolume) return {};
      persistVolume("gdg.masterVolume", clamped);
      return { masterVolume: clamped };
    }),
  setBgmVolume: (v) =>
    set((s) => {
      if (typeof v !== "number" || !Number.isFinite(v)) return {};
      const clamped = Math.max(0, Math.min(1, v));
      if (clamped === s.bgmVolume) return {};
      persistVolume("gdg.bgmVolume", clamped);
      return { bgmVolume: clamped };
    }),
  setSfxVolume: (v) =>
    set((s) => {
      if (typeof v !== "number" || !Number.isFinite(v)) return {};
      const clamped = Math.max(0, Math.min(1, v));
      if (clamped === s.sfxVolume) return {};
      persistVolume("gdg.sfxVolume", clamped);
      return { sfxVolume: clamped };
    }),

  // WEB.4.CS — cutscene player state
  cutsceneGroupId: null,
  cutsceneFrameIdx: 0,
  showCutscenes: true,
  setShowCutscenes: (show) =>
    set((s) => {
      if (show === s.showCutscenes) return {};
      // codex CS R2 LOW: disabling mid-sequence must also clear the active
      // cutscene so its setTimeout/keyboard effects unmount with the player
      // overlay. Otherwise Esc/Space would be consumed invisibly.
      if (!show && s.cutsceneGroupId !== null) {
        return { showCutscenes: false, cutsceneGroupId: null, cutsceneFrameIdx: 0 };
      }
      return { showCutscenes: show };
    }),
  playCutscene: (groupId) =>
    set((s) => {
      if (!s.showCutscenes) return {};
      const group = _getCutsceneGroup(groupId);
      if (group === undefined || group.frameIds.length === 0) return {};
      return { cutsceneGroupId: groupId, cutsceneFrameIdx: 0 };
    }),
  advanceCutsceneFrame: () =>
    set((s) => {
      if (s.cutsceneGroupId === null) return {};
      const group = _getCutsceneGroup(s.cutsceneGroupId);
      if (group === undefined) return { cutsceneGroupId: null, cutsceneFrameIdx: 0 };
      const next = s.cutsceneFrameIdx + 1;
      if (next >= group.frameIds.length) {
        // End of sequence — close player.
        return { cutsceneGroupId: null, cutsceneFrameIdx: 0 };
      }
      return { cutsceneFrameIdx: next };
    }),
  skipCutscene: () =>
    set((s) => {
      if (s.cutsceneGroupId === null) return {};
      return { cutsceneGroupId: null, cutsceneFrameIdx: 0 };
    }),

  // WEB.4.MM (P0.3) — main menu phase
  gamePhase: "menu",
  assetGateDone: false,
  setGamePhase: (phase) =>
    set((s) => {
      if (phase === s.gamePhase) return {};
      return { gamePhase: phase };
    }),
  startGame: () => {
    // Transition from menu → char-select.
    // R9: CharSelect stub intercepts before playing.
    // CharSelect "Begin" button calls beginGame() to go to playing + intro cutscene.
    // DA-fix: generate a fresh seed so every new game has a different RNG sequence.
    const s = useGameStore.getState();
    s.resetGame(freshGameSeed());
    set({ gamePhase: "char-select", paused: false });
  },
  beginGame: (numPlayers = 4) => {
    // char-select → playing. Gate-first (2026-06-13): cutscene deferred to
    // startIntroAfterGate() (called by AssetLoadingGate on dismiss) so the
    // intro BGM never races the preload fetches. BA-1: game-start analytics.
    const s = useGameStore.getState();
    // assetGateDone false until the gate dismisses — HexBoard's era BGM waits
    // on it so no audio stream competes with the critical-asset fetches.
    // numPlayers (DEFAULT 4) is locked into the authoritative state HERE — the
    // game-start commit point. Immutable for the rest of the game.
    set({ gamePhase: "playing", paused: false, assetGateDone: false, numPlayers });
    // BA-1: fire game-start analytics event.
    _recordAnalyticsEvent({
      type: "game-start",
      t: Date.now(),
      seatPerks: [...s.playerPerks] as [
        AnalyticsArchetype,
        AnalyticsArchetype,
        AnalyticsArchetype,
        AnalyticsArchetype,
      ],
      rngSeed: s.rngSeed,
    });
    // Intentionally NOT calling s.playCutscene("intro") here.
    // See startIntroAfterGate() below.
  },
  startIntroAfterGate: () => {
    // Called by AssetLoadingGate on dismiss. Starts the intro cutscene if
    // the user hasn't opted out (showCutscenes pref). The gate only fires
    // this once per game-start (it guards with dismissedRef). No gamePhase
    // mutation — we are already in "playing".
    // assetGateDone unblocks HexBoard's era-BGM effect; with showCutscenes on,
    // the cutscene's own crossfade (CutscenePlayer effect) takes over in the
    // same flush — identical to the pre-gate-first same-tick behavior.
    set({ assetGateDone: true });
    const s = useGameStore.getState();
    s.playCutscene("intro");
  },
  returnToMainMenu: () => {
    // Tear down the current game and bring up MainMenu. Called from
    // PauseMenu's Main Menu button. Pre-seed with a fresh value so the next
    // startGame() call doesn't inherit the completed game's seed.
    const s = useGameStore.getState();
    s.resetGame(freshGameSeed());
    set({ gamePhase: "menu", paused: false, assetGateDone: false });
  },

  // R16 /goal Fix B — thin wrappers exposing analytics.ts for facilitator UI.
  exportAnalyticsData: () => _exportAnalyticsSessions(),
  clearAnalyticsData: () => _clearAnalyticsSessions(),

  // R9 — per-seat archetype perks (default assignment).
  // R14 — runtime-mutable via setPlayerPerk with mutual-swap dedupe.
  playerPerks: ["engineer", "scientist", "activist", "politician"] as [
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
    "engineer" | "scientist" | "activist" | "politician",
  ],
  setPlayerPerk: (seat, archetype) =>
    set((s) => {
      // R14 Codex-A MED 1 fix: guard invalid seat / archetype against tuple corruption.
      if (seat !== 0 && seat !== 1 && seat !== 2 && seat !== 3) return {};
      if (
        archetype !== "engineer" &&
        archetype !== "scientist" &&
        archetype !== "activist" &&
        archetype !== "politician"
      ) {
        return {};
      }
      const current = s.playerPerks[seat];
      if (current === undefined) return {};
      if (current === archetype) return {};
      // Find which seat (if any) currently holds the requested archetype.
      const swapSeat = s.playerPerks.findIndex((p) => p === archetype) as -1 | 0 | 1 | 2 | 3;
      const next = [...s.playerPerks] as typeof s.playerPerks;
      next[seat] = archetype;
      if (swapSeat !== -1 && swapSeat !== seat) {
        next[swapSeat] = current;
      }
      return { playerPerks: next };
    }),
  payToLeaveJail: () =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      if (_isGameInputBlocked(s)) return {};
      const seat = s.currentSeat;
      const remaining = s.jailTurnsRemaining[seat] ?? 0;
      if (remaining <= 0) return {};
      const balance = s.tokens[seat] ?? 0;
      const bailPrice = computeBailPrice(s, seat);
      if (balance < bailPrice) {
        console.warn(
          `[payToLeaveJail] Seat ${seat} cannot afford ${bailPrice}T bail (balance=${balance}); still jailed.`,
        );
        return {};
      }
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[seat] = Math.max(0, balance - bailPrice);
      const newJail = [...s.jailTurnsRemaining] as [number, number, number, number];
      newJail[seat] = 0;
      const newMult = [...s.bailMultiplier] as [number, number, number, number];
      newMult[seat] = 0.25;
      const newAttempts = [...s.jailEscapeAttempts] as [number, number, number, number];
      newAttempts[seat] = 0;
      // R6 #1: mark guard NPC interacted on payment SUCCESS (not on modal open).
      const tileIdx = s.playerTiles[seat] ?? 0;
      const guardKey = `${tileIdx}:guard.png`;
      return {
        tokens: newTokens,
        jailTurnsRemaining: newJail,
        bailMultiplier: newMult,
        jailEscapeAttempts: newAttempts,
        // R3 Bug 1 — fire 2s bail-paid toast so player sees confirmation.
        bailPaidToast: { amount: bailPrice },
        // R4: also reset per-turn escape flag on bail payment
        jailEscapeUsedThisTurn: Object.assign(
          [...s.jailEscapeUsedThisTurn] as [boolean, boolean, boolean, boolean],
          { [seat]: false },
        ) as [boolean, boolean, boolean, boolean],
        // R4 — close guard modal on successful payment
        prisonGuardModalOpen: false,
        // R6 #1 — mark guard interacted now that action completed
        npcInteractionState: { ...s.npcInteractionState, [guardKey]: "completed" as const },
      };
    }),
  attemptEscapeJail: () =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      if (_isGameInputBlocked(s)) return {};
      const seat = s.currentSeat;
      if ((s.jailTurnsRemaining[seat] ?? 0) <= 0) {
        console.warn(`[attemptEscapeJail] Seat ${seat} is not in jail.`);
        return {};
      }
      const attempts = s.jailEscapeAttempts[seat] ?? 0;
      if (attempts >= 2) {
        console.warn(`[attemptEscapeJail] Seat ${seat} has used both attempts.`);
        return {};
      }
      // R4: gate on per-turn flag only. turnPhase may be "landed" for jailed players
      // in SidescrollerScene — that is the intended trigger point via InmateEscapeModal.
      if (s.jailEscapeUsedThisTurn[seat]) {
        console.warn(`[attemptEscapeJail] Seat ${seat} already attempted this turn.`);
        return {};
      }
      // RNG threshold: 25% on 1st attempt, 20% on 2nd
      const threshold = attempts === 0 ? 25 : 20;
      const roll = _rng.nextInt(1, 100);
      const success = roll <= threshold;

      const newJailTurns = [...s.jailTurnsRemaining] as [number, number, number, number];
      const newMult = [...s.bailMultiplier] as [number, number, number, number];
      const newAttempts = [...s.jailEscapeAttempts] as [number, number, number, number];

      if (success) {
        newJailTurns[seat] = 0;
        newMult[seat] = 0.25;
        newAttempts[seat] = 0;
        console.info(`[attemptEscapeJail] Seat ${seat} escaped (roll=${roll}/${threshold}).`);
      } else {
        newAttempts[seat] = attempts + 1;
        // R4 spec: each fail +3 turns, bail multiplier ×2 (uniform rule)
        newJailTurns[seat] = (newJailTurns[seat] ?? 0) + 3;
        newMult[seat] = (newMult[seat] ?? 0.25) * 2;
        console.info(
          `[attemptEscapeJail] Seat ${seat} failed (attempt ${attempts + 1}, roll=${roll}/${threshold}). Turns+3, mult×2.`,
        );
      }
      const newUsed = [...s.jailEscapeUsedThisTurn] as [boolean, boolean, boolean, boolean];
      newUsed[seat] = true;
      // R6 #1: mark inmate interacted on attempt (success OR fail). The
      // attempt itself counts as the action.
      const tileIdx = s.playerTiles[seat] ?? 0;
      const inmateKey = `${tileIdx}:inmate.png`;
      return {
        jailTurnsRemaining: newJailTurns,
        bailMultiplier: newMult,
        jailEscapeAttempts: newAttempts,
        jailEscapeUsedThisTurn: newUsed,
        // R4 — store result for InmateEscapeModal flash toast (R10 #5: include roll+threshold)
        jailEscapeResult: { success, roll, threshold },
        inmateEscapeModalOpen: false,
        // R6 #1 — mark inmate interacted now that action completed
        npcInteractionState: { ...s.npcInteractionState, [inmateKey]: "completed" as const },
      };
    }),
  tradingPromptOpen: false,
  // R4 Bug 9: slider-based trade. 1 energy → (8 - era) tokens base rate.
  // Era 1=7T, Era 2=6T, Era 3=5T, Era 4=4T. TradeBonus auras stack on top.
  executeTrade: (energyAmount: number) =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      if (_isGameInputBlocked(s)) return {};
      const seat = s.currentSeat;
      const currentEnergy = s.energy[seat] ?? 0;
      const amount = Math.max(0, Math.min(energyAmount, currentEnergy));
      if (amount === 0) return { tradingPromptOpen: false, tradeOfferRejectionReason: null };
      const newEnergy = [...s.energy] as [number, number, number, number];
      const newTokens = [...s.tokens] as [number, number, number, number];
      newEnergy[seat] = currentEnergy - amount;
      const baseRate = (8 - s.era) as number; // 7|6|5|4
      let perEnergyBonus = 0;
      const seatAuras = s.auraState.bySeat[String(seat)] ?? [];
      for (const aura of seatAuras) {
        if (aura.scope === "TradeBonus") {
          perEnergyBonus += aura.effect.valueInt ?? 0;
        }
      }
      const effectiveRate = baseRate + perEnergyBonus;
      // R9 perk: Politician trade rate 50% of base era rate (politicians tax trade).
      const finalRate =
        s.playerPerks[seat] === "politician"
          ? Math.max(1, Math.floor(effectiveRate * 0.5))
          : effectiveRate;
      newTokens[seat] = Math.max(0, (newTokens[seat] ?? 0) + amount * finalRate);
      // R6 #1: mark trader NPC interacted on successful trade (not on prompt open).
      const tileIdx = s.playerTiles[seat] ?? 0;
      const traderKey = `${tileIdx}:trader-e1.png`;
      return {
        energy: newEnergy,
        tokens: newTokens,
        tradingPromptOpen: false,
        tradeOfferRejectionReason: null,
        npcInteractionState: { ...s.npcInteractionState, [traderKey]: "completed" as const },
      };
    }),
  /**
   * @deprecated Use `executeTrade(N)`. Kept as a thin shim that delegates to
   * executeTrade(1) so any lingering callers / tests get the locked R4 ratio
   * `(8 - era)` instead of the old fixed 5T. Codex MED 2026-05-16 fix.
   */
  confirmTrade: () => {
    get().executeTrade(1);
  },
  skipTrade: () => set({ tradingPromptOpen: false, tradeOfferRejectionReason: null }),

  // WEB.4.UI.14 — threshold event banner state
  lastThresholdEvent: null,
  dismissThresholdEvent: () => set({ lastThresholdEvent: null }),
  highestThresholdTierFired: null,

  // ---------------------------------------------------------------------------
  // N-2 — Climate Crisis Vote actions
  // ---------------------------------------------------------------------------

  setClimateVote: (seat, vote) =>
    set((s) => {
      if (_isGameInputBlocked(s)) return {};
      if (s.pendingClimateVote === null) return {};
      // Codex Phase 2a MED 3 fix: YES vote requires the full 5T. Seats that
      // cannot afford the bid are coerced to NO and a warning is logged so
      // they can't get a free pollution reduction via partial-payment edge.
      const VOTE_COST = 5;
      let effective = vote;
      if (vote === "yes" && (s.tokens[seat] ?? 0) < VOTE_COST) {
        console.warn(
          `[setClimateVote] Seat ${seat} cannot afford ${VOTE_COST}T YES (balance=${s.tokens[seat]}); coercing to NO.`,
        );
        effective = "no";
      }
      const newVotes = [...s.pendingClimateVote.votes] as [
        null | "yes" | "no",
        null | "yes" | "no",
        null | "yes" | "no",
        null | "yes" | "no",
      ];
      // Toggle: clicking same vote again un-votes (null)
      newVotes[seat] = newVotes[seat] === effective ? null : effective;
      return {
        pendingClimateVote: { ...s.pendingClimateVote, votes: newVotes },
      };
    }),

  resolveClimateVote: () =>
    set((s) => {
      if (s.pendingClimateVote === null) return {};
      const { votes } = s.pendingClimateVote;
      // Tally PRESENT seats only (index < numPlayers). Default 4 → all four slots counted,
      // byte-identical. For 2/3 players the absent slots (pre-filled "no") are excluded so a
      // 2-player vote isn't doomed to lose by two phantom "no" votes. Bankrupt seats auto-NO
      // (pre-filled at fire-time); a null (unvoted) present seat counts as NO. Quorum = strict
      // majority of present seats (yesCount > floor(numPlayers/2)), ties → NO — preserving the
      // 4-player "yes needs > 2 of 4" behavior.
      let yesCount = 0;
      let presentCount = 0;
      for (let i = 0; i < 4; i++) {
        if (i >= s.numPlayers) continue;
        presentCount++;
        if (votes[i] === "yes") yesCount++;
      }
      const noCount = presentCount - yesCount; // null + "no" among present seats
      const majorityYes = yesCount > noCount; // ties → NO

      if (!majorityYes) {
        console.info(
          `[resolveClimateVote] NO wins (yes=${yesCount}, no/abstain=${noCount}). No effect.`,
        );
        return { pendingClimateVote: null };
      }

      // Majority YES: debit 5 tokens from each YES voter, reduce 30 pollution proportionally.
      const VOTE_COST = 5;
      const POLLUTION_REDUCTION = 30;

      const newTokens = [...s.tokens] as [number, number, number, number];
      for (let i = 0; i < 4; i++) {
        if (i >= s.numPlayers) continue; // not-in-game seat (default 4 → none skipped)
        if (votes[i] === "yes") {
          newTokens[i as SeatIndex] = Math.max(0, (newTokens[i as SeatIndex] ?? 0) - VOTE_COST);
        }
      }

      const reductions = computeProportionalPollutionReduction(
        s.pollution,
        POLLUTION_REDUCTION,
        s.numPlayers,
      );
      const newPollution = s.pollution.map((p, i) =>
        Math.max(0, p - reductions[i as SeatIndex]),
      ) as [number, number, number, number];

      console.info(
        `[resolveClimateVote] YES wins. Token debits applied. Pollution reductions: ${reductions.join(",")}`,
      );
      // BA-1: minigame-result pattern for climate-vote — Builders BX/BY/BZ: follow this pattern for your minigames.
      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "climate-vote",
        seat: s.currentSeat,
        outcome: "yes-wins",
        tokensDelta: -VOTE_COST,
      });

      // Re-run game-end detection after vote applies (vote may rescue from tipping point).
      const endState = detectGameEnd(
        {
          tokens: newTokens,
          energy: s.energy,
          pollution: newPollution,
          round: s.round,
          lostSeats: s.lostSeats,
          builtFacilities: s.builtFacilities as Record<number, string | undefined>,
          ownedBy: s.ownedBy as Record<number, number | undefined>,
        },
        s.lostSeats,
        s.numPlayers,
      );

      return {
        pendingClimateVote: null,
        tokens: newTokens,
        pollution: newPollution,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
        lostSeats: endState.lostSeats,
      };
    }),
  // R11.2.5 fix #8: 0 means "no era advance yet — first transition can fire freely".
  lastEraAdvanceRound: 0,

  // ---------------------------------------------------------------------------
  // Tile actions
  // ---------------------------------------------------------------------------

  setHoveredTile: (idx, pos) => set({ hoveredTile: idx, hoverPos: pos ?? null }),
  setActiveTile: (idx) => set({ activeTile: idx }),
  selectTile: (idx) => set({ selectedTile: idx }),

  movePlayer: (seat, tileIdx) =>
    set((s) => {
      const next: [number, number, number, number] = [...s.playerTiles] as [
        number,
        number,
        number,
        number,
      ];
      next[seat] = tileIdx;
      return { playerTiles: next, activeTile: tileIdx };
    }),

  setOwnership: (tileIdx, seat) =>
    set((s) => {
      const next = { ...s.ownedBy };
      if (seat === undefined) {
        delete next[tileIdx];
      } else {
        next[tileIdx] = seat;
      }
      return { ownedBy: next };
    }),

  // ---------------------------------------------------------------------------
  // Turn actions
  // ---------------------------------------------------------------------------

  rollDice: () =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse all turn-progressing actions once
      // the game has ended. Caller should call resetGame() to start over.
      if (s.gameStatus === "ended") return {};
      // R9 #7 — block all input during cutscene / post-cutscene dialogue / grand transition.
      if (_isGameInputBlocked(s)) return {};
      // Guard: already rolled this turn — no-op
      if (s.turnPhase === "landed") return {};
      // R20-GF-4: block roll while TargetPicker is open — turn must not advance
      // while the current player is selecting an opponent for an OpponentChosen card.
      if (s.targetPickerCard !== null) return {};
      // WEB.4.UI.13 jail: in-jail seats cannot roll. They must endTurn (decrement
      // remaining) or call payToLeaveJail.
      if ((s.jailTurnsRemaining[s.currentSeat] ?? 0) > 0) {
        console.warn(
          `[rollDice] Seat ${s.currentSeat} is in jail (${s.jailTurnsRemaining[s.currentSeat]} turns remaining); cannot roll.`,
        );
        return {};
      }

      const roll = _rng.nextInt(1, 6);
      const currentTile = s.playerTiles[s.currentSeat] ?? 0;
      const nextTile = (currentTile + roll) % TILE_COUNT;

      const nextTiles: [number, number, number, number] = [...s.playerTiles] as [
        number,
        number,
        number,
        number,
      ];
      nextTiles[s.currentSeat] = nextTile;

      // WEB.4.UI.9c — PassingStart aura decay.
      // When this roll causes the player to pass the Start tile, their auras
      // with discardMode="PassStart" auto-decay (Steam_Engine_Breakthrough,
      // Ocean_Acidification, Drought). Whenever-mode auras do NOT auto-decay —
      // owner manually discards via AuraPanel (user-confirmed 2026-05-12).
      // Teleport landings deliberately skip this decay (not physical movement).
      let nextAuraState = s.auraState;
      // R10 #11 — pass-Start +20 token bonus (separate from land-on-Start Mayor interact bonus).
      const passedStart = didPassStart(currentTile, roll, TILE_COUNT);
      // Compute next tokens early so we can apply both pass and land bonuses before toll.
      const passBonusTokens: [number, number, number, number] = [...s.tokens] as [
        number,
        number,
        number,
        number,
      ];
      if (passedStart) {
        const decayLog = createActionLog();
        const decayRes = decayPassingStartAuras(s.auraState, decayLog, s.currentSeat, getCardById);
        nextAuraState = decayRes.newAura;
        if (decayRes.discardedCount > 0) {
          console.info(
            `[rollDice] Seat ${s.currentSeat} passed Start; ${decayRes.discardedCount} passing-start auras decayed.`,
          );
        }
        passBonusTokens[s.currentSeat] = (passBonusTokens[s.currentSeat] ?? 0) + 20;
        console.info(`[rollDice] Seat ${s.currentSeat} passed Start: +20 tokens.`);
      }

      // Bug 6 fix (WEB.4.PT): tile-landing card draw removed from rollDice.
      // Event / Policy cards now drawn only on E-interact with relevant NPC in
      // SidescrollerScene (newsgirl → Event, bureaucrat → Policy).
      // WEB.4.PT.R2: TILE_DRAW_POOL no longer referenced in confirmTilePick (Teleport branch removed).
      const tileType = _tileTypeByIndex[nextTile] ?? "";

      // hands / detailModalCard / modalRevealState are NOT mutated by rollDice
      // anymore — kept in store as-is until NPC interact fires drawCard/openCardDetail.
      const nextHands = s.hands as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      const nextDetailCard: CardInHand | null = s.detailModalCard;
      const nextRevealState: "cover" | "revealed" = s.modalRevealState;

      // WEB.4.UI.9b — Toll on landing.
      // If the drawer lands on a tile owned by an OPPONENT with a built
      // facility, drawer pays toll. WEB.4.UI.10a: insufficient funds NOW
      // triggers bankruptcy (drawer added to lostSeats; game-end detection
      // runs after).
      // R10 #11: start from passBonusTokens (may already include +20 pass-Start bonus).
      const nextTokens: [number, number, number, number] = passBonusTokens;
      const drawer = s.currentSeat;
      const tileOwner = s.ownedBy[nextTile];
      const tileFacilityId = s.builtFacilities[nextTile];
      let bankruptThisRoll = false;
      const peqAfterRoll = s.peqState;
      // R5 #11 codex HIGH: capture toll info to surface via TollPaidModal.
      let nextPendingToll: GameState["pendingTollPayment"] = null;
      if (tileOwner !== undefined && tileOwner !== drawer && tileFacilityId !== undefined) {
        // WEB.4.UI.11c: use per-tile meta.tollPerLand when present.
        const tileMeta = s.builtFacilityMeta[nextTile];
        const tollBreakdown = computeTollOnLanding(
          tileFacilityId,
          s.auraState,
          tileOwner,
          tileMeta?.tollPerLand,
          // R9 perk: Politician toll × 1.5
          s.playerPerks[tileOwner],
        );
        // R22-GD-7: tile toll is NOT a card-related loss — HalveNextTokenLoss
        // only covers card-effect losses (event threshold, Smog transfer, etc.).
        const tollAmount = tollBreakdown.effective;
        if (tollAmount > 0) {
          const drawerBalance = nextTokens[drawer] ?? 0;
          let actualPaid = 0;
          if (wouldGoBankrupt(drawerBalance, tollAmount)) {
            // Pay whatever they have; mark bankrupt.
            nextTokens[drawer] = 0;
            nextTokens[tileOwner] = Math.max(0, (nextTokens[tileOwner] ?? 0) + drawerBalance);
            actualPaid = drawerBalance;
            bankruptThisRoll = true;
          } else {
            nextTokens[drawer] = Math.max(0, drawerBalance - tollAmount);
            nextTokens[tileOwner] = Math.max(0, (nextTokens[tileOwner] ?? 0) + tollAmount);
            actualPaid = tollAmount;
          }
          // R5 #11 codex HIGH: surface to TollPaidModal — modal will fire
          // after toll is deducted and lander's balance is updated.
          // R19-GA-5: also capture owner's new balance so modal can show +N line.
          nextPendingToll = {
            tileIdx: nextTile,
            facilityId: tileFacilityId,
            ownerSeat: tileOwner as SeatIndex,
            amount: actualPaid,
            newLanderBalance: nextTokens[drawer] ?? 0,
            newOwnerBalance: nextTokens[tileOwner] ?? 0,
          };
        }
      }

      // Compute next lostSeats + game-end status.
      const nextLostSeats = bankruptThisRoll ? new Set([...s.lostSeats, drawer]) : s.lostSeats;
      const endState = detectGameEnd(
        {
          tokens: nextTokens,
          energy: s.energy,
          pollution: s.pollution,
          round: s.round,
          lostSeats: nextLostSeats,
          builtFacilities: s.builtFacilities as Record<number, string | undefined>,
          ownedBy: s.ownedBy as Record<number, number | undefined>,
        },
        nextLostSeats,
        s.numPlayers,
      );

      // T-1: Tourism bonus — apply +3T if current seat has remaining bonus landings.
      let nextTourism = s.tourismBonusTilesRemaining;
      const tourismRemaining = s.tourismBonusTilesRemaining[drawer] ?? 0;
      if (tourismRemaining > 0) {
        const TOURISM_BONUS = 3;
        nextTokens[drawer] = (nextTokens[drawer] ?? 0) + TOURISM_BONUS;
        const newTourism = [...s.tourismBonusTilesRemaining] as [number, number, number, number];
        newTourism[drawer] = tourismRemaining - 1;
        nextTourism = newTourism;
        console.info(
          `[rollDice] Seat ${drawer} tourism bonus +${TOURISM_BONUS}T (${tourismRemaining - 1} remaining).`,
        );
      }

      // BuildMenu auto-open removed from rollDice (Bug 4 fix — WEB.4.PT).
      // BuildMenu now opens only on E-interact with the Builder NPC in
      // SidescrollerScene (triggerNpcInteract, builder-e1.png branch).
      // WEB.4.PT.R2: isBuildable computation removed from confirmTilePick Teleport branch too.

      // R10 #12: Teleport TilePicker no longer opened here (rollDice).
      // Previously, setting tilePickerOpen:true in rollDice caused the picker to
      // render against HexBoard (main screen) BEFORE SidescrollerScene mounted —
      // a stray floating UI during the board token-slide animation (1000ms delay).
      // Picker now opens exclusively via the keeper NPC E-interact inside
      // SidescrollerScene (triggerNpcInteract keeper.png branch → openTilePicker).
      // This is functionally identical — player must talk to keeper to teleport.
      const tilePickerState = {};

      // WEB.4.UI.13 — Jail tile landing.
      // Setting jailTurnsRemaining[seat] = 3 per docx ("Can not move 3 turns").
      // Subsequent endTurn calls decrement; rollDice gated until 0 or payToLeaveJail.
      let nextJail = s.jailTurnsRemaining;
      if (tileType === "Jail") {
        const newJail = [...s.jailTurnsRemaining] as [number, number, number, number];
        newJail[s.currentSeat] = 3;
        nextJail = newJail;
      }

      // WEB.4.UI.13 — Trading tile landing → open trade prompt
      // ("Trade 1 energy for 5 tokens. Nor reversible." per docx).
      const tradingPromptOpen = tileType === "Trading";

      // N-1 (R16): Energy Dilemma — terrain + era>=2 + per-landing one-shot.
      // Inline (mirrors tradingPromptOpen); modal gates itself on boardAnimationActive.
      // R21-GF-7: RNG scenario selection with recent-exclusion bias.
      const _edSeatRD = s.currentSeat;
      const _edKeyRD = `${nextTile}:${_edSeatRD}`;
      const _edEligibleRD =
        SCOUT_TERRAIN_TYPES.has(tileType) &&
        s.era >= 2 &&
        s.pendingQuiz === null &&
        s.pendingAuction === null &&
        s.pendingClimateVote === null &&
        s.pendingScout === null &&
        s.pendingEnergyDilemma === null &&
        s.pendingTrade === null &&
        s.pendingPollutionTax === null &&
        !s.energyDilemmaShownThisLanding[_edKeyRD];
      const _edScenarioRD = _edEligibleRD
        ? _pickEnergyDilemmaScenario(s.recentEnergyDilemmaScenarios)
        : null;
      const nextEnergyDilemmaState =
        _edEligibleRD && _edScenarioRD !== null
          ? {
              pendingEnergyDilemma: {
                tileIdx: nextTile,
                terrain: tileType,
                optionsKey: _edScenarioRD,
              },
              energyDilemmaShownThisLanding: {
                ...s.energyDilemmaShownThisLanding,
                [_edKeyRD]: true,
              },
              recentEnergyDilemmaScenarios: [
                _edScenarioRD,
                ...s.recentEnergyDilemmaScenarios,
              ].slice(0, 3),
            }
          : {};

      return {
        playerTiles: nextTiles,
        activeTile: nextTile,
        lastDiceRoll: roll,
        turnPhase: "landed" as const,
        // R11.2 — set boardAnimationActive=true so ALL tile-stage UI (TileTopBar,
        // PrimaryActionZone landed actions, PlayerHUD jail buttons, PrisonGuardModal,
        // etc.) stays hidden until HexBoard finishes walking the token. HexBoard's
        // useEffect flips this back to false at the end of the walk (or immediately
        // for snap/teleport cases).
        boardAnimationActive: true,
        // WEB.4.PT.Phase1 codex R2 HIGH: seed scenePos to spawn at every landing so
        // SidescrollerScene starts fresh (not inheriting prior tile's walked position).
        // 315 = TILE_WIDTH (900) × 0.35 spawn offset. Mirror in confirmTilePick Teleport branch.
        scenePos: { x: 315, y: 0 },
        hands: nextHands,
        detailModalCard: nextDetailCard,
        modalRevealState: nextRevealState,
        // buildMenuOpen stays false — opened by Builder NPC E-interact (Bug 4 fix)
        buildMenuOpen: false,
        tokens: nextTokens,
        auraState: nextAuraState,
        // WEB.4.UI.16 — persist post-toll PEQ state (HalveNextTokenLoss may have consumed).
        peqState: peqAfterRoll,
        // T-1: persist updated tourism bonus counter.
        tourismBonusTilesRemaining: nextTourism,
        lostSeats: nextLostSeats,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
        jailTurnsRemaining: nextJail,
        tradingPromptOpen,
        // R19-GC-2: fresh Trading-tile landing clears any stale rejection reason.
        ...(tradingPromptOpen ? { tradeOfferRejectionReason: null } : {}),
        // R5 #11: forced-acknowledge TollPaidModal — non-null when toll deducted.
        pendingTollPayment: nextPendingToll,
        // N-1 (R16): set dilemma pending inline with landing state so the modal
        // can gate itself on boardAnimationActive (mirrors tradingPromptOpen pattern).
        ...nextEnergyDilemmaState,
        ...tilePickerState,
      };
    }),

  endTurn: () =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      // R9 #7 — block all input during cutscene / post-cutscene dialogue / grand transition.
      if (_isGameInputBlocked(s)) return {};
      // WEB.4.UI.9a: at end of CURRENT seat's turn, accrue energy + pollution
      // from facilities owned by that seat. Active Energy / Pollution auras
      // modify the per-facility delta via aura-tick.ts.
      const endingSeat = s.currentSeat;
      const { energy: deltaE, pollution: deltaP } = computeTurnEndDeltas(
        s.builtFacilities as Record<number, string | undefined>,
        s.ownedBy as Record<number, number | undefined>,
        s.auraState,
        endingSeat,
        // WEB.4.UI.11c: pass per-tile meta so era-specific stats apply.
        s.builtFacilityMeta,
        // R9: pass perk so aura-tick applies per-archetype modifiers.
        s.playerPerks[endingSeat],
      );

      const newEnergy = [...s.energy] as [number, number, number, number];
      const newPollution = [...s.pollution] as [number, number, number, number];
      newEnergy[endingSeat] = Math.max(0, (newEnergy[endingSeat] ?? 0) + deltaE);
      newPollution[endingSeat] = Math.max(0, (newPollution[endingSeat] ?? 0) + deltaP);

      // Skip past bankrupt AND not-in-game seats (>= numPlayers) when advancing turn.
      // Keep the 4-slot `% 4` wrap (4 physical seat slots); the skip condition is
      // !seatActive — present AND not lost. Seat 0 is always in-game (numPlayers>=2),
      // so wrap-to-0 round increment stays correct for 2/3/4 players.
      let nextSeat = ((endingSeat + 1) % 4) as SeatIndex;
      // Increment round when seat wraps to 0 (start of a new round).
      let nextRound = nextSeat === 0 ? s.round + 1 : s.round;
      let safety = 4;
      while (!seatActive(s, nextSeat) && safety > 0) {
        nextSeat = ((nextSeat + 1) % 4) as SeatIndex;
        if (nextSeat === 0) nextRound = s.round + 1;
        safety--;
      }

      // R9 — player-driven era advancement (supersedes round-only schedule).
      // R11.2.5 fix #8: cap step to s.era+1 AND require 2-round cooldown
      // between era changes so transition cutscenes never back-to-back.
      const eraFromFacilities = _shouldAdvanceEra({
        era: s.era,
        round: nextRound,
        builtFacilities: s.builtFacilities as Record<number, string>,
        builtFacilityMeta: s.builtFacilityMeta,
      });
      const desiredEra =
        eraFromFacilities > computeEraForRound(nextRound)
          ? eraFromFacilities
          : computeEraForRound(nextRound);
      const ERA_COOLDOWN_ROUNDS = 2;
      const sinceLastAdvance = nextRound - s.lastEraAdvanceRound;
      let nextEra: 1 | 2 | 3 | 4 = s.era;
      if (desiredEra > s.era && sinceLastAdvance >= ERA_COOLDOWN_ROUNDS) {
        nextEra = Math.min(s.era + 1, desiredEra) as 1 | 2 | 3 | 4;
      }
      const nextLastEraAdvanceRound = nextEra > s.era ? nextRound : s.lastEraAdvanceRound;

      // WEB.4.UI.13 — decrement ending seat's jail counter (if any).
      let nextJail = s.jailTurnsRemaining;
      let nextBailMult = s.bailMultiplier;
      let nextEscapeAttempts = s.jailEscapeAttempts;
      if ((s.jailTurnsRemaining[endingSeat] ?? 0) > 0) {
        const newJail = [...s.jailTurnsRemaining] as [number, number, number, number];
        newJail[endingSeat] = Math.max(0, (newJail[endingSeat] ?? 0) - 1);
        nextJail = newJail;
        if ((newJail[endingSeat] ?? 0) === 0) {
          // Reset bail + attempts when jail term ends naturally
          const newMult = [...s.bailMultiplier] as [number, number, number, number];
          const newAtt = [...s.jailEscapeAttempts] as [number, number, number, number];
          newMult[endingSeat] = 0.25;
          newAtt[endingSeat] = 0;
          nextBailMult = newMult;
          nextEscapeAttempts = newAtt;
        }
      }

      // R20-GD-1 — Drought: decrement ending seat's Hydro-block counter (if any).
      let nextHydroBlock = s.hydroBlockTurns;
      if ((s.hydroBlockTurns[endingSeat] ?? 0) > 0) {
        const newBlock = [...s.hydroBlockTurns] as [number, number, number, number];
        newBlock[endingSeat] = Math.max(0, (newBlock[endingSeat] ?? 0) - 1);
        nextHydroBlock = newBlock;
      }

      // N-2 — Climate Crisis Vote trigger (DEFERRED). Codex Phase 2a HIGH 3:
      // detection must run AFTER headline effects so headline-driven pollution
      // crossings (e.g. punish_fossils +2) can also trigger the vote. Moved
      // below the headline block; declared here for return-shape symmetry.
      let nextPendingClimateVote = s.pendingClimateVote;
      let nextClimateVoteFired = s.climateVoteFired;
      // CS.4: one-shot flag for first climate vote cameo.
      let nextFirstClimateVoteShown = s.firstClimateVoteShown;

      // WEB.4.UI.14 — threshold consequence card on UPWARD tier crossing.
      // Codex R1 HIGH 1 fix: gate on tier rank vs `highestThresholdTierFired`,
      // NOT on era change. Same-tier or lower never re-fires; upward crossing
      // (null→medium, medium→high, etc.) fires exactly once per tier.
      let nextTokens = s.tokens;
      let nextEnergyAfterEvent = newEnergy;
      let nextPollutionAfterEvent = newPollution;
      let lastThresholdEvent: GameState["lastThresholdEvent"] = s.lastThresholdEvent;
      let nextHighestTier = s.highestThresholdTierFired;
      let peqAfterThreshold = s.peqState;
      // WEB.4.UI.PT-bonus — track bankruptcies from threshold negative deltas
      let nextLostSeatsThreshold = s.lostSeats;
      {
        const total = newPollution[0] + newPollution[1] + newPollution[2] + newPollution[3];
        const tier = tierForPollution(total);
        if (
          tier !== null &&
          thresholdTierRank(tier) > thresholdTierRank(s.highestThresholdTierFired)
        ) {
          const card = pickThresholdCard(tier, _rng);
          if (card !== null) {
            console.info(
              `[endTurn] Threshold tier ${s.highestThresholdTierFired ?? "none"}→${tier} crossed at pollution=${total}. Triggering: ${card.cardName}`,
            );
            const tokDelta = card.tokenDelta ?? 0;
            const eneDelta = card.energyDelta ?? 0;
            const polDelta = card.pollutionDelta ?? 0;
            nextTokens = [...s.tokens] as [number, number, number, number];
            nextEnergyAfterEvent = [...nextEnergyAfterEvent] as [number, number, number, number];
            nextPollutionAfterEvent = [...nextPollutionAfterEvent] as [
              number,
              number,
              number,
              number,
            ];
            // PRESENT seats only — absent seats (>= numPlayers) keep INITIAL values;
            // applying a threshold delta to them corrupts the pollution sum (climate
            // meter / game-end) and the bankrupt count in 2/3-player games.
            const seats: SeatIndex[] = seatsInGame(s.numPlayers);
            for (const seat of seats) {
              if (nextLostSeatsThreshold.has(seat)) continue;
              if (tokDelta !== 0) {
                // WEB.4.UI.16 — when threshold imposes a token LOSS (tokDelta < 0),
                // honor HalveNextTokenLoss per-seat (each seat may hold its own).
                let appliedDelta = tokDelta;
                if (tokDelta < 0) {
                  const halvedT = _applyHalveNextTokenLoss(peqAfterThreshold, seat, -tokDelta);
                  appliedDelta = -halvedT.halvedLoss;
                  peqAfterThreshold = halvedT.newPeqState;
                  if (halvedT.consumed) {
                    const origLoss = -tokDelta;
                    const halvLoss = halvedT.halvedLoss;
                    console.info(
                      `[endTurn:threshold] Seat ${seat} HalveNextTokenLoss consumed: loss ${tokDelta}→${appliedDelta}.`,
                    );
                    // R22-GD-7 — record lastHalveNextTokenLossUse for regression + UI toast.
                    useGameStore.setState({
                      lastHalveNextTokenLossUse: {
                        seat: seat as SeatIndex,
                        originalAmount: origLoss,
                        halvedAmount: halvLoss,
                        sourceContext: "threshold-event",
                      },
                    });
                    // R9 #10 — AuraTriggeredToast: event loss halved.
                    useGameStore
                      .getState()
                      .showAuraTriggeredToast(
                        `Event loss halved: ${origLoss}T → ${halvLoss}T`,
                        "Climate Adaptation",
                      );
                    window.setTimeout(
                      () => useGameStore.getState().clearLastHalveNextTokenLossUse(),
                      2000,
                    );
                  }
                }
                // WEB.4.UI.PT-bonus — bankruptcy on involuntary negative threshold delta
                if (appliedDelta < 0 && wouldGoBankrupt(nextTokens[seat] ?? 0, -appliedDelta)) {
                  console.info(
                    `[endTurn:threshold] Seat ${seat} bankrupt from threshold card (balance=${nextTokens[seat]}, loss=${-appliedDelta}).`,
                  );
                  nextTokens[seat] = 0;
                  nextLostSeatsThreshold = bankruptSeat(
                    { lostSeats: nextLostSeatsThreshold },
                    seat,
                  );
                } else {
                  nextTokens[seat] = Math.max(0, (nextTokens[seat] ?? 0) + appliedDelta);
                }
              }
              if (eneDelta !== 0) {
                nextEnergyAfterEvent[seat] = Math.max(
                  0,
                  (nextEnergyAfterEvent[seat] ?? 0) + eneDelta,
                );
              }
              if (polDelta !== 0) {
                nextPollutionAfterEvent[seat] = Math.max(
                  0,
                  (nextPollutionAfterEvent[seat] ?? 0) + polDelta,
                );
              }
            }
            if (card.deferred === true) {
              console.warn(
                `[endTurn:threshold] Card ${card.id} has unsupported effects beyond simple deltas (UI.14 v1 — wires resource deltas only). Other effects skipped.`,
              );
            }
            lastThresholdEvent = {
              tier: card.tier,
              cardId: card.id,
              cardName: card.cardName,
              description: card.description,
            };
            nextHighestTier = tier;
          }
        }
      }

      // WEB.4.PT codex R1 HIGH (jail+bankruptcy bundle): threshold-card bankruptcy
      // may have just added the chosen nextSeat to nextLostSeatsThreshold. The
      // initial nextSeat skip above used PRE-threshold s.lostSeats, so we must
      // re-skip using the updated set. Each (nextSeat → 0) wrap still increments
      // round once.
      if (nextLostSeatsThreshold !== s.lostSeats) {
        let safetyPostThreshold = 4;
        // Skip newly-bankrupt seats AND not-in-game seats (>= numPlayers). Uses the
        // updated threshold list (not s.lostSeats); seatInGame guards the absent seats.
        while (
          (!seatInGame(s.numPlayers, nextSeat) || nextLostSeatsThreshold.has(nextSeat)) &&
          safetyPostThreshold > 0
        ) {
          nextSeat = ((nextSeat + 1) % 4) as SeatIndex;
          if (nextSeat === 0) nextRound = s.round + 1;
          safetyPostThreshold--;
        }
      }

      // WEB.4.UI.10a + UI.14 R1 HIGH 2 (Codex) — detect end conditions AFTER
      // threshold-card pollution is applied. Codex Phase 2a HIGH 2 fix: this
      // becomes `let` because headline + climate-vote effects (below) require
      // a second pass with their updated totals.
      let endState = detectGameEnd(
        {
          tokens: nextTokens,
          energy: nextEnergyAfterEvent,
          pollution: nextPollutionAfterEvent,
          round: nextRound,
          lostSeats: nextLostSeatsThreshold,
          builtFacilities: s.builtFacilities as Record<number, string | undefined>,
          ownedBy: s.ownedBy as Record<number, number | undefined>,
        },
        nextLostSeatsThreshold,
        s.numPlayers,
      );

      // WEB.4.SH (P0.1) — Open hot-seat handoff modal between turns. Skip when
      // (a) handoff disabled (test mode / future solo / multiplayer), (b) the
      // game just ended (GameOverModal owns the screen), or (c) only one seat
      // is still active (no opponent to hand off to).
      const activeSeatCount =
        s.numPlayers - (endState.lostSeats?.size ?? nextLostSeatsThreshold.size ?? 0);
      const shouldOpenHandoff =
        s.seatHandoffEnabled === true && endState.status !== "ended" && activeSeatCount > 1;

      // N-3 — News Headline Bonus.
      // Pick and apply a headline on every round-wrap (nextRound > s.round, i.e. nextSeat===0
      // after the seat-skip loop). Round 1→2 = first round-wrap = first headline.
      // Effect is applied to nextTokens / nextPollutionAfterEvent here (store-side).
      let nextHeadline = s.currentHeadline;
      let nextTokensAfterHeadline = nextTokens;
      let nextPollutionAfterHeadline = nextPollutionAfterEvent;
      if (nextRound > s.round) {
        // nextRound advanced — pick a headline for this new round
        const allHeadlines = (
          headlinesJson as {
            headlines: Array<{
              id: string;
              title: string;
              body: string;
              effect: string;
              era: number;
            }>;
          }
        ).headlines;
        const eligibleHeadlines = allHeadlines.filter((h) => h.era <= nextEra);
        if (eligibleHeadlines.length > 0) {
          const hIdx = _rng.nextInt(0, eligibleHeadlines.length - 1);
          const picked = eligibleHeadlines[hIdx];
          if (picked !== undefined) {
            nextHeadline = {
              id: picked.id,
              title: picked.title,
              body: picked.body,
              effect: picked.effect as
                | "reward_renewables"
                | "reward_low_pollution"
                | "punish_fossils"
                | "global_bonus"
                | "flavor",
              era: picked.era,
            };
            // Apply effect to all active seats (present < numPlayers AND not bankrupt).
            // Default 4 → all four eligible, byte-identical. Excludes not-in-game seats so a
            // global_bonus / reward_low_pollution never credits a phantom (INITIAL) seat.
            const activeSeatsForHeadline = [0, 1, 2, 3].filter(
              (i) => i < s.numPlayers && !nextLostSeatsThreshold.has(i as SeatIndex),
            ) as SeatIndex[];
            nextTokensAfterHeadline = [...nextTokens] as [number, number, number, number];
            nextPollutionAfterHeadline = [...nextPollutionAfterEvent] as [
              number,
              number,
              number,
              number,
            ];

            switch (picked.effect) {
              case "reward_renewables": {
                // +10 tokens to each seat with >= 2 renewable facilities
                for (const seat of activeSeatsForHeadline) {
                  let renewCount = 0;
                  for (const [tileStr, facilityId] of Object.entries(s.builtFacilities)) {
                    if (s.ownedBy[Number(tileStr)] !== seat) continue;
                    const def = FACILITIES.find((f) => f.id === facilityId);
                    if (def?.category === "Renewable") renewCount++;
                  }
                  if (renewCount >= 2) {
                    nextTokensAfterHeadline[seat] = (nextTokensAfterHeadline[seat] ?? 0) + 10;
                    console.info(
                      `[endTurn:headline] Seat ${seat} reward_renewables +10T (${renewCount} renewables).`,
                    );
                  }
                }
                break;
              }
              case "reward_low_pollution": {
                // +8 tokens to each seat with pollution < 20
                for (const seat of activeSeatsForHeadline) {
                  if ((nextPollutionAfterEvent[seat] ?? 0) < 20) {
                    nextTokensAfterHeadline[seat] = (nextTokensAfterHeadline[seat] ?? 0) + 8;
                    console.info(`[endTurn:headline] Seat ${seat} reward_low_pollution +8T.`);
                  }
                }
                break;
              }
              case "punish_fossils": {
                // +2 pollution to each seat that owns at least 1 fossil facility
                for (const seat of activeSeatsForHeadline) {
                  let hasFossil = false;
                  for (const [tileStr, facilityId] of Object.entries(s.builtFacilities)) {
                    if (s.ownedBy[Number(tileStr)] !== seat) continue;
                    const def = FACILITIES.find((f) => f.id === facilityId);
                    if (def?.category === "Fossil") {
                      hasFossil = true;
                      break;
                    }
                  }
                  if (hasFossil) {
                    nextPollutionAfterHeadline[seat] = (nextPollutionAfterHeadline[seat] ?? 0) + 2;
                    console.info(`[endTurn:headline] Seat ${seat} punish_fossils +2 pollution.`);
                  }
                }
                break;
              }
              case "global_bonus": {
                // +5 tokens to all active seats
                for (const seat of activeSeatsForHeadline) {
                  nextTokensAfterHeadline[seat] = (nextTokensAfterHeadline[seat] ?? 0) + 5;
                }
                console.info(
                  `[endTurn:headline] global_bonus +5T to ${activeSeatsForHeadline.length} seats.`,
                );
                break;
              }
              default:
                // "flavor" — no mechanical effect
                break;
            }
          }
        }
      }

      // Codex Phase 2a HIGH 2 fix: re-run game-end detection AFTER headline
      // effects. punish_fossils can add +2 pollution per fossil-owning seat,
      // which may cross the tipping-point threshold. The earlier endState
      // would otherwise be stale and miss this end.
      if (nextPollutionAfterHeadline !== nextPollutionAfterEvent) {
        endState = detectGameEnd(
          {
            tokens: nextTokensAfterHeadline,
            energy: nextEnergyAfterEvent,
            pollution: nextPollutionAfterHeadline,
            round: nextRound,
            lostSeats: nextLostSeatsThreshold,
            builtFacilities: s.builtFacilities as Record<number, string | undefined>,
            ownedBy: s.ownedBy as Record<number, number | undefined>,
          },
          nextLostSeatsThreshold,
          s.numPlayers,
        );
      }

      // N-2 — Climate Crisis Vote trigger (codex HIGH 3: moved after headline
      // so headline-driven pollution crossings also trigger the vote).
      // Fires the FIRST time total pollution crosses 300 (one-shot per game).
      // CF-1 expansion: scaled 100 → 300 (3× round expansion 10 → 30).
      // Gate: don't fire if game just ended OR cutscene/dialogue is active
      // (the modal timer would expire while voting is blocked).
      if (
        !s.climateVoteFired &&
        s.pendingClimateVote === null &&
        endState.status !== "ended" &&
        !s.boardAnimationActive
      ) {
        const totalPollAfterAll =
          nextPollutionAfterHeadline[0] +
          nextPollutionAfterHeadline[1] +
          nextPollutionAfterHeadline[2] +
          nextPollutionAfterHeadline[3];
        if (totalPollAfterAll > 300) {
          const initialVotes: [
            null | "yes" | "no",
            null | "yes" | "no",
            null | "yes" | "no",
            null | "yes" | "no",
          ] = [null, null, null, null];
          for (let i = 0; i < 4; i++) {
            // Pre-fill NON-present seats (>= numPlayers) AND bankrupt seats as "no" so the
            // modal's votes.every(v!==null) completion never waits on an absent/bankrupt seat.
            // (Tally counts present seats only — see resolveClimateVote — so an inactive
            // "no" never affects the result; for default-4 only bankrupt seats are pre-filled,
            // byte-identical to before.)
            if (i >= s.numPlayers || nextLostSeatsThreshold.has(i as SeatIndex)) {
              initialVotes[i as SeatIndex] = "no";
            }
          }
          nextPendingClimateVote = {
            proposedAt: nextRound,
            deadline: Date.now() + 15_000,
            votes: initialVotes,
          };
          nextClimateVoteFired = true;
          console.info(`[endTurn] Climate vote triggered at total pollution=${totalPollAfterAll}.`);
          // CS.4 Trigger 3 — first climate vote cameo (sad, SDG 17 fact). One-shot.
          nextFirstClimateVoteShown = true;
        }
      }

      // BA-1: fire round-end analytics event when the round counter advances.
      if (nextRound > s.round) {
        const _activeSeatsCount = s.numPlayers - nextLostSeatsThreshold.size;
        _recordAnalyticsEvent({
          type: "round-end",
          t: Date.now(),
          round: s.round,
          era: nextEra,
          tokens: [...nextTokensAfterHeadline] as [number, number, number, number],
          energy: [...nextEnergyAfterEvent] as [number, number, number, number],
          pollution: [...nextPollutionAfterHeadline] as [number, number, number, number],
          activeSeats: _activeSeatsCount,
        });
      }
      // BA-1: fire game-end analytics event when the game ends (endTurn path).
      if (endState.status === "ended") {
        _recordAnalyticsEvent({
          type: "game-end",
          t: Date.now(),
          endReason: endState.reason ?? "unknown",
          winnerSeat: endState.winnerSeat ?? null,
          round: nextRound,
        });
      }

      return {
        currentSeat: nextSeat,
        round: nextRound,
        era: nextEra,
        // R11.2.5 fix #8: track round of last advance for 2-round cooldown.
        lastEraAdvanceRound: nextLastEraAdvanceRound,
        tokens: nextTokensAfterHeadline,
        energy: nextEnergyAfterEvent,
        pollution: nextPollutionAfterHeadline,
        activeTile: null,
        selectedTile: null,
        lastDiceRoll: null,
        // WEB.4.PT codex R1 MED — reset per-turn escape-used flags so next seat starts fresh.
        jailEscapeUsedThisTurn: [false, false, false, false] as [
          boolean,
          boolean,
          boolean,
          boolean,
        ],
        gameStatus: endState.status,
        endReason: endState.reason,
        lostSeats: endState.lostSeats,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
        jailTurnsRemaining: nextJail,
        bailMultiplier: nextBailMult,
        jailEscapeAttempts: nextEscapeAttempts,
        // R20-GD-1 — Drought: persist post-decrement Hydro-block counter.
        hydroBlockTurns: nextHydroBlock,
        lastThresholdEvent,
        highestThresholdTierFired: nextHighestTier,
        // WEB.4.UI.16 — persist post-threshold PEQ state.
        peqState: peqAfterThreshold,
        // WEB.4.SH (P0.1) — open seat-handoff modal between turns.
        seatHandoffOpen: shouldOpenHandoff,
        seatHandoffFromSeat: shouldOpenHandoff ? endingSeat : null,
        // R8 codex LOW: close hand-slot drawer on endTurn so next player's
        // landed phase starts clean (drawer not stale-open from prior turn).
        handSlotDrawerOpen: false,
        // N-2 — climate vote state
        pendingClimateVote: nextPendingClimateVote,
        climateVoteFired: nextClimateVoteFired,
        // N-3 — headline state
        currentHeadline: nextHeadline,
        // M-1: clear quiz rate-limit on round-wrap (nextSeat===0 means round advanced).
        quizCompletedThisRound: nextSeat === 0 ? {} : s.quizCompletedThisRound,
        // M-1: clear any stale pending quiz offer on turn advance.
        pendingQuiz: null,
        // T-1: clear scout one-shot gate on turn advance (new landing each turn).
        scoutCompletedThisLanding: {} as Record<string, boolean>,
        // T-1: clear stale pending scout on turn advance.
        pendingScout: null,
        // N-1: clear energy dilemma one-shot gate + stale pending on turn advance.
        energyDilemmaShownThisLanding: {} as Record<string, boolean>,
        pendingEnergyDilemma: null,
        // CS.4 — first climate vote cameo (fires on the same turn that nextFirstClimateVoteShown flips).
        firstClimateVoteShown: nextFirstClimateVoteShown,
        ...(nextFirstClimateVoteShown && !s.firstClimateVoteShown
          ? {
              pendingMaeCameo: {
                mood: "sad" as const,
                line: "SDG 17: global cooperation — no nation solves climate alone.",
              },
            }
          : {}),
        // Bug 5 fix (WEB.4.PT): if the incoming seat is jailed AND already sitting
        // on the Jail tile, skip idle and go straight to "landed" so
        // SidescrollerScene mounts. The player can then interact with guard/inmate
        // NPCs (pay bail / attempt escape) without needing to roll first.
        // Note: nextJail still has the POST-decrement value from this endTurn call,
        // so a seat whose counter just hit 0 won't be auto-promoted (they roll normally).
        //
        // R10 #6 era-warp gate: when an era transition fires (nextEra > s.era), the
        // CutsceneTriggerWatcher will play a cutscene on the next React render cycle.
        // If we immediately set turnPhase="landed" for a jailed incoming seat, the
        // SidescrollerScene mounts DURING the cutscene. Instead, store the seat in
        // pendingJailWarpSeat and apply the warp in dismissGrandTransition, which fires
        // only after the full cutscene + dialogue + grand-transition sequence ends.
        ...(() => {
          const nextSeatIsJailed =
            (nextJail[nextSeat] ?? 0) > 0 &&
            (_tileTypeByIndex[s.playerTiles[nextSeat] ?? 0] ?? "") === "Jail";
          const eraTransitionPending = nextEra > s.era;
          if (nextSeatIsJailed && eraTransitionPending) {
            // Defer warp — cutscene about to play
            return { turnPhase: "idle" as const, pendingJailWarpSeat: nextSeat };
          }
          return {
            turnPhase: nextSeatIsJailed ? ("landed" as const) : ("idle" as const),
            pendingJailWarpSeat: null,
          };
        })(),
      };
    }),

  // ---------------------------------------------------------------------------
  // R10 #11 — Mayor / Start tile land bonus
  // ---------------------------------------------------------------------------

  claimStartBonus: () =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      if (_isGameInputBlocked(s)) return {};
      const seat = s.currentSeat;
      const tileIdx = s.playerTiles[seat] ?? 0;
      // R10 codex MED 2: tile-validate + idempotent guard. Direct dispatches or
      // duplicate callbacks must not double-mint tokens off-Start.
      const currentTileType = _tileTypeByIndex[tileIdx] ?? "";
      if (currentTileType !== "Start") return {};
      const mayorKey = `${tileIdx}:mayor.png`;
      if (s.npcInteractionState[mayorKey] === "completed") return {};
      const START_BONUS = 20;
      const next = [...s.tokens] as [number, number, number, number];
      next[seat] = (next[seat] ?? 0) + START_BONUS;
      console.info(`[claimStartBonus] Seat ${seat} claims +${START_BONUS} tokens from Mayor.`);
      return {
        tokens: next,
        npcInteractionState: { ...s.npcInteractionState, [mayorKey]: "completed" as const },
      };
    }),

  // ---------------------------------------------------------------------------
  // Resource actions — all clamp at 0 (cannot go negative)
  // ---------------------------------------------------------------------------

  adjustTokens: (seat, delta) =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      const next: [number, number, number, number] = [...s.tokens] as [
        number,
        number,
        number,
        number,
      ];
      next[seat] = Math.max(0, (next[seat] ?? 0) + delta);
      return { tokens: next };
    }),

  adjustEnergy: (seat, delta) =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      const next: [number, number, number, number] = [...s.energy] as [
        number,
        number,
        number,
        number,
      ];
      next[seat] = Math.max(0, (next[seat] ?? 0) + delta);
      return { energy: next };
    }),

  adjustPollution: (seat, delta) =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      const next: [number, number, number, number] = [...s.pollution] as [
        number,
        number,
        number,
        number,
      ];
      next[seat] = Math.max(0, (next[seat] ?? 0) + delta);
      return { pollution: next };
    }),

  // ---------------------------------------------------------------------------
  // WEB.4.UI.3 — Hand / card actions
  // ---------------------------------------------------------------------------

  drawCard: (seat, pool) => {
    let drawn: CardInHand | null = null;
    set((s) => {
      if (s.gameStatus === "ended") return {};
      const currentHand = s.hands[seat];

      const cardType = pool === "event" ? "Event" : "Policy";
      const allPoolCards = _cardsByType(cardType);
      if (allPoolCards.length === 0) return {};

      // R13 Bug #10 fix: for policy pool, exclude already-drawn IDs so the same
      // card cannot be drawn twice. When the full pool is spent, reset the tracker
      // (deck reshuffles) rather than deadlocking.
      // R18-FB-1: same dual-pool pattern applied to event pool.
      let poolCards = allPoolCards;
      let nextSpentPolicyIds = s.spentPolicyIds;
      let nextSpentEventIds = s.spentEventIds;
      if (pool === "policy") {
        const spentSet = new Set(s.spentPolicyIds);
        const remaining = allPoolCards.filter((c) => !spentSet.has(c.name));
        if (remaining.length === 0) {
          // All policies drawn — reshuffle: clear spent set and use full pool.
          poolCards = allPoolCards;
          nextSpentPolicyIds = [];
        } else {
          poolCards = remaining;
        }
      } else {
        // event pool depletion (R18-FB-1)
        const spentSet = new Set(s.spentEventIds);
        const remaining = allPoolCards.filter((c) => !spentSet.has(c.name));
        if (remaining.length === 0) {
          // All events drawn — reshuffle: clear spent set and use full pool.
          poolCards = allPoolCards;
          nextSpentEventIds = [];
        } else {
          poolCards = remaining;
        }
      }

      const idx = _rng.nextInt(0, poolCards.length - 1);
      const picked = poolCards[idx];
      if (picked === undefined) return {};

      const instanceId = String(_rng.nextInt(0, 2 ** 31 - 1));
      drawn = { id: picked.name, instanceId }; // unique machine ID, NOT display name

      // Track spent ID per pool.
      if (pool === "policy") {
        nextSpentPolicyIds = [...nextSpentPolicyIds, picked.name];
      } else {
        nextSpentEventIds = [...nextSpentEventIds, picked.name];
      }

      // WEB.4.R2.CF: hand-full → set handFullPendingCard instead of silently dropping.
      if (currentHand.length >= MAX_HAND_SIZE) {
        return {
          handFullPendingCard: drawn,
          spentPolicyIds: nextSpentPolicyIds,
          spentEventIds: nextSpentEventIds,
        };
      }

      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = [...currentHand, drawn];
      // R5 #5/#14: auto-register auras on hand entry
      const drawnDef = getCardById(drawn.id);
      const nextAuraDraw =
        drawnDef !== undefined && _isAuraCard(drawnDef)
          ? _autoRegisterCardAuras(s.auraState, drawnDef, seat, drawn.instanceId)
          : s.auraState;
      // Bug #14: Whenever BuildCostMod cards must enqueue PEQ on hand entry.
      const nextPeqDraw =
        drawnDef !== undefined
          ? _enqueueWheneverBuildCostMod(s.peqState, drawnDef, seat, drawn.instanceId)
          : s.peqState;
      return {
        hands: nextHands,
        spentPolicyIds: nextSpentPolicyIds,
        spentEventIds: nextSpentEventIds,
        auraState: nextAuraDraw,
        peqState: nextPeqDraw,
      };
    });
    return drawn;
  },

  handleNpcCardDraw: (seat, pool) => {
    const s = useGameStore.getState();
    if (s.gameStatus === "ended") return;

    const cardType = pool === "event" ? "Event" : "Policy";
    const allPoolCards = _cardsByType(cardType);
    if (allPoolCards.length === 0) return;

    // R13 Codex-A MED fix: NPC policy draw path must also honor spentPolicyIds
    // so the same card cannot be drawn twice (matches drawCard semantics).
    // R18-FB-1: same dual-pool depletion tracking for event pool.
    let poolCards = allPoolCards;
    let nextSpentPolicyIds = s.spentPolicyIds;
    let nextSpentEventIds = s.spentEventIds;
    if (pool === "policy") {
      const spentSet = new Set(s.spentPolicyIds);
      const remaining = allPoolCards.filter((c) => !spentSet.has(c.name));
      if (remaining.length === 0) {
        poolCards = allPoolCards;
        nextSpentPolicyIds = [];
      } else {
        poolCards = remaining;
      }
    } else {
      // event pool depletion (R18-FB-1)
      const spentSet = new Set(s.spentEventIds);
      const remaining = allPoolCards.filter((c) => !spentSet.has(c.name));
      if (remaining.length === 0) {
        poolCards = allPoolCards;
        nextSpentEventIds = [];
      } else {
        poolCards = remaining;
      }
    }

    const idx = _rng.nextInt(0, poolCards.length - 1);
    const picked = poolCards[idx];
    if (picked === undefined) return;

    const instanceId = String(_rng.nextInt(0, 2 ** 31 - 1));
    const drawn: CardInHand = { id: picked.name, instanceId };

    if (pool === "policy") {
      nextSpentPolicyIds = [...nextSpentPolicyIds, picked.name];
      set({ spentPolicyIds: nextSpentPolicyIds });
    } else {
      nextSpentEventIds = [...nextSpentEventIds, picked.name];
      set({ spentEventIds: nextSpentEventIds });
    }

    const cardDef = getCardById(drawn.id);
    if (cardDef === undefined) return;

    const isImmediate = cardDef.isImmediate === true && cardDef.cardType === "Event";
    const hasBuyCost = (cardDef.buyCost ?? 0) > 0;

    const currentHand = useGameStore.getState().hands[seat];
    const handFull = currentHand.length >= MAX_HAND_SIZE;

    if (isImmediate) {
      // Bug #12 — OR-choice intercept: isImmediate cards with branchId-annotated effects
      // (Wildfire_Near_Facilities, Labor_Union_Strikes, Nuclear_Safety_Regulation, Flash_Flood)
      // show a 2-button choice modal instead of auto-running the consequence branch.
      const branchIdsInCard = [
        ...new Set(
          cardDef.effects.map((e) => e.branchId).filter((b): b is string => b !== undefined),
        ),
      ];
      const hasBranches = branchIdsInCard.length >= 2 && branchIdsInCard.includes("pay");
      if (hasBranches) {
        const riskBranchId =
          branchIdsInCard.find((b) => b !== "pay") ?? branchIdsInCard[0] ?? "risk";
        const payEff = cardDef.effects.find((e) => e.branchId === "pay" && e.kind === "TokenDelta");
        const payAmt =
          payEff !== undefined && typeof payEff.valueInt === "number"
            ? Math.abs(payEff.valueInt)
            : 0;
        const payLabel = payAmt > 0 ? `Pay ${payAmt} tokens` : "Pay tokens";
        const riskLabel =
          riskBranchId === "decom-all"
            ? "Decommission all coal"
            : riskBranchId === "destroy"
              ? "Destroy nearest facility"
              : "Risk (50% decommission)";
        // Mark NPC completed so leave-tile isn't blocked while choice modal is open.
        const stateOR = useGameStore.getState();
        const nextNpcOR = _markCardDrawNpcCompleted(
          stateOR.npcInteractionState,
          seat,
          stateOR.playerTiles,
        );
        // Add card to hand now — resolveOrChoice removes it after resolution.
        const nextHandsOR = [...stateOR.hands] as [
          CardInHand[],
          CardInHand[],
          CardInHand[],
          CardInHand[],
        ];
        nextHandsOR[seat] = [...currentHand, drawn];
        // R21-GE-8: open CDM in review mode; OrChoiceModal gates on !cardReviewBeatActive.
        // Player clicks "MAKE DECISION" → clearCardReviewBeat() → CDM closes → OrChoiceModal opens.
        set({
          hands: nextHandsOR,
          npcInteractionState: nextNpcOR,
          detailModalCard: drawn,
          detailModalCardFromHand: false,
          modalRevealState: "revealed" as const,
          cardReviewBeatActive: true,
          pendingOrChoice: {
            card: drawn,
            payLabel,
            riskLabel,
            payBranchId: "pay",
            riskBranchId,
            cardName: cardDef.cardName,
          },
        });
        // R21-GE-8: no setTimeout — player clicks "MAKE DECISION" button in CDM
        // footer (review-beat mode) when ready. clearCardReviewBeat() fires on click.
        return;
      }

      // Case A-instant: isImmediate + discardMode="Instantly" + no OpponentChosen target
      // → skip CDM entirely. Auto-run playCard pipeline, show a 2-second result toast.
      // WHY: user was required to click "Acknowledge" for cards that have no player choice.
      //      1973_Oil_Crisis, Pipeline_Explosion, Solar_Storm etc. all have single-target
      //      Self effects — the outcome is deterministic. The Acknowledge beat added no
      //      informational value and violated "instantly resolve" card semantics.
      // CARDS AFFECTED: all isImmediate=true + discardMode="Instantly" + no branchId
      //   + no OpponentChosen target (17 cards as of R21: Oil_Crisis_1973, etc.)
      // Cards with discardMode="AfterUse" keep the old CDM flow (user retains card).
      const isInstantlyDiscard = cardDef.discardMode === "Instantly";
      const hasOpponentChosenEff = cardDef.effects.some((e) => e.target === "OpponentChosen");
      if (isInstantlyDiscard && !hasOpponentChosenEff) {
        // Auto-resolve: add card to hand transiently, call playCard, card is consumed by pipeline.
        const stateAI = useGameStore.getState();
        const nextHandsAI = [...stateAI.hands] as [
          CardInHand[],
          CardInHand[],
          CardInHand[],
          CardInHand[],
        ];
        nextHandsAI[seat] = [...currentHand, drawn];
        const nextNpcAI = _markCardDrawNpcCompleted(
          stateAI.npcInteractionState,
          seat,
          stateAI.playerTiles,
        );
        // Stage: put card in hand so playCard can find it.
        set({ hands: nextHandsAI, npcInteractionState: nextNpcAI });
        // Call playCard — for Self/NearestPlayer targets, this resolves inline and consumes the card.
        useGameStore.getState().playCard(drawn);
        // After playCard: card is gone from hand, pendingEffectAck is set with deltas.
        // Auto-dismiss the ack modal after 2 s so user doesn't need to click anything.
        const _autoAckTimer = window.setTimeout(() => {
          useGameStore.getState().dismissEffectAck();
        }, 2000);
        // Store the timer handle for cleanup on resetGame (defensive).
        _instantAckTimers.push(_autoAckTimer);
        return;
      }

      // Case A: Event isImmediate (no OR-choice, not instantly-auto-resolved) — add to hand, open CDM
      const stateA = useGameStore.getState();
      const nextHandsA = [...stateA.hands] as [
        CardInHand[],
        CardInHand[],
        CardInHand[],
        CardInHand[],
      ];
      nextHandsA[seat] = [...currentHand, drawn];
      // R5 #5/#14: auto-register auras on hand entry
      const nextAuraA = _isAuraCard(cardDef)
        ? _autoRegisterCardAuras(stateA.auraState, cardDef, seat, instanceId)
        : stateA.auraState;
      // Bug #14: Whenever BuildCostMod cards must enqueue PEQ on hand entry.
      const nextPeqA = _enqueueWheneverBuildCostMod(stateA.peqState, cardDef, seat, instanceId);
      // R9 #11 — mark NPC completed for Case A (immediate card auto-entered hand).
      const stateA2 = useGameStore.getState();
      const nextNpcA = _markCardDrawNpcCompleted(
        stateA2.npcInteractionState,
        seat,
        stateA2.playerTiles,
      );
      // Bug #8: explicitly clear detailModalCardFromHand so CDM shows "acknowledge"
      // mode (not "hand-use-only" / "Use" button from stale flag).
      set({
        hands: nextHandsA,
        detailModalCard: drawn,
        modalRevealState: "cover",
        detailModalCardFromHand: false,
        auraState: nextAuraA,
        peqState: nextPeqA,
        npcInteractionState: nextNpcA,
      });
    } else if (hasBuyCost) {
      // Case B: paid card — do NOT add to hand yet. Open CDM in draw-buy mode.
      // NPC marked completed when Buy or Discard is confirmed (not on draw alone).
      // R22-GD-2: explicitly clear detailModalCardFromHand so an aura/passive card
      // (e.g. Carbon_Capture_System discardMode=Whenever) is NOT routed to the
      // hand-aura-passive footer (which shows only Discard+Close with no Buy button).
      set({
        pendingDrawCard: drawn,
        detailModalCard: drawn,
        modalRevealState: "cover",
        detailModalCardFromHand: false,
      });
    } else if (!handFull) {
      // Case C: free, hand not full — R5 #4: open free-draw acknowledge modal.
      // Card does NOT enter hand until player clicks "Take Card" (acceptFreeDrawCard).
      // NPC marked completed when Take or Discard is confirmed.
      set({ pendingFreeDrawCard: drawn, detailModalCard: drawn, modalRevealState: "cover" });
    } else {
      // Case D: free, hand full — set handFullPendingCard, HandFullModal opens
      // NPC marked completed when HandFullModal resolves.
      set({ handFullPendingCard: drawn });
    }
  },

  buyPendingCard: () => {
    set((s) => {
      if (_isGameInputBlocked(s)) return {};
      const card = s.pendingDrawCard;
      if (card === null) return {};
      const cardDef = getCardById(card.id);
      if (cardDef === undefined) return {};
      const seat = s.currentSeat as SeatIndex;
      const buyCost = cardDef.buyCost ?? 0;
      const costCurrency: "Token" | "EnergyCube" =
        cardDef.costCurrency === "EnergyCube" ? "EnergyCube" : "Token";

      // R18-FA-3 parity: NextPaidDrawFree waives this buy (per-seat only, NO bySeat[-1]).
      // Mirror the auction's consume pattern — consumed on use, exactly one entry.
      const peqLog = createActionLog();
      const freeDrawResult = tryConsumePending(
        s.peqState,
        peqLog,
        seat,
        "NextPaidDrawFree",
        null,
        null,
      );
      const effectiveCost = freeDrawResult.ok ? 0 : buyCost;
      const peqAfterFree = freeDrawResult.ok ? freeDrawResult.newState : s.peqState;

      // Insufficient-balance guard — skip when cost is waived by NextPaidDrawFree.
      if (!freeDrawResult.ok) {
        const balance =
          costCurrency === "EnergyCube" ? (s.energy[seat] ?? 0) : (s.tokens[seat] ?? 0);
        if (balance < buyCost) {
          // Insufficient — leave modal open (UI disables Buy button, but guard here too)
          return {};
        }
      }

      // Debit effective cost (0 when waived)
      const nextTokens = [...s.tokens] as [number, number, number, number];
      const nextEnergy = [...s.energy] as [number, number, number, number];
      if (costCurrency === "EnergyCube") {
        nextEnergy[seat] = Math.max(0, nextEnergy[seat] - effectiveCost);
      } else {
        nextTokens[seat] = Math.max(0, nextTokens[seat] - effectiveCost);
      }

      const currentHand = s.hands[seat];
      if (currentHand.length >= MAX_HAND_SIZE) {
        // Hand full after buy — show HandFullModal, keep cost debited
        return {
          tokens: nextTokens,
          energy: nextEnergy,
          peqState: peqAfterFree,
          pendingDrawCard: null,
          detailModalCard: null,
          modalRevealState: "cover" as const,
          handFullPendingCard: card,
        };
      }

      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = [...currentHand, card];
      // R5 #5/#14: auto-register auras on hand entry (buyPendingCard was missing this)
      const nextAuraBuy = _isAuraCard(cardDef)
        ? _autoRegisterCardAuras(s.auraState, cardDef, seat, card.instanceId)
        : s.auraState;
      // Bug #14: Whenever BuildCostMod cards must enqueue PEQ on hand entry.
      // Chain off peqAfterFree so the NextPaidDrawFree consumption is preserved.
      const nextPeqBuy = _enqueueWheneverBuildCostMod(peqAfterFree, cardDef, seat, card.instanceId);
      // R9 #11 — mark NPC completed on successful buy
      const nextNpcBuy = _markCardDrawNpcCompleted(s.npcInteractionState, seat, s.playerTiles);
      return {
        tokens: nextTokens,
        energy: nextEnergy,
        hands: nextHands,
        auraState: nextAuraBuy,
        peqState: nextPeqBuy,
        pendingDrawCard: null,
        detailModalCard: null,
        modalRevealState: "cover" as const,
        npcInteractionState: nextNpcBuy,
      };
    });
  },

  discardPendingCard: () =>
    set((s) => {
      const seat = s.currentSeat as SeatIndex;
      // R9 #11 — mark NPC completed on discard (player resolved their draw)
      const nextNpcDiscard = _markCardDrawNpcCompleted(s.npcInteractionState, seat, s.playerTiles);
      // R19-GA-3: skipped buy-required (pendingDrawCard) cards must be added to
      // spentPolicyIds so they cannot be redrawn. All pendingDrawCard cards come
      // from the policy pool (_cardsByType returns Policy+Tech+Mitigate together).
      const nextSpentPolicyIds =
        s.pendingDrawCard !== null ? [...s.spentPolicyIds, s.pendingDrawCard.id] : s.spentPolicyIds;
      return {
        pendingDrawCard: null,
        pendingFreeDrawCard: null,
        detailModalCard: null,
        modalRevealState: "cover" as const,
        npcInteractionState: nextNpcDiscard,
        spentPolicyIds: nextSpentPolicyIds,
      };
    }),

  acceptFreeDrawCard: () =>
    set((s) => {
      if (_isGameInputBlocked(s)) return {};
      const card = s.pendingFreeDrawCard;
      if (card === null) return {};
      const seat = s.currentSeat as SeatIndex;
      const cardDef = getCardById(card.id);
      if (cardDef === undefined) return {};
      const currentHand = s.hands[seat];
      if (currentHand.length >= MAX_HAND_SIZE) return {};
      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = [...currentHand, card];
      // R5 #5/#14: auto-register auras on hand entry
      const nextAura = _isAuraCard(cardDef)
        ? _autoRegisterCardAuras(s.auraState, cardDef, seat, card.instanceId)
        : s.auraState;
      // Bug #14: Whenever BuildCostMod cards must enqueue PEQ on hand entry.
      const nextPeq = _enqueueWheneverBuildCostMod(s.peqState, cardDef, seat, card.instanceId);
      // R9 #11 — mark NPC completed on accept
      const nextNpcAccept = _markCardDrawNpcCompleted(s.npcInteractionState, seat, s.playerTiles);
      return {
        hands: nextHands,
        pendingFreeDrawCard: null,
        detailModalCard: null,
        modalRevealState: "cover" as const,
        auraState: nextAura,
        peqState: nextPeq,
        npcInteractionState: nextNpcAccept,
      };
    }),

  discardHandCard: (card: CardInHand) =>
    set((s) => {
      const seat = s.currentSeat as SeatIndex;
      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = s.hands[seat].filter((c) => c.instanceId !== card.instanceId);
      // R5 #5/#14: auto-remove auras on discard
      const nextAura = _autoRemoveCardAuras(s.auraState, card.instanceId);
      // Bug #14: Whenever BuildCostMod cards must dequeue PEQ on discard.
      const cardDef = getCardById(card.id);
      const nextPeqDiscard =
        cardDef !== undefined
          ? _dequeueWheneverBuildCostMod(s.peqState, cardDef, seat, card.instanceId)
          : s.peqState;
      return {
        hands: nextHands,
        auraState: nextAura,
        peqState: nextPeqDiscard,
        detailModalCard: null,
        modalRevealState: "cover" as const,
        detailModalCardFromHand: false,
      };
    }),

  resolveHandFull: (action) => {
    set((s) => {
      const pending = s.handFullPendingCard;
      if (pending === null) return {};
      const seat = s.currentSeat as SeatIndex;
      const currentHand = s.hands[seat];
      // R9 #11 — mark NPC completed on HandFull resolution (either discard or replace)
      const nextNpcHF = _markCardDrawNpcCompleted(s.npcInteractionState, seat, s.playerTiles);

      if (action === "discard") {
        // Discard new card — hand unchanged
        return { handFullPendingCard: null, npcInteractionState: nextNpcHF };
      }

      const replaceIdx = action as number;
      if (replaceIdx < 0 || replaceIdx >= currentHand.length) return {};

      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      const newHand = [...currentHand];
      const evictedCard = currentHand[replaceIdx];
      newHand[replaceIdx] = pending; // replace slot
      nextHands[seat] = newHand;
      // Bug #14: evicted card loses its PEQ entry; incoming card gains one.
      const evictedDef = evictedCard !== undefined ? getCardById(evictedCard.id) : undefined;
      const incomingDef = getCardById(pending.id);
      let nextPeqHF = s.peqState;
      if (evictedDef !== undefined && evictedCard !== undefined) {
        nextPeqHF = _dequeueWheneverBuildCostMod(
          nextPeqHF,
          evictedDef,
          seat,
          evictedCard.instanceId,
        );
      }
      if (incomingDef !== undefined) {
        nextPeqHF = _enqueueWheneverBuildCostMod(nextPeqHF, incomingDef, seat, pending.instanceId);
      }
      // Also register auras for incoming card (resolveHandFull was missing aura registration).
      const nextAuraHF =
        incomingDef !== undefined && _isAuraCard(incomingDef)
          ? _autoRegisterCardAuras(s.auraState, incomingDef, seat, pending.instanceId)
          : s.auraState;
      // Also remove auras for evicted card.
      const nextAuraHF2 =
        evictedCard !== undefined
          ? _autoRemoveCardAuras(nextAuraHF, evictedCard.instanceId)
          : nextAuraHF;
      return {
        hands: nextHands,
        handFullPendingCard: null,
        npcInteractionState: nextNpcHF,
        peqState: nextPeqHF,
        auraState: nextAuraHF2,
      };
    });
  },

  dismissHandFull: () => set({ handFullPendingCard: null }),

  dismissCardDrawnToast: () => set({ cardDrawnToast: null }),

  // ---------------------------------------------------------------------------
  // Bug #12 — OR-choice resolution (Wildfire, Labor_Union_Strikes, etc.)
  // ---------------------------------------------------------------------------

  resolveOrChoice: (branchId) =>
    set((s) => {
      const choice = s.pendingOrChoice;
      if (choice === null) return {};
      const card = choice.card;
      const cardDef = getCardById(card.id);
      if (cardDef === undefined) {
        return { pendingOrChoice: null };
      }
      const seat = s.currentSeat as SeatIndex;

      // Mirror playCard pipeline — seeded with chosen branchId only.
      const pollutionTotal = s.pollution.reduce((a, b) => a + b, 0);
      let tokenState = createTokenState();
      let energyState = createEnergyState();
      for (let i = 0; i < 4; i++) {
        tokenState = setTokenBalance(tokenState, i, s.tokens[i] ?? 0);
        energyState = setEnergyCubes(energyState, i, s.energy[i] ?? 0);
      }
      const pollutionState = createPollutionState(pollutionTotal);

      const cisInit = createCardInstanceState();
      const { newState: cisAfter, instanceId: cardInstId } = createForSeat(
        cisInit,
        createActionLog(),
        cardDef.name,
        seat,
        "Hand",
      );

      // Resolve singleTarget for Destroy/Decom/RngBranch in chosen branch.
      let singleTargetVictimOR: SeatIndex = seat;
      let singleTargetTileIndicesOR: number[] = [];
      const singleTargetEffOR = cardDef.effects.find(
        (e) =>
          e.branchId === branchId &&
          (e.kind === "Destroy" || e.kind === "Decommission" || e.kind === "RngBranch") &&
          e.affectsAll !== true &&
          (e.target === "Self" || e.target === "NearestPlayer"),
      );
      if (singleTargetEffOR !== undefined) {
        if (singleTargetEffOR.target === "NearestPlayer") {
          // R18-FB-6: use combined helper so we skip nearest seats with no facilities.
          const nearestOR = _resolveNearestPlayerSeatWithFacility(
            s.playerTiles,
            seat,
            s.lostSeats,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffOR.categoryFilter,
            s.numPlayers,
          );
          if (nearestOR !== null) {
            singleTargetVictimOR = nearestOR.seat;
            singleTargetTileIndicesOR = [nearestOR.tileIndex];
          }
        } else {
          // target === "Self"
          const ownerOR: SeatIndex = seat;
          singleTargetVictimOR = ownerOR;
          const tileOR = _findFirstFacilityTileFor(
            ownerOR,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffOR.categoryFilter,
          );
          if (tileOR >= 0) singleTargetTileIndicesOR = [tileOR];
        }
      }

      const ctx = makeEffectContext(seat, singleTargetVictimOR, {
        cardTags: cardDef.tags ?? [],
        rngSeed: s.rngSeed,
        cardInstanceId: cardInstId,
        chosenBranchId: branchId,
        ...(singleTargetTileIndicesOR.length > 0
          ? { targetTileIndices: singleTargetTileIndicesOR }
          : {}),
      });

      const services: PipelineServices = {
        token: tokenState,
        energy: energyState,
        pollution: pollutionState,
        aura: s.auraState,
        peq: s.peqState,
        cis: cisAfter,
        // DC-1 Bug #11: real countProvider (was all-zero stub).
        countProvider: _buildCountProvider(_seedFacilityOwnership(s)),
        facilityOwnership: _seedFacilityOwnership(s),
        rng: _rng,
        immunity: s.immunityState,
      };

      const facilityOwnershipBefore = services.facilityOwnership;
      let pipelineResult: ReturnType<typeof runCard>;
      try {
        pipelineResult = runCard(cardDef, ctx, services, createActionLog());
      } catch (err) {
        console.error("[resolveOrChoice] runCard threw:", err);
        return { pendingOrChoice: null };
      }

      const removedTiles = _extractRemovedTiles(
        facilityOwnershipBefore,
        pipelineResult.services.facilityOwnership,
      );
      const newTokens = [...s.tokens] as [number, number, number, number];
      const newEnergy = [...s.energy] as [number, number, number, number];
      const newPollution = [...s.pollution] as [number, number, number, number];

      for (let i = 0; i < 4; i++) {
        const nb = pipelineResult.services.token.balances[i];
        if (typeof nb === "number") newTokens[i] = Math.max(0, nb);
        const ne = pipelineResult.services.energy.energy[i];
        if (typeof ne === "number") newEnergy[i] = Math.max(0, ne);
      }

      const newGlobal = Math.max(0, pipelineResult.services.pollution.globalPollution);
      const pollutionDelta = newGlobal - pollutionTotal;
      if (pollutionDelta !== 0) {
        newPollution[seat] = Math.max(0, (newPollution[seat] ?? 0) + pollutionDelta);
      }

      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== card.instanceId);

      const newBuiltFacilities = { ...s.builtFacilities };
      const newOwnedBy = { ...s.ownedBy };
      const newMeta = { ...s.builtFacilityMeta };
      let handsAfterTriggers = nextHands;

      if (removedTiles.length > 0) {
        const hasDestroy = cardDef.effects.some((e) => e.kind === "Destroy");
        const trigEvent: "AfterFacilityDestroyedMine" | "AfterFacilityDecommissionedMine" =
          hasDestroy ? "AfterFacilityDestroyedMine" : "AfterFacilityDecommissionedMine";
        for (const tileIdx of removedTiles) {
          const ownerSeat = s.ownedBy[tileIdx];
          delete newBuiltFacilities[tileIdx];
          delete newOwnedBy[tileIdx];
          delete newMeta[tileIdx];
          if (ownerSeat !== undefined) {
            const trigResult = _dispatchFacilityTrigger(
              handsAfterTriggers,
              ownerSeat as SeatIndex,
              trigEvent,
            );
            handsAfterTriggers = trigResult.newHands;
            if (trigResult.tokenGain > 0) {
              newTokens[ownerSeat] = (newTokens[ownerSeat] ?? 0) + trigResult.tokenGain;
            }
          }
        }
      }

      const endState = detectGameEnd(
        {
          tokens: newTokens,
          energy: newEnergy,
          pollution: newPollution,
          round: s.round,
          lostSeats: s.lostSeats,
          builtFacilities: newBuiltFacilities as Record<number, string | undefined>,
          ownedBy: newOwnedBy as Record<number, number | undefined>,
        },
        s.lostSeats,
        s.numPlayers,
      );

      const ackPayload =
        pipelineResult.services.token.balances[seat] !== undefined
          ? {
              cardName: cardDef.cardName,
              deltas: {
                tokens:
                  Math.max(0, pipelineResult.services.token.balances[seat] ?? 0) -
                  (s.tokens[seat] ?? 0),
                energy:
                  Math.max(0, pipelineResult.services.energy.energy[seat] ?? 0) -
                  (s.energy[seat] ?? 0),
                pollution: pollutionDelta,
                reputation: 0,
              },
            }
          : null;

      return {
        tokens: newTokens,
        energy: newEnergy,
        pollution: newPollution,
        hands: handsAfterTriggers,
        builtFacilities: newBuiltFacilities,
        ownedBy: newOwnedBy,
        builtFacilityMeta: newMeta,
        auraState: pipelineResult.services.aura,
        immunityState: pipelineResult.services.immunity,
        peqState: pipelineResult.services.peq,
        pendingOrChoice: null,
        detailModalCard: null,
        modalRevealState: "cover" as const,
        detailModalCardFromHand: false,
        pendingEffectAck: ackPayload,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
      };
    }),

  openCardDetail: (card, fromHand = false) =>
    set({ detailModalCard: card, modalRevealState: "cover", detailModalCardFromHand: fromHand }),

  closeCardDetail: () =>
    set({ detailModalCard: null, modalRevealState: "cover", detailModalCardFromHand: false }),

  flipReveal: () =>
    set((s) => ({
      modalRevealState: s.modalRevealState === "cover" ? "revealed" : "cover",
    })),

  /**
   * Play a card from hand.
   *
   * Fully wired for Event/Crisis cards whose effects are all in the
   * SUPPORTED_EFFECT_KINDS set (TokenDelta/EnergyDelta/PollutionDelta/StealEnergy)
   * AND have no OpponentChosen targets (TargetPicker deferred to WEB.4.UI.4+).
   *
   * For DEFERRED cards (Policy AuraApply, OpponentChosen target, etc.):
   *   - Card is NOT consumed (stays in hand)
   *   - buyCost is NOT charged
   *   - Modal closes with a console.warn
   *   - User can Cancel; card playable again in a future sub-phase when wiring lands
   *
   * Invalid card id → logs error, closes modal, does NOT consume.
   */
  playCard: (card) =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      // R9 codex HIGH: block input during cutscene/dialogue/transition.
      if (_isGameInputBlocked(s)) return {};
      const cardDef = getCardById(card.id);
      if (cardDef === undefined) {
        console.error("[playCard] Card id not found:", card.id);
        return { detailModalCard: null, modalRevealState: "cover" as const };
      }

      const seat = s.currentSeat;

      // WEB.4.UI.6 codex R2 HIGH 1: OpponentChosen routing MUST run BEFORE the
      // unsupported-kind gate. Otherwise Spy_In_Action (StealCard) /
      // Mercenaries (Destroy) / Smog_Emergency (FacilityTransfer) /
      // Climate_Protest (Decommission) get deferred-without-picker — never opens.
      // TargetPicker handles whether-to-consume in confirmTarget (chained-effect
      // cards like Smog/Mercenaries warn + return card to hand pending TilePicker).
      const needsOpponentPicker = cardDef.effects.some((e) => e.target === "OpponentChosen");
      if (needsOpponentPicker) {
        return {
          detailModalCard: null,
          modalRevealState: "cover" as const,
          targetPickerCard: card,
          targetPickerVictimSeat: null,
        };
      }

      // Playability gate — DO NOT consume card if anything is deferred.
      // (Codex R1 HIGH: AuraApply was consuming card silently.)
      // WEB.4.UI.8a: AuraApply now supported (registers into persistent auraState).
      // WEB.4.UI.18: multi-tile global sweeps (RngBranch/Decommission/Destroy affectsAll)
      // now supported — _isEffectSupportedInPlayCard handles per-effect logic.
      const unsupportedKinds = cardDef.effects
        .filter((e) => !_isEffectSupportedInPlayCard(e))
        .map((e) => e.kind);
      if (unsupportedKinds.length > 0) {
        console.warn(
          `[playCard] Deferred — unsupported effect kinds: ${unsupportedKinds.join(", ")}. Card NOT consumed. (WEB.4.UI.4+)`,
        );
        return { detailModalCard: null, modalRevealState: "cover" as const };
      }

      // WEB.4.UI.X.4 (Net_Zero fix): same-scope-multi-AuraApply defer REMOVED.
      // The earlier UI.8a R2 HIGH 2 gate deferred Net_Zero_Movement (two Toll
      // effects sharing scope) because rules-engine AuraManager.register
      // deduped by (instanceId, scope). The dedupe key is now also keyed by
      // categoryFilter + facilityFilter so two scoped auras with different
      // filters BOTH register. Net_Zero_Movement and similar cards now play
      // end-to-end.

      // WEB.4.UI.8a codex R2 HIGH 1: debit buyCost from drawer BEFORE pipeline.
      // UI.15 R1 MED (Codex): also honor cardDef.costCurrency — defensive
      // Mitigate cards (UN_Green_Pact_Support, Microgrids, Disaster_Relief_Fund)
      // use "EnergyCube" not Token. Debit the matching balance; cancel without
      // consuming if drawer cannot afford the right currency.
      const buyCost = cardDef.buyCost ?? 0;
      const costCurrency: "Token" | "EnergyCube" =
        cardDef.costCurrency === "EnergyCube" ? "EnergyCube" : "Token";
      if (buyCost > 0) {
        const drawerBal =
          costCurrency === "EnergyCube" ? (s.energy[seat] ?? 0) : (s.tokens[seat] ?? 0);
        if (drawerBal < buyCost) {
          console.warn(
            `[playCard] ${cardDef.name} buyCost=${buyCost} ${costCurrency} exceeds drawer balance=${drawerBal}. Card NOT consumed.`,
          );
          return { detailModalCard: null, modalRevealState: "cover" as const };
        }
      }

      // Build PipelineServices from current store state.
      // Apply buyCost to drawer's seat balance BEFORE seeding TokenState so the
      // pipeline operates on post-payment tokens (or post-payment energy).
      const pollutionTotal = s.pollution.reduce((a, b) => a + b, 0);

      const tokenDebit = costCurrency === "Token" ? buyCost : 0;
      const energyDebit = costCurrency === "EnergyCube" ? buyCost : 0;

      let tokenState = createTokenState();
      let energyState = createEnergyState();
      for (let i = 0; i < 4; i++) {
        const seedTok = i === seat ? (s.tokens[i] ?? 0) - tokenDebit : (s.tokens[i] ?? 0);
        const seedEng = i === seat ? (s.energy[i] ?? 0) - energyDebit : (s.energy[i] ?? 0);
        tokenState = setTokenBalance(tokenState, i, seedTok);
        energyState = setEnergyCubes(energyState, i, seedEng);
      }
      const pollutionState = createPollutionState(pollutionTotal);

      // WEB.4.UI.8a: mint a CardInstance for the played card so AuraApply
      // resolver has a valid `ctx.cardInstanceId > 0` + CIS lookup yields the
      // correct sourceCardName (cardDef.name) rather than cardTags[0] fallback.
      const cisInit = createCardInstanceState();
      const { newState: cisAfter, instanceId: cardInstId } = createForSeat(
        cisInit,
        createActionLog(),
        cardDef.name,
        seat,
        "Hand",
      );

      // WEB.4.UI.18 — OR-choice isImmediate cards (Labor_Union_Strikes,
      // Nuclear_Safety_Regulation) have branchId on their effects. The pipeline
      // skips all effects when ctx.chosenBranchId is undefined. For playCard
      // (no OR-choice UI), auto-select the first non-"pay" branchId so the
      // "consequence" branch runs (decommission / rng-risk).
      const branchIds = [
        ...new Set(
          cardDef.effects.map((e) => e.branchId).filter((b): b is string => b !== undefined),
        ),
      ];
      const autoChosenBranch = branchIds.find((b) => b !== "pay") ?? branchIds[0];

      // WEB.4.PT.17 / Bug #12 — resolve single-target ctx for
      // Destroy/Decom/RngBranch/StealFacility non-affectsAll cards.
      // Compute ownerSeat + first facility tile.
      let singleTargetVictim: SeatIndex = seat; // default Self
      let singleTargetTileIndices: number[] = [];
      const singleTargetEffect = cardDef.effects.find(
        (e) =>
          (e.kind === "Destroy" ||
            e.kind === "Decommission" ||
            e.kind === "RngBranch" ||
            e.kind === "StealFacility") &&
          e.affectsAll !== true &&
          (e.target === "Self" || e.target === "NearestPlayer"),
      );
      if (singleTargetEffect !== undefined) {
        if (singleTargetEffect.target === "NearestPlayer") {
          // R18-FB-6: use combined helper so we skip nearest seats with no facilities.
          const nearest = _resolveNearestPlayerSeatWithFacility(
            s.playerTiles,
            seat,
            s.lostSeats,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffect.categoryFilter,
            s.numPlayers,
          );
          if (nearest === null) {
            console.info(
              `[playCard:${cardDef.name}] NearestPlayer — no active opponent has a matching facility. Pipeline will Skip facility effect.`,
            );
          } else {
            singleTargetVictim = nearest.seat;
            singleTargetTileIndices = [nearest.tileIndex];
          }
        } else {
          // target === "Self"
          const ownerSeat: SeatIndex = seat;
          singleTargetVictim = ownerSeat;
          const targetTile = _findFirstFacilityTileFor(
            ownerSeat,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffect.categoryFilter,
          );
          if (targetTile >= 0) singleTargetTileIndices = [targetTile];
        }
      }

      const ctx = makeEffectContext(seat, singleTargetVictim, {
        cardTags: cardDef.tags ?? [],
        rngSeed: s.rngSeed,
        cardInstanceId: cardInstId,
        ...(autoChosenBranch !== undefined ? { chosenBranchId: autoChosenBranch } : {}),
        ...(singleTargetTileIndices.length > 0
          ? { targetTileIndices: singleTargetTileIndices }
          : {}),
      });

      // Bug #6 fix — Whenever+BuildCostMod double-enqueue prevention.
      // When a Whenever card (e.g. Industrial_Revolution) enters hand,
      // _enqueueWheneverBuildCostMod adds PEQ entry #1 (instanceId = card.instanceId).
      // If the player then plays it via playCard (CDM "Use"), the pipeline's
      // resolveBuildCostMod adds PEQ entry #2 (instanceId = CIS-minted).
      // buildFacility consumes only ONE — the other leaks as a ghost NextBuildFree.
      // Fix: dequeue the hand-entry entry BEFORE seeding services.peq so the
      // pipeline entry is the sole remaining entry after the card is consumed.
      const peqForPipeline =
        cardDef.discardMode === "Whenever" && cardDef.effects.some((e) => e.kind === "BuildCostMod")
          ? _dequeueWheneverBuildCostMod(s.peqState, cardDef, seat, card.instanceId)
          : s.peqState;

      const services: PipelineServices = {
        token: tokenState,
        energy: energyState,
        pollution: pollutionState,
        // WEB.4.UI.8a: thread persistent aura state through — was previously
        // re-created here, so AuraApply registrations were dropped on the floor.
        aura: s.auraState,
        // WEB.4.UI.16 — thread persistent PEQ state through pipeline.
        // Bug #6: use peqForPipeline (hand-entry dequeued) not raw s.peqState.
        peq: peqForPipeline,
        cis: cisAfter,
        // DC-1 Bug #11 — real count provider derived from facilityOwnership.
        // The previous stub (all-zero) caused every predicate-gated card to
        // evaluate with wrong counts (1973_Oil_Crisis, Green_Bills_Enact, etc.).
        // Must be built AFTER facilityOwnership so we use the seeded state.
        countProvider: _buildCountProvider(_seedFacilityOwnership(s)),
        // WEB.4.UI.18 — seed facilityOwnership from store state so multi-tile resolvers
        // (RngBranch/Decommission affectsAll=true) see the actual board facilities.
        facilityOwnership: _seedFacilityOwnership(s),
        rng: _rng,
        // WEB.4.UI.15 (gap #3.A) — thread persistent immunity through pipeline.
        immunity: s.immunityState,
        // DC-1 Bug #7 — supply deck so DrawCard resolver can draw from Policy pool.
        // Without this, resolver returns Skipped("deck-not-supplied") silently.
        // R17b fix: only build deck (which shuffles via _rng) when card has DrawCard
        // effect — unconditional build consumed 80+ RNG calls and shifted RNG for all
        // subsequent RngBranch/rollDice rolls (broke Fukushima/Wildfire/Toll_Tax tests).
        ...(cardDef.effects.some((e) => e.kind === "DrawCard")
          ? { deck: _buildDeckForPlayCard(s.spentPolicyIds) }
          : {}),
      };

      // WEB.4.UI.18 — capture pre-pipeline ownership to compute removed tiles after.
      const facilityOwnershipBefore = services.facilityOwnership;

      let pipelineResult: ReturnType<typeof runCard>;
      try {
        pipelineResult = runCard(cardDef, ctx, services, createActionLog());
      } catch (err) {
        console.error("[playCard] runCard threw:", err);
        // Defensive: do NOT consume on pipeline error. Card stays in hand.
        return { detailModalCard: null, modalRevealState: "cover" as const };
      }

      // WEB.4.UI.18 — propagate facility removals (multi-tile sweeps) back to store.
      // Removed tiles → delete from builtFacilities, ownedBy, builtFacilityMeta;
      // dispatch DRF triggers per tile owner.
      const removedTiles = _extractRemovedTiles(
        facilityOwnershipBefore,
        pipelineResult.services.facilityOwnership,
      );

      // Extract new balances from pipeline result services
      const newTokens = [...s.tokens] as [number, number, number, number];
      const newEnergy = [...s.energy] as [number, number, number, number];
      const newPollution = [...s.pollution] as [number, number, number, number];

      // Apply token deltas
      for (let i = 0; i < 4; i++) {
        const newBal = pipelineResult.services.token.balances[i];
        if (typeof newBal === "number") {
          newTokens[i] = Math.max(0, newBal);
        }
        const newEng = pipelineResult.services.energy.energy[i];
        if (typeof newEng === "number") {
          newEnergy[i] = Math.max(0, newEng);
        }
      }

      // Apply pollution delta — rules-engine PollutionState is global (single number).
      // Codex R1 MED: previous "distribute evenly across 4 seats" lost
      // Unity/rules parity. Fix: attribute the WHOLE delta to currentSeat (the
      // player who played the card). Semantically: "you played it, you caused it".
      const prevGlobal = pollutionTotal;
      const newGlobal = Math.max(0, pipelineResult.services.pollution.globalPollution);
      const pollutionDelta = newGlobal - prevGlobal;
      if (pollutionDelta !== 0) {
        newPollution[seat] = Math.max(0, (newPollution[seat] ?? 0) + pollutionDelta);
      }

      // Remove card from hand
      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== card.instanceId);

      // WEB.4.UI.18 — propagate multi-tile facility removals back to store maps.
      // For each tile removed by the pipeline (RngBranch affectsAll / Decommission affectsAll):
      //   1. Delete from builtFacilities, ownedBy, builtFacilityMeta.
      //   2. Dispatch _dispatchFacilityTrigger per tile owner (DRF insurance).
      //   3. Token refunds already flowed through pipelineResult.services.token above.
      //
      // Determine trigger event type: Destroy effect with affectsAll → DestroyedMine;
      // everything else (RngBranch valueInt=1/3, Decommission) → DecommissionedMine.
      const newBuiltFacilities = { ...s.builtFacilities };
      const newOwnedBy = { ...s.ownedBy };
      const newMeta = { ...s.builtFacilityMeta };
      let handsAfterTriggers = nextHands;

      if (removedTiles.length > 0) {
        // WEB.4.PT.17: single-target Destroy also uses DestroyedMine trigger.
        const hasDestroy = cardDef.effects.some((e) => e.kind === "Destroy");
        const trigEvent: "AfterFacilityDestroyedMine" | "AfterFacilityDecommissionedMine" =
          hasDestroy ? "AfterFacilityDestroyedMine" : "AfterFacilityDecommissionedMine";

        for (const tileIdx of removedTiles) {
          const ownerSeat = s.ownedBy[tileIdx];
          delete newBuiltFacilities[tileIdx];
          delete newOwnedBy[tileIdx];
          delete newMeta[tileIdx];

          // Fire DRF triggers for the tile's owner (if any).
          if (ownerSeat !== undefined) {
            const trigResult = _dispatchFacilityTrigger(
              handsAfterTriggers,
              ownerSeat as SeatIndex,
              trigEvent,
            );
            handsAfterTriggers = trigResult.newHands;
            if (trigResult.tokenGain > 0) {
              newTokens[ownerSeat] = (newTokens[ownerSeat] ?? 0) + trigResult.tokenGain;
              console.info(
                `[playCard:MultiTile] DRF trigger seat ${ownerSeat} +${trigResult.tokenGain}T tile=${tileIdx} from ${trigResult.firedCardNames.join(", ")}.`,
              );
            }
          }
        }
      }

      // Bug #12 fix — StealFacility (Trade_War) transfers ownership; tile stays in
      // facilityOwnership but ownerSeat changes. Propagate that change back to store.
      const transferredTiles = _extractTransferredTiles(
        facilityOwnershipBefore,
        pipelineResult.services.facilityOwnership,
      );
      for (const { tileIndex, newOwner } of transferredTiles) {
        newOwnedBy[tileIndex] = newOwner as SeatIndex;
        console.info(`[playCard:StealFacility] Tile ${tileIndex} transferred to seat ${newOwner}.`);
      }

      // WEB.4.UI.10a codex R1 HIGH 2: detect game-end after pollution delta
      // applied — playCard PollutionDelta cards (Pipeline_Explosion etc.) can
      // push global past the 600 ecosystem-collapse threshold (CF-1).
      const endState = detectGameEnd(
        {
          tokens: newTokens,
          energy: newEnergy,
          pollution: newPollution,
          round: s.round,
          lostSeats: s.lostSeats,
          builtFacilities: newBuiltFacilities as Record<number, string | undefined>,
          ownedBy: newOwnedBy as Record<number, number | undefined>,
        },
        s.lostSeats,
        s.numPlayers,
      );

      // R3 Bug 6 — compute per-resource deltas for CardEffectAckModal.
      const ackTokenDelta = (newTokens[seat] ?? 0) - (s.tokens[seat] ?? 0);
      const ackEnergyDelta = (newEnergy[seat] ?? 0) - (s.energy[seat] ?? 0);
      const newGlobalPollForAck = Math.max(0, pipelineResult.services.pollution.globalPollution);
      const ackPollutionDelta = newGlobalPollForAck - pollutionTotal;
      // If the card has predicated effects and NO delta fired (zero across all
      // resources), flag this so the ack modal can explain "conditions not met"
      // instead of the generic "no resource change" (helps with Green_Bills_Enact
      // tie cases where neither majorityRenewable nor majorityFossil holds).
      const hasPredicatedEffects = cardDef.effects.some(
        (e) => e.predicate != null && e.predicate.length > 0,
      );
      const noDeltaFired =
        ackTokenDelta === 0 && ackEnergyDelta === 0 && ackPollutionDelta === 0;
      // R20-GD-1 UX — TurnBlock cards (Drought) have NO numeric delta, so the ack
      // modal used to read "No resource change" and players thought the card did
      // nothing. Surface the block duration + when building resumes as note lines.
      // (Detection hoisted here; state application happens below.)
      const turnBlockEffects = cardDef.effects.filter(
        (e) => e.kind === "TurnBlock" && e.target === "Self",
      );
      const ackNotes: string[] = turnBlockEffects.map((tbe) => {
        const turns = typeof tbe.valueInt === "number" ? tbe.valueInt : 3;
        const what = (tbe.categoryFilter ?? ["Hydro"]).join(" / ");
        return (
          `No ${what} builds for ${turns} of your turns (including this one). ` +
          `The block ticks down each time you end your turn — you can build ${what} again on your turn after it reaches 0. ` +
          `The Build menu shows the remaining turns.`
        );
      });
      const ackPayload = {
        cardName: cardDef.cardName,
        deltas: {
          tokens: ackTokenDelta,
          energy: ackEnergyDelta,
          pollution: ackPollutionDelta,
          reputation: 0,
        },
        conditionsNotMet: hasPredicatedEffects && noDeltaFired && ackNotes.length === 0,
        ...(ackNotes.length > 0 ? { notes: ackNotes } : {}),
      };

      // BA-1: fire card-played analytics event.
      _recordAnalyticsEvent({
        type: "card-played",
        t: Date.now(),
        seat,
        cardId: card.id,
        outcome: "played",
      });
      // BA-1: fire game-end analytics event when playCard triggers game-end (e.g. pollution card).
      if (endState.status === "ended") {
        _recordAnalyticsEvent({
          type: "game-end",
          t: Date.now(),
          endReason: endState.reason ?? "unknown",
          winnerSeat: endState.winnerSeat ?? null,
          round: s.round,
        });
      }

      // DC-1 Bug #7 — propagate DrawCard results back to store.
      //
      // The DrawCard resolver mints new CIS Hand entries in pipelineResult.services.cis.
      // Compare against the pre-pipeline CIS to find new instances for the drawer seat.
      // Each new instance → set as pendingFreeDrawCard so CDM opens in "Take Card / Discard"
      // mode. Only the FIRST drawn card is surfaced; multiple DrawCard effects on the
      // same card are edge-case unsupported (no such card exists today).
      const preHandInstIds = new Set(
        getCISByOwner(cisAfter, seat)
          .filter((inst) => inst.zone === CardZone.Hand)
          .map((inst) => inst.instanceId),
      );
      const postHandInsts = getCISByOwner(pipelineResult.services.cis, seat).filter(
        (inst) => inst.zone === CardZone.Hand && !preHandInstIds.has(inst.instanceId),
      );
      // Take the first newly-drawn card (if any).
      const drawnCISInst = postHandInsts[0];
      const drawnCardForHand: CardInHand | null =
        drawnCISInst !== undefined
          ? { id: drawnCISInst.cardAssetId, instanceId: String(drawnCISInst.instanceId) }
          : null;

      // R20-GD-1 — TurnBlock inline handler: sets hydroBlockTurns[seat] = valueInt
      // for each TurnBlock effect targeting Self. categoryFilter = ["Hydro"] for Drought.
      // Pipeline does NOT handle TurnBlock (rules-engine has no resolver for it).
      // (turnBlockEffects hoisted above for the ack-note build.)
      let nextHydroBlockTurns = s.hydroBlockTurns;
      if (turnBlockEffects.length > 0) {
        const newBlock = [...s.hydroBlockTurns] as [number, number, number, number];
        for (const tbe of turnBlockEffects) {
          // valueInt is the number of turns to block; default 3 for Drought.
          const turns = typeof tbe.valueInt === "number" ? tbe.valueInt : 3;
          newBlock[seat] = Math.max(newBlock[seat] ?? 0, turns);
          console.info(
            `[playCard:TurnBlock] Seat ${seat} Hydro-block set to ${turns} turn(s) (categoryFilter=${JSON.stringify(tbe.categoryFilter)}).`,
          );
        }
        nextHydroBlockTurns = newBlock;
      }

      // R20-GD-9 — Chernobyl Incident impact modal: detect nuclear-plant removals
      // and surface per-seat summary. Triggered for every Chernobyl_Incident play,
      // INCLUDING the zero-lost branch (modal already handles totalLost===0 with the
      // "all plants survived" message — without this, players get no feedback).
      // R20-fix (codex-review #6): use Math.ceil to match resolver's refund credit.
      let nextPendingChernobylImpact = s.pendingChernobylImpact;
      if (card.id === "Chernobyl_Incident") {
        const perSeatMap = new Map<SeatIndex, { lost: number; refunded: number }>();
        for (const tileIdx of removedTiles) {
          const ownerSeat = s.ownedBy[tileIdx] as SeatIndex | undefined;
          if (ownerSeat === undefined) continue;
          const meta = s.builtFacilityMeta[tileIdx];
          const refund = meta !== undefined ? Math.ceil((meta.builtCost ?? 0) * 0.5) : 0;
          const prev = perSeatMap.get(ownerSeat) ?? { lost: 0, refunded: 0 };
          perSeatMap.set(ownerSeat, { lost: prev.lost + 1, refunded: prev.refunded + refund });
        }
        const perSeat = Array.from(perSeatMap.entries()).map(([s2, v]) => ({
          seat: s2,
          lost: v.lost,
          refunded: v.refunded,
        }));
        nextPendingChernobylImpact = {
          perSeat,
          totalLost: removedTiles.length,
          totalRefunded: perSeat.reduce((acc, x) => acc + x.refunded, 0),
        };
      }

      // WEB.4.UI.8a: persist post-pipeline aura state. AuraApply effects
      // registered new AuraInstance entries; capture them back into store.
      return {
        tokens: newTokens,
        energy: newEnergy,
        pollution: newPollution,
        hands: handsAfterTriggers,
        builtFacilities: newBuiltFacilities,
        ownedBy: newOwnedBy,
        builtFacilityMeta: newMeta,
        auraState: pipelineResult.services.aura,
        // WEB.4.UI.15 (gap #3.A) — persist Immunity registered by this play.
        immunityState: pipelineResult.services.immunity,
        // WEB.4.UI.16 — persist PEQ entries (HalveNextTokenLoss etc.).
        peqState: pipelineResult.services.peq,
        // R20-GD-1 — persist updated Hydro-block counter.
        hydroBlockTurns: nextHydroBlockTurns,
        // R20-GD-9 — persist Chernobyl impact modal state.
        pendingChernobylImpact: nextPendingChernobylImpact,
        // DC-1 Bug #7: if a card was drawn, open CDM in free-draw acknowledge mode.
        // detailModalCard = drawn card so CDM opens immediately on cover.
        // pendingFreeDrawCard set so CDM footer shows "Take Card / Discard".
        // pendingEffectAck suppressed — CDM is the acknowledge for this effect.
        detailModalCard: drawnCardForHand,
        modalRevealState: "cover" as const,
        detailModalCardFromHand: false,
        pendingFreeDrawCard: drawnCardForHand,
        pendingEffectAck: drawnCardForHand !== null ? null : ackPayload,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
      };
    }),

  toggleHandPanel: () => set((s) => ({ handPanelCollapsed: !s.handPanelCollapsed })),

  // ---------------------------------------------------------------------------
  // WEB.4.UI.4 — Build menu actions
  // ---------------------------------------------------------------------------

  openBuildMenu: () => set((s) => (_isGameInputBlocked(s) ? {} : { buildMenuOpen: true })),

  closeBuildMenu: () => set({ buildMenuOpen: false }),

  buildFacility: (facilityId, cost, meta, chosenDiscountKind) =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      // R9 codex HIGH: block during cutscene/dialogue/transition.
      if (_isGameInputBlocked(s)) return {};
      const seat = s.currentSeat;
      const tileIdx = s.playerTiles[seat] ?? 0;

      // Guard: tile already has a facility — no-op
      if (s.builtFacilities[tileIdx] !== undefined) {
        console.warn("[buildFacility] Tile already occupied:", tileIdx);
        return {};
      }

      // Guard: insufficient tokens — no-op
      const balance = s.tokens[seat] ?? 0;
      if (balance < cost) {
        console.warn("[buildFacility] Insufficient tokens:", balance, "need", cost);
        return {};
      }

      // R20-GD-1 — Drought: block Hydro builds while hydroBlockTurns[seat] > 0.
      // facilityId is lowercase web-client id; Hydro maps to "hydro".
      if ((s.hydroBlockTurns[seat] ?? 0) > 0 && facilityId === "hydro") {
        console.warn(
          `[buildFacility] Seat ${seat} cannot build Hydro — Drought blocks for ${s.hydroBlockTurns[seat]} more turn(s).`,
        );
        return {};
      }

      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[seat] = balance - cost;

      // WEB.4.UI.9a codex R1 HIGH 1: record tile ownership so endTurn's tick
      // (which filters by ownedBy[tileIdx] === endingSeat) actually credits
      // this facility's Energy / Pollution. Without this, real BuildMenu flow
      // produces a facility that never ticks.
      const newOwnedBy = { ...s.ownedBy, [tileIdx]: seat };

      // WEB.4.UI.11c: snapshot the FacilityRule used at build time. Caller
      // (BuildMenu) passes era-specific values from facility-rules.json so
      // downstream tick/toll/refund queries use the right numbers.
      const newMeta = { ...s.builtFacilityMeta };
      if (meta !== undefined) {
        newMeta[tileIdx] = {
          energyPerTurn: meta.energyPerTurn,
          pollutionPerTurn: meta.pollutionPerTurn,
          tollPerLand: meta.tollPerLand,
          builtCost: cost,
          builtInEra: meta.builtInEra,
          builtOnTerrain: meta.builtOnTerrain,
        };
      }

      // R5 #6 — Mark build anim played on actual build so subsequent remounts
      // for the same tile (another player lands) skip the construction sequence.
      const newAnimPlayedTiles = new Set([...s.facilityBuildAnimPlayedTiles, tileIdx]);
      console.info(
        `[buildFacility] Tile ${tileIdx}: built ${facilityId}, marking build-anim played.`,
      );

      // R11.2.6 (codex HIGH 2): drain PEQ bumps + consume chosen discount.
      // facilityId is a web-client lowercase id; map via FACILITY_ID_TO_TYPE for
      // rules-engine FacilityType (PascalCase). Simple toUpperCase() broke
      // multi-word ids like "offshoredrilling" / "floatingsolar" / "wastetoenergy".
      const ftForDrain = FACILITY_ID_TO_TYPE[facilityId];
      if (ftForDrain === undefined) {
        console.warn(
          `[buildFacility] Unmapped facility id "${facilityId}" — skipping PEQ drain (data drift).`,
        );
      }
      const { newState: peqAfterBuild } =
        ftForDrain === undefined
          ? { newState: s.peqState }
          : onBuildExecuted(
              s.peqState,
              createActionLog(),
              seat,
              ftForDrain,
              chosenDiscountKind ?? null,
            );

      // Bug #14 + R-item4 (auto-discard spent PEQ cards): After the build,
      // remove from the seat's hand every card whose PEQ entry was consumed or
      // drained this build. Covers BOTH a chosen Whenever-mode BuildCostMod
      // discount (existing) AND self-penalty bumps (NextBuildCostBump from
      // Logistics_Disruption / Worker_Union_Strikes) that onBuildExecuted's
      // drainBumps just removed — previously those penalty cards lingered in
      // hand after their +5 fired. Match by instanceId present in the pre-build
      // queue but absent post-build. A card keeping ANY pending entry (same
      // instanceId still in post) is retained — only fully-spent cards leave.
      let handsAfterBuild = s.hands;
      const preSeatQueue = s.peqState.queues[String(seat)] ?? Object.freeze([]);
      const postSeatQueue = peqAfterBuild.queues[String(seat)] ?? Object.freeze([]);
      const postInstanceIds = new Set(postSeatQueue.map((p) => String(p.instanceId)));
      const consumedInstanceIds = new Set<string>();
      for (const pe of preSeatQueue) {
        if (pe.instanceId > 0 && !postInstanceIds.has(String(pe.instanceId))) {
          consumedInstanceIds.add(String(pe.instanceId));
        }
      }
      if (consumedInstanceIds.size > 0) {
        const nextH = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        nextH[seat] = s.hands[seat].filter((c) => !consumedInstanceIds.has(c.instanceId));
        handsAfterBuild = nextH;
      }

      return {
        tokens: newTokens,
        builtFacilities: { ...s.builtFacilities, [tileIdx]: facilityId },
        builtFacilityMeta: newMeta,
        ownedBy: newOwnedBy,
        buildMenuOpen: false,
        facilityBuildAnimPlayedTiles: newAnimPlayedTiles,
        peqState: peqAfterBuild,
        hands: handsAfterBuild,
      };
    }),

  // WEB.4.SS.5: Voluntary demolish of own facility. Refunds 50% of builtCost
  // (matches Decommission card semantics). Tile becomes buildable again.
  // No-op if tile has no facility OR not owned by current seat.
  demolishOwnFacility: () =>
    set((s) => {
      if (s.gameStatus === "ended") return {};
      const seat = s.currentSeat;
      const tileIdx = s.playerTiles[seat] ?? 0;
      const facilityId = s.builtFacilities[tileIdx];
      if (facilityId === undefined) {
        console.warn("[demolishOwnFacility] No facility on current tile:", tileIdx);
        return {};
      }
      if (s.ownedBy[tileIdx] !== seat) {
        console.warn(`[demolishOwnFacility] Tile ${tileIdx} not owned by current seat ${seat}.`);
        return {};
      }

      // Refund: 50% of builtCost (from meta if present, else baseCost fallback)
      const meta = s.builtFacilityMeta[tileIdx];
      const builtCost =
        meta?.builtCost ?? FACILITIES.find((f) => f.id === facilityId)?.baseCost ?? 0;
      const refund = Math.ceil(builtCost * 0.5);
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[seat] = (newTokens[seat] ?? 0) + refund;

      const newBuilt = { ...s.builtFacilities };
      delete newBuilt[tileIdx];
      const newOwnedBy = { ...s.ownedBy };
      delete newOwnedBy[tileIdx];
      const newMeta = { ...s.builtFacilityMeta };
      delete newMeta[tileIdx];

      console.info(
        `[demolishOwnFacility] Seat ${seat} demolished ${facilityId} at tile ${tileIdx}, refunded ${refund}T.`,
      );

      return {
        tokens: newTokens,
        builtFacilities: newBuilt,
        ownedBy: newOwnedBy,
        builtFacilityMeta: newMeta,
        buildMenuOpen: false,
      };
    }),

  // ---------------------------------------------------------------------------
  // WEB.4.UI.6 — Target picker actions
  // ---------------------------------------------------------------------------

  openTargetPicker: (card) => set({ targetPickerCard: card, targetPickerVictimSeat: null }),

  closeTargetPicker: () => set({ targetPickerCard: null, targetPickerVictimSeat: null }),

  confirmTarget: (victimSeat) =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      const card = s.targetPickerCard;
      if (card === null) return {};

      const cardDef = getCardById(card.id);
      if (cardDef === undefined) {
        console.error("[confirmTarget] Card id not found:", card.id);
        return { targetPickerCard: null, targetPickerVictimSeat: null };
      }

      // WEB.4.UI.7: chained-effect cards (FacilityTransfer / Destroy / Decommission)
      // now open TilePicker to choose WHICH facility on the chosen opponent.
      const chainEff = cardDef.effects.find(
        (e) =>
          (e.kind === "FacilityTransfer" || e.kind === "Destroy" || e.kind === "Decommission") &&
          e.target === "OpponentChosen",
      );
      if (chainEff !== undefined) {
        // Codex R1 HIGH 2: pass authored categoryFilter so Climate_Protest
        // (Fossil-only) and Coal_Lobby_Influence (Coal-only) restrict valid
        // tiles to facilities in the allowed category/type set.
        const rawCatFilter = (chainEff as { categoryFilter?: readonly string[] }).categoryFilter;
        const catFilter =
          rawCatFilter !== undefined && rawCatFilter.length > 0 ? rawCatFilter : undefined;
        const filter: TileFilter = {
          kind: "OpponentOwned",
          opponentSeat: victimSeat,
          categoryFilter: catFilter,
        };
        type TilePickerIntent = GameState["tilePickerIntent"];
        let intent: TilePickerIntent = null;
        let mode: "select" | "destroy" | "protect" | "heal" | "teleport" = "select";

        if (chainEff.kind === "FacilityTransfer") {
          intent = { kind: "Smog", victimSeat, cardInstanceId: card.instanceId };
          mode = "select";
        } else if (chainEff.kind === "Destroy") {
          intent = { kind: "MercenariesDestroy", victimSeat, cardInstanceId: card.instanceId };
          mode = "destroy";
        } else {
          // Decommission — Climate_Protest
          intent = { kind: "ClimateProtestDecom", victimSeat, cardInstanceId: card.instanceId };
          mode = "select";
        }

        return {
          targetPickerCard: null,
          targetPickerVictimSeat: null,
          tilePickerOpen: true,
          tilePickerFilter: filter,
          tilePickerMode: mode,
          tilePickerCardId: card.id,
          selectedPickerTile: null,
          tilePickerIntent: intent,
        };
      }

      // WEB.4.UI.12: StealCard now supported. Spy_In_Action / Policy_Infiltration /
      // Policy_Exchange (all StealCard@OpponentChosen) handled inline below
      // — pick a random card from victim's hand, transfer to drawer.
      // R19-GC: Policy_Exchange also has a GiveCard arm — after steal fires,
      // open pendingGiveCard so drawer picks one own card to give back.
      const stealEffect = cardDef.effects.find(
        (e) => e.kind === "StealCard" && e.target === "OpponentChosen",
      );
      if (stealEffect !== undefined) {
        const seat = s.currentSeat;
        // WEB.4.UI.15 (gap #3.B) — Immunity gate vs StealCard.
        const immLogSteal = createActionLog();
        const immSteal = consumeImmunityIfMatches(
          s.immunityState,
          immLogSteal,
          victimSeat,
          "StealCard",
        );
        if (immSteal.intercepted) {
          console.info(
            `[confirmTarget:StealCard] Seat ${victimSeat} immunity intercepted ${cardDef.name}.`,
          );
          const handsBlock = [...s.hands] as [
            CardInHand[],
            CardInHand[],
            CardInHand[],
            CardInHand[],
          ];
          handsBlock[seat] = handsBlock[seat].filter((c) => c.instanceId !== card.instanceId);
          return {
            hands: handsBlock,
            targetPickerCard: null,
            targetPickerVictimSeat: null,
            lastStolenCard: null,
            immunityState: immSteal.newState,
            lastImmunityBlock: {
              defenderSeat: victimSeat,
              attackerSeat: seat,
              effectKind: "StealCard",
              sourceCardName: cardDef.name,
            },
          };
        }
        const victimHand = s.hands[victimSeat];
        if (victimHand.length === 0) {
          console.warn(
            `[confirmTarget:StealCard] Victim seat ${victimSeat} has empty hand — no card to steal. Card consumed anyway.`,
          );
          // Consume the played card without effect (victim had nothing).
          const nextHands = [...s.hands] as [
            CardInHand[],
            CardInHand[],
            CardInHand[],
            CardInHand[],
          ];
          nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== card.instanceId);
          return {
            hands: nextHands,
            targetPickerCard: null,
            targetPickerVictimSeat: null,
            lastStolenCard: null,
          };
        }
        // Pick a random card from victim's hand using seeded RNG.
        const idx = _rng.nextInt(0, victimHand.length - 1);
        const stolen = victimHand[idx];
        if (stolen === undefined) {
          return { targetPickerCard: null, targetPickerVictimSeat: null };
        }
        // Transfer: remove from victim, append to drawer.
        const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        nextHands[victimSeat] = victimHand.filter((c) => c.instanceId !== stolen.instanceId);
        nextHands[seat] = [
          ...nextHands[seat].filter((c) => c.instanceId !== card.instanceId),
          stolen,
        ];
        // R19-GC: check if this card also has a GiveCard arm (Policy_Exchange SWAP).
        // If yes, open pendingGiveCard so drawer picks which card to give.
        // The Policy_Exchange card was removed from drawer's hand above (filter by instanceId).
        const hasGiveArm = cardDef.effects.some(
          (e) => e.kind === "GiveCard" && e.target === "OpponentChosen",
        );
        if (hasGiveArm) {
          // Drawer's hand at this point has the stolen card added + PE card removed.
          // They now pick which remaining card to give to victimSeat.
          return {
            hands: nextHands,
            targetPickerCard: null,
            targetPickerVictimSeat: null,
            lastStolenCard: { from: victimSeat, to: seat, cardId: stolen.id },
            pendingGiveCard: { victimSeat, sourceCardId: card.id },
          };
        }
        return {
          hands: nextHands,
          targetPickerCard: null,
          targetPickerVictimSeat: null,
          lastStolenCard: { from: victimSeat, to: seat, cardId: stolen.id },
        };
      }

      // Bug #12 fix: DiceOutcome@OpponentChosen (Mercenaries Policy / Spy_Sabotage_II).
      // Player picked a victim seat — now open TilePicker to pick WHICH facility.
      // Pipeline will run in confirmTilePick with targetTileIndices+victimSeat seeded.
      const diceOutcomeEff = cardDef.effects.find(
        (e) => e.kind === "DiceOutcome" && e.target === "OpponentChosen",
      );
      if (diceOutcomeEff !== undefined) {
        const rawCatFilterDice = (diceOutcomeEff as { categoryFilter?: readonly string[] })
          .categoryFilter;
        const catFilterDice =
          rawCatFilterDice !== undefined && rawCatFilterDice.length > 0
            ? rawCatFilterDice
            : undefined;
        const diceFilter: TileFilter = {
          kind: "OpponentOwned",
          opponentSeat: victimSeat,
          categoryFilter: catFilterDice,
        };
        return {
          targetPickerCard: null,
          targetPickerVictimSeat: null,
          tilePickerOpen: true,
          tilePickerFilter: diceFilter,
          tilePickerMode: "select" as const,
          tilePickerCardId: card.id,
          selectedPickerTile: null,
          tilePickerIntent: {
            kind: "DiceOutcomeAttack",
            victimSeat,
            cardInstanceId: card.instanceId,
            cardId: card.id,
          },
        };
      }

      // Also block unsupported effect kinds. Document as deferred.
      const SUPPORTED_OPPONENT_KINDS = new Set([
        "StealEnergy",
        "TokenDelta",
        "EnergyDelta",
        "PollutionDelta",
      ]);
      const opponentEffectsUnsupported = cardDef.effects
        .filter((e) => e.target === "OpponentChosen" && !SUPPORTED_OPPONENT_KINDS.has(e.kind))
        .map((e) => e.kind);
      if (opponentEffectsUnsupported.length > 0) {
        console.warn(
          `[confirmTarget] ${cardDef.name}: unsupported OpponentChosen effect(s) ${opponentEffectsUnsupported.join(", ")} — WEB.4.UI.7+. Card returned to hand.`,
        );
        return { targetPickerCard: null, targetPickerVictimSeat: null };
      }

      const seat = s.currentSeat;
      const pollutionTotal = s.pollution.reduce((a, b) => a + b, 0);

      let tokenState = createTokenState();
      let energyState = createEnergyState();
      for (let i = 0; i < 4; i++) {
        tokenState = setTokenBalance(tokenState, i, s.tokens[i] ?? 0);
        energyState = setEnergyCubes(energyState, i, s.energy[i] ?? 0);
      }
      const pollutionState = createPollutionState(pollutionTotal);

      // WEB.4.UI.8a: mint CIS instance so AuraApply (if any) gets valid cardInstanceId.
      const cisInit = createCardInstanceState();
      const { newState: cisAfter, instanceId: cardInstId } = createForSeat(
        cisInit,
        createActionLog(),
        cardDef.name,
        seat,
        "Hand",
      );

      // WEB.4.UI.18 — mirror playCard's autoChosenBranch so OR-choice cards routed
      // through confirmTarget (none today, but defensive) don't silently no-op.
      const branchIdsCT = [
        ...new Set(
          cardDef.effects.map((e) => e.branchId).filter((b): b is string => b !== undefined),
        ),
      ];
      const autoChosenBranchCT = branchIdsCT.find((b) => b !== "pay") ?? branchIdsCT[0];

      // WEB.4.PT.17 — mirror playCard: resolve single-target ctx for confirmTarget path.
      // None of the 8 PT.17 cards have OpponentChosen effects so they never reach this
      // path today, but kept symmetric for any future combo card.
      let singleTargetVictimCT: SeatIndex = victimSeat;
      let singleTargetTileIndicesCT: number[] = [];
      const singleTargetEffectCT = cardDef.effects.find(
        (e) =>
          (e.kind === "Destroy" || e.kind === "Decommission" || e.kind === "RngBranch") &&
          e.affectsAll !== true &&
          (e.target === "Self" || e.target === "NearestPlayer"),
      );
      if (singleTargetEffectCT !== undefined) {
        if (singleTargetEffectCT.target === "NearestPlayer") {
          // R18-FB-6: use combined helper so we skip nearest seats with no facilities.
          const nearestCT = _resolveNearestPlayerSeatWithFacility(
            s.playerTiles,
            seat,
            s.lostSeats,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffectCT.categoryFilter,
            s.numPlayers,
          );
          if (nearestCT !== null) {
            singleTargetVictimCT = nearestCT.seat;
            singleTargetTileIndicesCT = [nearestCT.tileIndex];
          }
        } else {
          // target === "Self"
          const ownerSeatCT: SeatIndex = seat;
          singleTargetVictimCT = ownerSeatCT;
          const targetTileCT = _findFirstFacilityTileFor(
            ownerSeatCT,
            s.builtFacilities as Record<number, string | undefined>,
            s.ownedBy as Record<number, number | undefined>,
            singleTargetEffectCT.categoryFilter,
          );
          if (targetTileCT >= 0) singleTargetTileIndicesCT = [targetTileCT];
        }
      }

      const ctx = makeEffectContext(seat, singleTargetVictimCT, {
        cardTags: cardDef.tags ?? [],
        rngSeed: s.rngSeed,
        cardInstanceId: cardInstId,
        ...(autoChosenBranchCT !== undefined ? { chosenBranchId: autoChosenBranchCT } : {}),
        ...(singleTargetTileIndicesCT.length > 0
          ? { targetTileIndices: singleTargetTileIndicesCT }
          : {}),
      });

      const services: PipelineServices = {
        token: tokenState,
        energy: energyState,
        pollution: pollutionState,
        // WEB.4.UI.8a: thread persistent aura state through confirmTarget too.
        aura: s.auraState,
        // WEB.4.UI.16 — thread persistent PEQ state through pipeline.
        peq: s.peqState,
        cis: cisAfter,
        // DC-1 Bug #11: real countProvider (was all-zero stub).
        countProvider: _buildCountProvider(_seedFacilityOwnership(s)),
        // WEB.4.UI.18 — mirror playCard seeding. No UI.18 card reaches confirmTarget
        // today (sweeps have no OpponentChosen effect), but a future combo card
        // mixing OpponentChosen + Global+affectsAll would silently no-op without this.
        facilityOwnership: _seedFacilityOwnership(s),
        rng: _rng,
        // WEB.4.UI.15 (gap #3.A) — thread persistent immunity through pipeline.
        immunity: s.immunityState,
      };

      // WEB.4.UI.18 — capture pre-pipeline ownership to compute removed tiles after.
      const facilityOwnershipBeforeCT = services.facilityOwnership;

      let pipelineResult: ReturnType<typeof runCard>;
      try {
        pipelineResult = runCard(cardDef, ctx, services, createActionLog());
      } catch (err) {
        console.error("[confirmTarget] runCard threw:", err);
        // Do NOT consume on pipeline error — return card to hand.
        return { targetPickerCard: null, targetPickerVictimSeat: null };
      }

      const newTokens = [...s.tokens] as [number, number, number, number];
      const newEnergy = [...s.energy] as [number, number, number, number];
      const newPollution = [...s.pollution] as [number, number, number, number];

      for (let i = 0; i < 4; i++) {
        const newBal = pipelineResult.services.token.balances[i];
        if (typeof newBal === "number") newTokens[i] = Math.max(0, newBal);
        const newEng = pipelineResult.services.energy.energy[i];
        if (typeof newEng === "number") newEnergy[i] = Math.max(0, newEng);
      }

      const prevGlobal = pollutionTotal;
      const newGlobal = Math.max(0, pipelineResult.services.pollution.globalPollution);
      const pollutionDelta = newGlobal - prevGlobal;
      if (pollutionDelta !== 0) {
        newPollution[seat] = Math.max(0, (newPollution[seat] ?? 0) + pollutionDelta);
      }

      const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== card.instanceId);

      // WEB.4.UI.18 — mirror playCard: propagate multi-tile facility removals.
      // No UI.18 card reaches this path today (sweeps have no OpponentChosen
      // effect), but kept symmetric with playCard for any future combo card.
      const removedTilesCT = _extractRemovedTiles(
        facilityOwnershipBeforeCT,
        pipelineResult.services.facilityOwnership,
      );
      const newBuiltFacilitiesCT = { ...s.builtFacilities };
      const newOwnedByCT = { ...s.ownedBy };
      const newMetaCT = { ...s.builtFacilityMeta };
      let handsAfterTriggersCT = nextHands;
      // DD Bug#10b: track first destroyed facility for toast.
      let facilityDestroyedToastCT: GameState["lastFacilityDestroyed"] = null;
      if (removedTilesCT.length > 0) {
        // WEB.4.PT.17: single-target Destroy also uses DestroyedMine trigger.
        const hasDestroyCT = cardDef.effects.some((e) => e.kind === "Destroy");
        const trigEventCT: "AfterFacilityDestroyedMine" | "AfterFacilityDecommissionedMine" =
          hasDestroyCT ? "AfterFacilityDestroyedMine" : "AfterFacilityDecommissionedMine";
        for (const tileIdx of removedTilesCT) {
          const ownerSeat = s.ownedBy[tileIdx];
          const facilityId = s.builtFacilities[tileIdx];
          // DD Bug#10b: capture the first removal for the destroy toast.
          if (
            facilityDestroyedToastCT === null &&
            ownerSeat !== undefined &&
            facilityId !== undefined
          ) {
            facilityDestroyedToastCT = { seat: ownerSeat as SeatIndex, facilityId, tileIdx };
          }
          delete newBuiltFacilitiesCT[tileIdx];
          delete newOwnedByCT[tileIdx];
          delete newMetaCT[tileIdx];
          if (ownerSeat !== undefined) {
            const trigResult = _dispatchFacilityTrigger(
              handsAfterTriggersCT,
              ownerSeat as SeatIndex,
              trigEventCT,
            );
            handsAfterTriggersCT = trigResult.newHands;
            if (trigResult.tokenGain > 0) {
              newTokens[ownerSeat] = (newTokens[ownerSeat] ?? 0) + trigResult.tokenGain;
              console.info(
                `[confirmTarget:MultiTile] DRF trigger seat ${ownerSeat} +${trigResult.tokenGain}T tile=${tileIdx} from ${trigResult.firedCardNames.join(", ")}.`,
              );
            }
          }
        }
      }

      // WEB.4.UI.10a codex R1 HIGH 2: confirmTarget pollution can also push
      // past threshold (Crisis cards via OpponentChosen). Detect end here too.
      const endState = detectGameEnd(
        {
          tokens: newTokens,
          energy: newEnergy,
          pollution: newPollution,
          round: s.round,
          lostSeats: s.lostSeats,
          builtFacilities: newBuiltFacilitiesCT as Record<number, string | undefined>,
          ownedBy: newOwnedByCT as Record<number, number | undefined>,
        },
        s.lostSeats,
        s.numPlayers,
      );

      return {
        tokens: newTokens,
        energy: newEnergy,
        pollution: newPollution,
        hands: handsAfterTriggersCT,
        builtFacilities: newBuiltFacilitiesCT,
        ownedBy: newOwnedByCT,
        builtFacilityMeta: newMetaCT,
        auraState: pipelineResult.services.aura,
        // WEB.4.UI.15 (gap #3.A) — persist Immunity registered by this play.
        immunityState: pipelineResult.services.immunity,
        // WEB.4.UI.16 — persist PEQ entries.
        peqState: pipelineResult.services.peq,
        targetPickerCard: null,
        targetPickerVictimSeat: null,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
        // DD Bug#10b — emit destroy toast if a facility was removed.
        ...(facilityDestroyedToastCT !== null
          ? { lastFacilityDestroyed: facilityDestroyedToastCT }
          : {}),
      };
    }),

  // ---------------------------------------------------------------------------
  // WEB.4.UI.7 — Tile picker actions
  // ---------------------------------------------------------------------------

  openTilePicker: (params) =>
    set({
      tilePickerOpen: true,
      tilePickerFilter: params.filter,
      tilePickerMode: params.mode,
      tilePickerCardId: params.cardId,
      selectedPickerTile: null,
      tilePickerIntent: params.intent,
    }),

  closeTilePicker: () =>
    set({
      tilePickerOpen: false,
      tilePickerFilter: null,
      tilePickerCardId: null,
      selectedPickerTile: null,
      tilePickerIntent: null,
    }),

  selectPickerTile: (tileIdx) => set({ selectedPickerTile: tileIdx }),

  confirmTilePick: () =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      if (_isGameInputBlocked(s)) return {};
      const tileIdx = s.selectedPickerTile;
      if (tileIdx === null) return {};

      const intent = s.tilePickerIntent;
      if (intent === null) return {};

      const closePicker = {
        tilePickerOpen: false,
        tilePickerFilter: null,
        tilePickerCardId: null,
        selectedPickerTile: null,
        tilePickerIntent: null,
      };

      // ── Teleport ──────────────────────────────────────────────────────────
      if (intent.kind === "Teleport") {
        const seat = s.currentSeat;
        const nextTiles: [number, number, number, number] = [...s.playerTiles] as [
          number,
          number,
          number,
          number,
        ];
        nextTiles[seat] = tileIdx;

        // Trigger landing effects for destination tile type
        const tileType = _tileTypeByIndex[tileIdx] ?? "";
        // WEB.4.PT.R2 HIGH: Teleport landing follows PT NPC-only-interact rule —
        // do NOT auto-draw cards or auto-open card detail modal. Player must walk
        // to the NPC and press E (same as normal rollDice landing). TILE_DRAW_POOL
        // is intentionally NOT consulted here. nextHands/detailModalCard are
        // preserved from current state (no mutation).
        const nextHands = s.hands as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        const nextDetailCard: CardInHand | null = s.detailModalCard;
        const nextRevealState: "cover" | "revealed" = s.modalRevealState;

        // WEB.4.PT.R2 HIGH: Jail tile landing via Teleport sets jail counter.
        let nextJail = s.jailTurnsRemaining;
        if (tileType === "Jail") {
          const newJail = [...s.jailTurnsRemaining] as [number, number, number, number];
          newJail[seat] = 3;
          nextJail = newJail;
        }

        // WEB.4.PT.R2 HIGH: Trading tile landing via Teleport opens trade prompt.
        const tradingPromptOpen = tileType === "Trading";

        // N-1 (R16): Energy Dilemma — terrain + era>=2 + per-landing one-shot (Teleport mirror).
        // R21-GF-7: RNG scenario selection with recent-exclusion bias.
        const _edKeyTP = `${tileIdx}:${seat}`;
        const _edEligibleTP =
          SCOUT_TERRAIN_TYPES.has(tileType) &&
          s.era >= 2 &&
          s.pendingQuiz === null &&
          s.pendingAuction === null &&
          s.pendingClimateVote === null &&
          s.pendingScout === null &&
          s.pendingEnergyDilemma === null &&
          s.pendingTrade === null &&
          s.pendingPollutionTax === null &&
          !s.energyDilemmaShownThisLanding[_edKeyTP];
        const _edScenarioTP = _edEligibleTP
          ? _pickEnergyDilemmaScenario(s.recentEnergyDilemmaScenarios)
          : null;
        const nextEnergyDilemmaStateTP =
          _edEligibleTP && _edScenarioTP !== null
            ? {
                pendingEnergyDilemma: {
                  tileIdx,
                  terrain: tileType,
                  optionsKey: _edScenarioTP,
                },
                energyDilemmaShownThisLanding: {
                  ...s.energyDilemmaShownThisLanding,
                  [_edKeyTP]: true,
                },
                recentEnergyDilemmaScenarios: [
                  _edScenarioTP,
                  ...s.recentEnergyDilemmaScenarios,
                ].slice(0, 3),
              }
            : {};

        // WEB.4.UI.9b codex MED 1: Teleport landings must also pay toll
        // when destination is owned by an opponent with a built facility.
        // WEB.4.UI.10a codex R1 HIGH 1: bankruptcy on failed Teleport toll —
        // mirror rollDice block exactly so a forced toll can mark the drawer
        // bankrupt and trigger end-conditions.
        const nextTokens: [number, number, number, number] = [...s.tokens] as [
          number,
          number,
          number,
          number,
        ];
        const tileOwner = s.ownedBy[tileIdx];
        const tileFacilityId = s.builtFacilities[tileIdx];
        let bankruptThisTeleport = false;
        const peqAfterTeleport = s.peqState;
        // R5 #11 codex HIGH: surface teleport-toll via TollPaidModal too.
        let nextPendingTollTp: GameState["pendingTollPayment"] = null;
        if (tileOwner !== undefined && tileOwner !== seat && tileFacilityId !== undefined) {
          const tileMeta = s.builtFacilityMeta[tileIdx];
          const tollBreakdown = computeTollOnLanding(
            tileFacilityId,
            s.auraState,
            tileOwner,
            tileMeta?.tollPerLand,
            // R9 perk: Politician toll × 1.5
            s.playerPerks[tileOwner],
          );
          // R22-GD-7: teleport tile toll is NOT a card-related loss —
          // HalveNextTokenLoss only covers card-effect losses.
          const tollAmount = tollBreakdown.effective;
          if (tollAmount > 0) {
            const drawerBalance = nextTokens[seat] ?? 0;
            let actualPaid = 0;
            if (wouldGoBankrupt(drawerBalance, tollAmount)) {
              nextTokens[seat] = 0;
              nextTokens[tileOwner] = Math.max(0, (nextTokens[tileOwner] ?? 0) + drawerBalance);
              actualPaid = drawerBalance;
              bankruptThisTeleport = true;
            } else {
              nextTokens[seat] = Math.max(0, drawerBalance - tollAmount);
              nextTokens[tileOwner] = Math.max(0, (nextTokens[tileOwner] ?? 0) + tollAmount);
              actualPaid = tollAmount;
            }
            nextPendingTollTp = {
              tileIdx,
              facilityId: tileFacilityId,
              ownerSeat: tileOwner as SeatIndex,
              amount: actualPaid,
              newLanderBalance: nextTokens[seat] ?? 0,
              // R19-GA-5: owner's new balance after receiving the toll.
              newOwnerBalance: nextTokens[tileOwner] ?? 0,
            };
          }
        }

        // Compute next lostSeats + run game-end detection.
        const nextLostSeats = bankruptThisTeleport ? new Set([...s.lostSeats, seat]) : s.lostSeats;
        const endState = detectGameEnd(
          {
            tokens: nextTokens,
            energy: s.energy,
            pollution: s.pollution,
            round: s.round,
            lostSeats: nextLostSeats,
            builtFacilities: s.builtFacilities as Record<number, string | undefined>,
            ownedBy: s.ownedBy as Record<number, number | undefined>,
          },
          nextLostSeats,
          s.numPlayers,
        );

        // NOTE: if destination is another Teleport tile, do NOT re-open
        // TilePicker (one teleport per turn — re-entry guard per spec).
        // WEB.4.PT.R2 HIGH: buildMenuOpen always false on teleport landing —
        // player must walk to Builder NPC and press E (PT NPC-only-interact rule).
        // isBuildable computation removed; build menu gated by NPC interaction only.
        return {
          ...closePicker,
          playerTiles: nextTiles,
          activeTile: tileIdx,
          hands: nextHands,
          detailModalCard: nextDetailCard,
          modalRevealState: nextRevealState,
          buildMenuOpen: false,
          tokens: nextTokens,
          // WEB.4.UI.16 — persist post-toll PEQ state.
          peqState: peqAfterTeleport,
          lostSeats: nextLostSeats,
          // WEB.4.PT.Phase1 codex R2 HIGH: Teleport is a landing too — reset spawn
          // so player doesn't inherit prior tile's walked position in SidescrollerScene.
          scenePos: { x: 315, y: 0 },
          gameStatus: endState.status,
          endReason: endState.reason,
          winnerSeat: endState.winnerSeat,
          winnerSeats: endState.winnerSeats,
          jailTurnsRemaining: nextJail,
          tradingPromptOpen,
          // R19-GC-2: Teleport landing on Trading tile clears stale rejection reason.
          ...(tradingPromptOpen ? { tradeOfferRejectionReason: null } : {}),
          // R5 #11: teleport-toll TollPaidModal surface.
          pendingTollPayment: nextPendingTollTp,
          // N-1 (R16): energy dilemma on terrain landing via Teleport.
          ...nextEnergyDilemmaStateTP,
        };
      }

      // ── Smog_Emergency — FacilityTransfer ────────────────────────────────
      if (intent.kind === "Smog") {
        const { victimSeat, cardInstanceId } = intent;

        // Confirm ownership
        const currentOwner = s.ownedBy[tileIdx];
        if (currentOwner !== victimSeat) {
          console.error("[confirmTilePick:Smog] Tile not owned by victimSeat", {
            tileIdx,
            currentOwner,
            victimSeat,
          });
          return closePicker;
        }

        const facilityId = s.builtFacilities[tileIdx];
        if (facilityId === undefined) {
          console.error("[confirmTilePick:Smog] No facility at tile", tileIdx);
          return closePicker;
        }

        // UI.11c R1 HIGH 3 (Codex): use per-tile builtCost snapshot (era-correct,
        // captures aura-modified paid cost) and fall back to static FACILITIES
        // baseCost only when meta absent (legacy tiles built before UI.11c).
        const facilityDef = FACILITIES.find((f) => f.id === facilityId);
        const metaCost = s.builtFacilityMeta[tileIdx]?.builtCost;
        const baseCost = metaCost ?? facilityDef?.baseCost ?? 0;
        const transferCost = Math.ceil(baseCost * 0.5);

        const seat = s.currentSeat;
        const drawerBalance = s.tokens[seat] ?? 0;

        // UI.16 R1 HIGH (Codex): peek the halved amount BEFORE affordability so
        // a drawer who can afford the halved-cost transfer (but not the full)
        // is correctly served by Climate_Adaptation_Strategy. The PEQ entry is
        // ONLY persisted (committed) when the transfer actually proceeds —
        // never consume the protection on a cancelled action.
        const halvedSmog = _applyHalveNextTokenLoss(s.peqState, seat, transferCost);
        const drawerPay = halvedSmog.halvedLoss;

        // UI.15 R1 HIGH 2 (Codex): affordability check BEFORE immunity consume.
        // Prior order was immunity → afford; a broke attacker could burn the
        // defender's shield with a transfer that would then be cancelled.
        // Codex R1 HIGH 1: require full payment of the halved amount; partial
        // payment would let a 1-token drawer take a 25-cost facility for 1T.
        // NOTE (WEB.4.PT): The spec originally proposed bankruptcy here, but the
        // existing test suite (UI.16 R1 HIGH + TilePicker Smog cancel test) explicitly
        // validates cancel semantics when `drawerBalance < drawerPay` (PEQ NOT consumed,
        // card returned). Bankruptcy at this site is deferred to a future iteration
        // that updates those tests. DO NOT change this to bankruptcy without updating
        // the tests at game-store.test.ts:2421 and TilePicker.test.tsx:534.
        if (drawerBalance < drawerPay) {
          console.warn(
            `[confirmTilePick:Smog] Drawer seat ${seat} cannot afford drawerPay=${drawerPay} (balance=${drawerBalance}). Cancelling transfer; PEQ NOT consumed, card returned to hand.`,
          );
          return closePicker;
        }

        // WEB.4.UI.15 (gap #3.B) — Immunity gate vs StealFacility (forced transfer).
        const immLogSmog = createActionLog();
        const immSmog = consumeImmunityIfMatches(
          s.immunityState,
          immLogSmog,
          victimSeat,
          "StealFacility",
          tileIdx,
        );
        if (immSmog.intercepted) {
          console.info(
            `[confirmTilePick:Smog] Seat ${victimSeat} immunity intercepted Smog_Emergency at tile ${tileIdx}.`,
          );
          const smogHands = [...s.hands] as [
            CardInHand[],
            CardInHand[],
            CardInHand[],
            CardInHand[],
          ];
          smogHands[seat] = smogHands[seat].filter((c) => c.instanceId !== cardInstanceId);
          // PEQ NOT consumed — the transfer didn't actually happen.
          return {
            ...closePicker,
            hands: smogHands,
            immunityState: immSmog.newState,
            lastImmunityBlock: {
              defenderSeat: victimSeat,
              attackerSeat: seat,
              effectKind: "StealFacility",
              sourceCardName: "Smog_Emergency",
            },
          };
        }

        if (halvedSmog.consumed) {
          console.info(
            `[confirmTilePick:Smog] Seat ${seat} HalveNextTokenLoss consumed: transferCost ${transferCost}→${drawerPay}.`,
          );
          // R22-GD-7 — record lastHalveNextTokenLossUse for regression + UI toast.
          useGameStore.setState({
            lastHalveNextTokenLossUse: {
              seat: seat as SeatIndex,
              originalAmount: transferCost,
              halvedAmount: drawerPay,
              sourceContext: "smog-transfer",
            },
          });
          useGameStore
            .getState()
            .showAuraTriggeredToast(
              `Smog cost halved: ${transferCost}T → ${drawerPay}T`,
              "Climate Adaptation",
            );
          window.setTimeout(
            () => useGameStore.getState().clearLastHalveNextTokenLossUse(),
            2000,
          );
        }

        // Drawer pays drawerPay; victim/current owner is credited the same amount.
        const newTokens = [...s.tokens] as [number, number, number, number];
        newTokens[seat] = Math.max(0, drawerBalance - drawerPay);
        newTokens[victimSeat] = Math.max(0, (newTokens[victimSeat] ?? 0) + drawerPay);

        const newOwnedBy = { ...s.ownedBy, [tileIdx]: seat as SeatIndex };
        const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== cardInstanceId);

        return {
          ...closePicker,
          tokens: newTokens,
          ownedBy: newOwnedBy,
          hands: nextHands,
          peqState: halvedSmog.newPeqState,
        };
      }

      // ── Mercenaries — Destroy ─────────────────────────────────────────────
      //
      // WHY COAL_LOBBY_INFLUENCE KEEPS REGRESSING — read before touching.
      //
      // ROOT CAUSE HISTORY (5 prior fixes, all partial):
      //   R5/R6: assumed discardMode=AfterUse was enough → wrong, discard never fired.
      //   R17b:  routed via existing path → path still didn't consume in all branches.
      //   R18:   claimed StealCard→MercenariesDestroy covered it → wrong card type.
      //   R20-GD-5: regression lock bypassed UI flow — setState({targetPickerCard}) then
      //             confirmTarget() directly, so confirmTilePick path was nominally tested
      //             but the test never drove playCard() → confirmTarget() → confirmTilePick()
      //             as a real player does, masking regressions in the middle hops.
      //
      // THE REAL BUG (why card persistently stays in hand):
      //   The ownership check below (currentOwner !== victimSeat) fires `return closePicker`
      //   which closes the TilePicker WITHOUT removing the card from hand. This happens when
      //   the victim's coal tile is concurrently claimed or the board state is stale.
      //   Card consumption MUST happen regardless of whether the destroy succeeds — the player
      //   already committed the card at `confirmTarget`. Only `closeTilePicker()` (Cancel button)
      //   is a legitimate "keep the card" path.
      //
      // JUNK TEMPLATE FIELDS (pendingKind:"NextBuildFree", auraScope:"Toll") on the effect:
      //   These are unused defaults from the card template. The pipeline never reads pendingKind
      //   for a Destroy effect (only BuildCostMod reads it). They are harmless but confusing.
      //
      // PREVENTION: R21-GD-3 regression lock drives the FULL store chain:
      //   playCard() → confirmTarget() → selectPickerTile() → confirmTilePick()
      //   and asserts card absent from hand AND facility removed. DO NOT use setState
      //   shortcuts that skip any of these steps in the lock.
      //
      if (intent.kind === "MercenariesDestroy") {
        const { victimSeat, cardInstanceId } = intent;

        // Consume card from hand immediately — player committed at confirmTarget.
        // This is done BEFORE the ownership check so the card is ALWAYS consumed
        // once confirmTilePick fires, even if the target is no longer valid.
        // (Cancelling is only possible via closeTilePicker before confirmation.)
        const seat = s.currentSeat;
        const nextHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        nextHands[seat] = nextHands[seat].filter((c) => c.instanceId !== cardInstanceId);

        const currentOwner = s.ownedBy[tileIdx];
        if (currentOwner !== victimSeat) {
          console.error(
            "[confirmTilePick:Destroy] Tile not owned by victimSeat — destroy aborted, card still consumed",
            {
              tileIdx,
              currentOwner,
              victimSeat,
            },
          );
          // Card is consumed (committed), but destroy is a no-op.
          return { ...closePicker, hands: nextHands };
        }

        // WEB.4.UI.15 (gap #3.B) — Immunity gate. If victim has a matching
        // shield (e.g. UN_Green_Pact_Support), consume one charge and cancel
        // the destroy. Attacker's card is still consumed (paid the play cost).
        const immLog = createActionLog();
        const immResult = consumeImmunityIfMatches(
          s.immunityState,
          immLog,
          victimSeat,
          "Destroy",
          tileIdx,
        );
        if (immResult.intercepted) {
          console.info(
            `[confirmTilePick:Destroy] Seat ${victimSeat} immunity intercepted Mercenaries at tile ${tileIdx}.`,
          );
          return {
            ...closePicker,
            hands: nextHands,
            immunityState: immResult.newState,
            lastImmunityBlock: {
              defenderSeat: victimSeat,
              attackerSeat: seat,
              effectKind: "Destroy",
              sourceCardName: "Mercenaries",
            },
          };
        }

        const newBuiltFacilities = { ...s.builtFacilities };
        delete newBuiltFacilities[tileIdx];

        const newOwnedBy = { ...s.ownedBy };
        delete newOwnedBy[tileIdx];

        // UI.11c R1 HIGH 3 (Codex) — clear stale meta when facility destroyed.
        const newMetaMerc = { ...s.builtFacilityMeta };
        newMetaMerc[tileIdx] = undefined;

        // WEB.4.UI.15 (gap #3.C) — Disaster_Relief_Fund trigger dispatch.
        // Victim seat retaliates with hand-held insurance cards.
        const trigMerc = _dispatchFacilityTrigger(
          nextHands,
          victimSeat,
          "AfterFacilityDestroyedMine",
        );
        const newTokensMerc = [...s.tokens] as [number, number, number, number];
        if (trigMerc.tokenGain > 0) {
          newTokensMerc[victimSeat] = (newTokensMerc[victimSeat] ?? 0) + trigMerc.tokenGain;
          console.info(
            `[confirmTilePick:Destroy] Trigger dispatch: seat ${victimSeat} +${trigMerc.tokenGain}T from ${trigMerc.firedCardNames.join(", ")}.`,
          );
        }

        const destroyedFacilityId = s.builtFacilities[tileIdx] ?? "unknown";
        return {
          ...closePicker,
          builtFacilities: newBuiltFacilities,
          ownedBy: newOwnedBy,
          builtFacilityMeta: newMetaMerc,
          hands: trigMerc.newHands,
          tokens: newTokensMerc,
          // DD Bug#10b — destroy toast
          lastFacilityDestroyed: {
            seat: victimSeat as SeatIndex,
            facilityId: destroyedFacilityId,
            tileIdx,
          },
        };
      }

      // ── Climate_Protest — Decommission (50% refund to OWNER) ─────────────
      if (intent.kind === "ClimateProtestDecom") {
        const { victimSeat, cardInstanceId } = intent;

        const currentOwner = s.ownedBy[tileIdx];
        if (currentOwner !== victimSeat) {
          console.error("[confirmTilePick:Decom] Tile not owned by victimSeat", {
            tileIdx,
            currentOwner,
            victimSeat,
          });
          return closePicker;
        }

        const seat = s.currentSeat;
        const handsDecomEarly = [...s.hands] as [
          CardInHand[],
          CardInHand[],
          CardInHand[],
          CardInHand[],
        ];
        handsDecomEarly[seat] = handsDecomEarly[seat].filter(
          (c) => c.instanceId !== cardInstanceId,
        );

        // WEB.4.UI.15 (gap #3.B) — Immunity gate vs Decommission.
        const immLogDecom = createActionLog();
        const immDecom = consumeImmunityIfMatches(
          s.immunityState,
          immLogDecom,
          victimSeat,
          "Decommission",
          tileIdx,
        );
        if (immDecom.intercepted) {
          console.info(
            `[confirmTilePick:Decom] Seat ${victimSeat} immunity intercepted Climate_Protest at tile ${tileIdx}.`,
          );
          return {
            ...closePicker,
            hands: handsDecomEarly,
            immunityState: immDecom.newState,
            lastImmunityBlock: {
              defenderSeat: victimSeat,
              attackerSeat: seat,
              effectKind: "Decommission",
              sourceCardName: "Climate_Protest",
            },
          };
        }

        // Decommission refund to victimSeat.
        // Default = 50% of build cost (locked rule).
        // R9 perk: Politician owner → 25% refund override.
        // UI.11c R1 HIGH 3 (Codex): use per-tile builtCost snapshot so era-3
        // nuclear built for 45T refunds 23T (not 13T from static 25T). Falls
        // back to FACILITIES.baseCost only when meta absent (legacy tiles).
        const decomFacilityId = s.builtFacilities[tileIdx];
        const decomFacilityDef = FACILITIES.find((f) => f.id === decomFacilityId);
        const decomMetaCost = s.builtFacilityMeta[tileIdx]?.builtCost;
        const refundBase = decomMetaCost ?? decomFacilityDef?.baseCost ?? 0;
        const refundFraction = s.playerPerks[victimSeat] === "politician" ? 0.25 : 0.5;
        const refund = Math.ceil(refundBase * refundFraction);

        const newBuiltFacilities = { ...s.builtFacilities };
        delete newBuiltFacilities[tileIdx];

        const newOwnedBy = { ...s.ownedBy };
        delete newOwnedBy[tileIdx];

        // UI.11c R1 HIGH 3 (Codex) — clear stale meta on decommission.
        const newMetaDecom = { ...s.builtFacilityMeta };
        newMetaDecom[tileIdx] = undefined;

        const newTokens = [...s.tokens] as [number, number, number, number];
        if (refund > 0) {
          newTokens[victimSeat] = (newTokens[victimSeat] ?? 0) + refund;
        }

        // WEB.4.UI.15 (gap #3.C) — Disaster_Relief_Fund trigger dispatch.
        const trigDecom = _dispatchFacilityTrigger(
          handsDecomEarly,
          victimSeat,
          "AfterFacilityDecommissionedMine",
        );
        if (trigDecom.tokenGain > 0) {
          newTokens[victimSeat] = (newTokens[victimSeat] ?? 0) + trigDecom.tokenGain;
          console.info(
            `[confirmTilePick:Decom] Trigger dispatch: seat ${victimSeat} +${trigDecom.tokenGain}T from ${trigDecom.firedCardNames.join(", ")}.`,
          );
        }

        return {
          ...closePicker,
          builtFacilities: newBuiltFacilities,
          ownedBy: newOwnedBy,
          builtFacilityMeta: newMetaDecom,
          tokens: newTokens,
          hands: trigDecom.newHands,
          // DD Bug#10b — decommission toast (shows same UI as destroy)
          lastFacilityDestroyed: {
            seat: victimSeat as SeatIndex,
            facilityId: decomFacilityId ?? "unknown",
            tileIdx,
          },
        };
      }

      // ── DiceOutcomeAttack — Mercenaries (Policy) / Spy_Sabotage_II ───────────
      // Bug #12 fix: player picked both victim seat (in confirmTarget) and target
      // tile (here). Seed ctx with targetTileIndices+victimSeat and run pipeline.
      // The DiceOutcome resolver handles the dice roll and on-hit dispatch
      // (StealFacility valueInt=1 or Destroy valueInt=2) internally.
      if (intent.kind === "DiceOutcomeAttack") {
        const {
          victimSeat: diceVictimSeat,
          cardInstanceId: diceCardInstId,
          cardId: diceCardId,
        } = intent;

        const seat = s.currentSeat;
        const diceCardDef = getCardById(diceCardId);
        if (diceCardDef === undefined) {
          console.error("[confirmTilePick:DiceOutcomeAttack] Card not found:", diceCardId);
          return closePicker;
        }

        // Consume card from hand immediately (AfterUse).
        const diceHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
        diceHands[seat] = diceHands[seat].filter((c) => c.instanceId !== diceCardInstId);

        // Build pipeline state.
        let diceTokenState = createTokenState();
        let diceEnergyState = createEnergyState();
        for (let i = 0; i < 4; i++) {
          diceTokenState = setTokenBalance(diceTokenState, i, s.tokens[i] ?? 0);
          diceEnergyState = setEnergyCubes(diceEnergyState, i, s.energy[i] ?? 0);
        }
        const dicePollutionTotal = s.pollution.reduce((a, b) => a + b, 0);
        const dicePollutionState = createPollutionState(dicePollutionTotal);

        const diceCisInit = createCardInstanceState();
        const { newState: diceCisAfter, instanceId: diceInstId } = createForSeat(
          diceCisInit,
          createActionLog(),
          diceCardDef.name,
          seat,
          "Hand",
        );

        const diceCtx = makeEffectContext(seat, diceVictimSeat, {
          cardTags: diceCardDef.tags ?? [],
          rngSeed: s.rngSeed,
          cardInstanceId: diceInstId,
          targetTileIndices: [tileIdx],
        });

        const diceFacilityOwnershipBefore = _seedFacilityOwnership(s);
        const diceServices: PipelineServices = {
          token: diceTokenState,
          energy: diceEnergyState,
          pollution: dicePollutionState,
          aura: s.auraState,
          peq: s.peqState,
          cis: diceCisAfter,
          countProvider: _buildCountProvider(diceFacilityOwnershipBefore),
          facilityOwnership: diceFacilityOwnershipBefore,
          rng: _rng,
          immunity: s.immunityState,
        };

        let dicePipelineResult: ReturnType<typeof runCard>;
        try {
          dicePipelineResult = runCard(diceCardDef, diceCtx, diceServices, createActionLog());
        } catch (err) {
          console.error("[confirmTilePick:DiceOutcomeAttack] runCard threw:", err);
          // Pipeline error — card already removed from hand (consumed), close picker.
          return { ...closePicker, hands: diceHands };
        }

        const diceNewTokens = [...s.tokens] as [number, number, number, number];
        const diceNewEnergy = [...s.energy] as [number, number, number, number];

        for (let i = 0; i < 4; i++) {
          const nb = dicePipelineResult.services.token.balances[i];
          if (typeof nb === "number") diceNewTokens[i] = Math.max(0, nb);
          const ne = dicePipelineResult.services.energy.energy[i];
          if (typeof ne === "number") diceNewEnergy[i] = Math.max(0, ne);
        }

        const diceNewPollution = [...s.pollution] as [number, number, number, number];
        const diceNewGlobal = Math.max(0, dicePipelineResult.services.pollution.globalPollution);
        const dicePollDelta = diceNewGlobal - dicePollutionTotal;
        if (dicePollDelta !== 0) {
          diceNewPollution[seat] = Math.max(0, (diceNewPollution[seat] ?? 0) + dicePollDelta);
        }

        // Propagate facility removals (Destroy on-hit).
        const diceNewBuiltFacilities = { ...s.builtFacilities };
        const diceNewOwnedBy = { ...s.ownedBy };
        const diceNewMeta = { ...s.builtFacilityMeta };
        let diceHandsAfterTriggers = diceHands;

        const diceRemovedTiles = _extractRemovedTiles(
          diceFacilityOwnershipBefore,
          dicePipelineResult.services.facilityOwnership,
        );
        for (const removedTileIdx of diceRemovedTiles) {
          const ownerSeat = s.ownedBy[removedTileIdx];
          delete diceNewBuiltFacilities[removedTileIdx];
          delete diceNewOwnedBy[removedTileIdx];
          diceNewMeta[removedTileIdx] = undefined;
          if (ownerSeat !== undefined) {
            const trigResult = _dispatchFacilityTrigger(
              diceHandsAfterTriggers,
              ownerSeat as SeatIndex,
              "AfterFacilityDestroyedMine",
            );
            diceHandsAfterTriggers = trigResult.newHands;
            if (trigResult.tokenGain > 0) {
              diceNewTokens[ownerSeat] = (diceNewTokens[ownerSeat] ?? 0) + trigResult.tokenGain;
              console.info(
                `[confirmTilePick:DiceOutcomeAttack] DRF trigger seat ${ownerSeat} +${trigResult.tokenGain}T tile=${removedTileIdx}.`,
              );
            }
          }
        }

        // Propagate ownership transfers (StealFacility on-hit).
        const diceTransferredTiles = _extractTransferredTiles(
          diceFacilityOwnershipBefore,
          dicePipelineResult.services.facilityOwnership,
        );
        for (const { tileIndex: transferredIdx, newOwner } of diceTransferredTiles) {
          diceNewOwnedBy[transferredIdx] = newOwner as SeatIndex;
          console.info(
            `[confirmTilePick:DiceOutcomeAttack] Tile ${transferredIdx} transferred to seat ${newOwner}.`,
          );
        }

        const diceEndState = detectGameEnd(
          {
            tokens: diceNewTokens,
            energy: diceNewEnergy,
            pollution: diceNewPollution,
            round: s.round,
            lostSeats: s.lostSeats,
            builtFacilities: diceNewBuiltFacilities as Record<number, string | undefined>,
            ownedBy: diceNewOwnedBy as Record<number, number | undefined>,
          },
          s.lostSeats,
          s.numPlayers,
        );

        return {
          ...closePicker,
          tokens: diceNewTokens,
          energy: diceNewEnergy,
          pollution: diceNewPollution,
          hands: diceHandsAfterTriggers,
          builtFacilities: diceNewBuiltFacilities,
          ownedBy: diceNewOwnedBy,
          builtFacilityMeta: diceNewMeta,
          auraState: dicePipelineResult.services.aura,
          immunityState: dicePipelineResult.services.immunity,
          peqState: dicePipelineResult.services.peq,
          gameStatus: diceEndState.status,
          endReason: diceEndState.reason,
          winnerSeat: diceEndState.winnerSeat,
          winnerSeats: diceEndState.winnerSeats,
        };
      }

      return closePicker;
    }),

  // ---------------------------------------------------------------------------
  // WEB.4.UI.9c (refactor) — manual aura discard
  // ---------------------------------------------------------------------------

  manualDiscardAura: (auraInstanceId, scope, categoryFilter, facilityFilter) =>
    set((s) => {
      // WEB.4.UI.10a codex R1 MED 1: refuse once ended.
      if (s.gameStatus === "ended") return {};
      // Find the EXACT aura by (instanceId, scope, categoryFilter, facilityFilter).
      // Net_Zero_Movement registers TWO Toll auras differing only by filter; the
      // composite key is required (Net_Zero engine fix codex R1 HIGH 2).
      const seat = s.currentSeat;
      const seatKey = String(seat);
      const seatBucket = s.auraState.bySeat[seatKey] ?? [];
      const idx = seatBucket.findIndex(
        (a) =>
          a.instanceId === auraInstanceId &&
          a.scope === scope &&
          (a.categoryFilter ?? null) === (categoryFilter ?? null) &&
          (a.facilityFilter ?? null) === (facilityFilter ?? null),
      );
      if (idx === -1) {
        console.warn(
          `[manualDiscardAura] No aura with (id=${auraInstanceId}, scope=${scope}, cat=${categoryFilter ?? "any"}, ft=${facilityFilter ?? "any"}) for seat ${seat}.`,
        );
        return {};
      }
      const aura = seatBucket[idx];
      if (aura === undefined) return {};

      // Enforce ownership (defensive — already constrained to currentSeat bucket).
      if (aura.ownerSeat !== seat) {
        console.warn(
          `[manualDiscardAura] Aura ownerSeat=${aura.ownerSeat} != currentSeat=${seat}; refusing.`,
        );
        return {};
      }

      // Enforce discardMode === "Whenever". PassStart auto-decays on pass-start;
      // Permanent/AfterUse/Instantly are not user-discardable.
      const card = getCardById(aura.sourceCardName);
      if (card === undefined || card.discardMode !== "Whenever") {
        console.warn(
          `[manualDiscardAura] Aura source ${aura.sourceCardName} has discardMode=${card?.discardMode ?? "?"}; refusing (only Whenever is manually discardable).`,
        );
        return {};
      }

      // Splice the exact aura out of the seat bucket.
      const newBucket = Object.freeze([...seatBucket.slice(0, idx), ...seatBucket.slice(idx + 1)]);
      const newBySeat = Object.freeze({ ...s.auraState.bySeat, [seatKey]: newBucket });
      return { auraState: Object.freeze({ bySeat: newBySeat }) };
    }),

  // ---------------------------------------------------------------------------
  // Game lifecycle
  // ---------------------------------------------------------------------------

  resetGame: (seed, numPlayers = 4) =>
    set((s) => {
      const newSeed = seed ?? s.rngSeed;
      _reinitRng(newSeed);
      // R21-GD-9: cancel any pending instant-resolve auto-ack timers.
      for (const t of _instantAckTimers.splice(0)) {
        window.clearTimeout(t);
      }
      // R7 #11: stop stale BGM so New Game doesn't inherit the previous session's track.
      resetAudioForNewGame();
      return {
        rngSeed: newSeed,
        // DEFAULT 4 — byte-identical to pre-numPlayers behavior. CharSelect's
        // beginGame(count) locks the real player count at game-start.
        numPlayers,
        hoveredTile: null,
        hoverPos: null,
        activeTile: null,
        selectedTile: null,
        currentSeat: 0,
        playerTiles: [...INITIAL_PLAYER_TILES] as [number, number, number, number],
        ownedBy: {},
        tokens: [...INITIAL_TOKENS] as [number, number, number, number],
        energy: [...INITIAL_ENERGY] as [number, number, number, number],
        pollution: [...INITIAL_POLLUTION] as [number, number, number, number],
        round: 1,
        era: 1 as const,
        lastDiceRoll: null,
        turnPhase: "idle" as const,
        // WEB.4.UI.3 hand reset
        hands: [[], [], [], []] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]],
        detailModalCard: null,
        modalRevealState: "cover" as const,
        handPanelCollapsed: false,
        // WEB.4.UI.4 build menu reset
        buildMenuOpen: false,
        builtFacilities: {} as Record<number, string>,
        // R5 #6 — reset anim-gate so fresh game replays all build anims.
        facilityBuildAnimPlayedTiles: new Set<number>(),
        // WEB.4.UI.11c per-tile meta reset
        builtFacilityMeta: {} as Record<
          number,
          | {
              energyPerTurn: number;
              pollutionPerTurn: number;
              tollPerLand: number;
              builtCost: number;
              builtInEra: 1 | 2 | 3 | 4;
              builtOnTerrain: string;
            }
          | undefined
        >,
        // WEB.4.UI.6 target picker reset
        targetPickerCard: null,
        targetPickerVictimSeat: null,
        // WEB.4.UI.7 tile picker reset
        tilePickerOpen: false,
        tilePickerFilter: null,
        tilePickerMode: "select" as const,
        tilePickerCardId: null,
        selectedPickerTile: null,
        tilePickerIntent: null,
        // WEB.4.UI.8a aura state reset
        auraState: createAuraManagerState(),
        // WEB.4.UI.15 immunity reset (gap #3.A)
        immunityState: createImmunityState(),
        // WEB.4.UI.16 PEQ reset
        peqState: createPendingEffectQueueState(),
        // R22-GD-7 — HalveNextTokenLoss consume notification reset
        lastHalveNextTokenLossUse: null,
        // WEB.4.UI.15 immunity-block notification reset (gap #3.B)
        lastImmunityBlock: null,
        // WEB.4.UI.10a game-end reset
        gameStatus: "playing" as const,
        endReason: null,
        lostSeats: new Set<SeatIndex>(),
        winnerSeat: null,
        winnerSeats: [] as ReadonlyArray<SeatIndex>,
        // WEB.4.R2.CF card-flow rework reset
        pendingDrawCard: null,
        pendingFreeDrawCard: null, // R5 #4
        handFullPendingCard: null,
        cardDrawnToast: null,
        pendingTollPayment: null, // R5 #11
        // Bug #12 — OR-choice reset
        pendingOrChoice: null,
        // R13 Bug #10 — reset policy deck depletion tracker.
        spentPolicyIds: [] as string[],
        // R18-FB-1 — reset event deck depletion tracker.
        spentEventIds: [] as string[],
        // R3 Bug 6/9b reset
        pendingEffectAck: null,
        detailModalCardFromHand: false,
        // WEB.4.UI.12 stolen-card toast reset
        lastStolenCard: null,
        // DD Bug#10b — facility-destroyed toast reset
        lastFacilityDestroyed: null,
        // WEB.4.UI.13 jail + trading reset
        jailTurnsRemaining: [0, 0, 0, 0] as [number, number, number, number],
        bailMultiplier: [0.25, 0.25, 0.25, 0.25] as [number, number, number, number],
        jailEscapeAttempts: [0, 0, 0, 0] as [number, number, number, number],
        bailPaidToast: null,
        jailEscapeUsedThisTurn: [false, false, false, false] as [
          boolean,
          boolean,
          boolean,
          boolean,
        ],
        tradingPromptOpen: false,
        // R4 — jail interaction modal reset
        prisonGuardModalOpen: false,
        inmateEscapeModalOpen: false,
        jailEscapeResult: null,
        pendingJailWarpSeat: null,
        // WEB.4.UI.14 threshold-event banner reset
        lastThresholdEvent: null,
        highestThresholdTierFired: null,
        // R11.2.5 fix #8: reset era-advance cooldown tracker.
        lastEraAdvanceRound: 0,
        // WEB.4.SS.1 scene position reset
        scenePos: { x: 0, y: 0 },
        // R11.2: clear board-walk gate on game reset.
        boardAnimationActive: false,
        // WEB.4.SH (P0.1) — seat-handoff modal reset. Preserve the user's
        // enabled flag across resetGame (it's a mode-level toggle owned by
        // MainMenu, not a per-game value).
        seatHandoffOpen: false,
        seatHandoffFromSeat: null,
        // WEB.4.PM (P0.2) — pause menu reset.
        paused: false,
        // WEB.4.R3.8 — takeover mechanic reset
        takeoverConfirmOpen: false,
        takeoverTileIdx: null,
        takeoverPrevOwner: null,
        takeoverAmounts: null,
        // R7 #8 — insufficient-funds toast reset
        insufficientFundsToast: null,
        // R9 #10 — aura triggered toast reset
        auraTriggeredToast: null,
        // WEB.4.R3.9 — NPC re-interact reset
        npcInteractionState: {},
        // R8 — new feature resets
        handSlotDrawerOpen: false,
        pendingPostCutsceneDialogue: null,
        grandTransitionActive: null,
        // R9 #7: cutsceneGroupId must be cleared on reset so _isGameInputBlocked
        // returns false for the next game's first turn.
        cutsceneGroupId: null,
        cutsceneFrameIdx: 0,
        // R9 — restore default perk assignment on reset.
        playerPerks: ["engineer", "scientist", "activist", "politician"] as [
          "engineer" | "scientist" | "activist" | "politician",
          "engineer" | "scientist" | "activist" | "politician",
          "engineer" | "scientist" | "activist" | "politician",
          "engineer" | "scientist" | "activist" | "politician",
        ],
        // M-1 SDG quiz reset.
        pendingQuiz: null,
        quizCompletedThisRound: {} as Record<string, boolean>,
        quizUsedIndices: new Set<number>(),
        // N-2 — climate vote reset.
        pendingClimateVote: null,
        climateVoteFired: false,
        // N-3 — headline reset.
        currentHeadline: null,
        // T-2 — policy auction reset.
        auctionLandingCount: [0, 0, 0, 0] as [number, number, number, number],
        pendingAuction: null,
        pendingAuctionHandFull: null,
        // T-1 — scout reset.
        pendingScout: null,
        scoutCompletedThisLanding: {} as Record<string, boolean>,
        tourismBonusTilesRemaining: [0, 0, 0, 0] as [number, number, number, number],
        // R16 — minigame pre-pass scaffold reset.
        pendingEnergyDilemma: null,
        energyDilemmaShownThisLanding: {} as Record<string, boolean>,
        recentEnergyDilemmaScenarios: [] as string[], // R21-GF-7 reset
        pendingTrade: null,
        tradeOfferedThisGame: [false, false, false, false] as [boolean, boolean, boolean, boolean],
        tradeOfferRejectionReason: null,
        pendingGiveCard: null,
        pendingPollutionTax: null,
        // CS.4 — Mae cameo reset.
        pendingMaeCameo: null,
        firstQuizShown: false,
        firstClimateVoteShown: false,
        // R20-GD-1 — Drought: reset Hydro-block turn counters.
        hydroBlockTurns: [0, 0, 0, 0] as [number, number, number, number],
        // R20-GD-9 — Chernobyl: clear any stale impact modal.
        pendingChernobylImpact: null,
      };
    }),

  // WEB.4.SS.1 — SidescrollerScene
  scenePos: { x: 0, y: 0 },
  setScenePos: (x, y) => set({ scenePos: { x, y } }),
  // WEB.4.SS.5 — Leave-scene request counter
  sceneLeaveRequestId: 0,
  requestLeaveScene: () => set((s) => ({ sceneLeaveRequestId: s.sceneLeaveRequestId + 1 })),

  // WEB.4.R3.8 — Takeover mechanic initial state + actions
  takeoverConfirmOpen: false,
  takeoverTileIdx: null,
  takeoverPrevOwner: null,
  takeoverAmounts: null,

  openTakeoverConfirm: () =>
    set((s) => {
      const tileIdx = s.playerTiles[s.currentSeat] ?? 0;
      const prevOwner = s.ownedBy[tileIdx];
      if (prevOwner === undefined || prevOwner === s.currentSeat) return {};
      const meta = s.builtFacilityMeta[tileIdx];
      const printedCost = meta?.builtCost ?? 0;
      const baseAmounts = computeTakeoverAmounts(printedCost);
      // R9 perk: Politician (LANDER) pays +25% of burned on top of totalCost.
      const politicianSurcharge =
        s.playerPerks[s.currentSeat] === "politician" ? Math.ceil(baseAmounts.burned * 0.25) : 0;
      const amounts =
        politicianSurcharge > 0
          ? {
              ...baseAmounts,
              totalCost: baseAmounts.totalCost + politicianSurcharge,
              burned: baseAmounts.burned + politicianSurcharge,
            }
          : baseAmounts;
      const balance = s.tokens[s.currentSeat] ?? 0;
      // R7 #8: show toast instead of silent no-op when insufficient funds.
      if (balance < amounts.totalCost) {
        return {
          insufficientFundsToast: {
            needed: amounts.totalCost,
            have: balance,
            action: "takeover" as const,
          },
        };
      }
      return {
        takeoverConfirmOpen: true,
        takeoverTileIdx: tileIdx,
        takeoverPrevOwner: prevOwner as SeatIndex,
        takeoverAmounts: amounts,
      };
    }),

  closeTakeoverConfirm: () =>
    set({
      takeoverConfirmOpen: false,
      takeoverTileIdx: null,
      takeoverPrevOwner: null,
      takeoverAmounts: null,
    }),

  confirmTakeover: () =>
    set((s) => {
      if (_isGameInputBlocked(s)) return {};
      const { takeoverTileIdx, takeoverAmounts, takeoverPrevOwner } = s;
      if (takeoverTileIdx === null || takeoverAmounts === null || takeoverPrevOwner === null)
        return {};
      const lander = s.currentSeat as SeatIndex;
      const landerBalance = s.tokens[lander] ?? 0;
      const ownershipState = _seedFacilityOwnership(s);
      const log = createActionLog();
      const result = _takeoverFacilityEngine(
        ownershipState,
        log,
        lander,
        landerBalance,
        4,
        takeoverTileIdx,
      );
      if (!result.ok) return {};
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[lander] = Math.max(0, (newTokens[lander] ?? 0) - takeoverAmounts.totalCost);
      newTokens[takeoverPrevOwner] =
        (newTokens[takeoverPrevOwner] ?? 0) + takeoverAmounts.prevOwnerCredit;
      const newOwnedBy = { ...s.ownedBy, [takeoverTileIdx]: lander };
      return {
        tokens: newTokens,
        ownedBy: newOwnedBy,
        takeoverConfirmOpen: false,
        takeoverTileIdx: null,
        takeoverPrevOwner: null,
        takeoverAmounts: null,
      };
    }),

  // R7 #8 — Insufficient-funds toast initial state + actions
  insufficientFundsToast: null,
  showInsufficientFundsToast: (needed, have, action) =>
    set({ insufficientFundsToast: { needed, have, action } }),
  dismissInsufficientFundsToast: () => set({ insufficientFundsToast: null }),

  // R9 #10 — AuraTriggeredToast state + actions
  auraTriggeredToast: null,
  showAuraTriggeredToast: (message, sourceCardName) =>
    set({ auraTriggeredToast: { message, sourceCardName } }),
  dismissAuraTriggeredToast: () => set({ auraTriggeredToast: null }),

  // WEB.4.R3.9 — NPC re-interact state + actions
  npcInteractionState: {},
  getNpcInteractionKey: (tileIdx, npcFile) => `${tileIdx}:${npcFile}`,
  markNpcInteracted: (tileIdx, npcFile) =>
    set((s) => {
      const key = `${tileIdx}:${npcFile}`;
      if (s.npcInteractionState[key] === "completed") return {};
      return {
        npcInteractionState: { ...s.npcInteractionState, [key]: "completed" as const },
      };
    }),
  resetNpcInteractionsForTile: (tileIdx) =>
    set((s) => {
      const prefix = `${tileIdx}:`;
      const next: Record<string, "fresh" | "completed"> = {};
      for (const [k, v] of Object.entries(s.npcInteractionState)) {
        if (!k.startsWith(prefix)) next[k] = v;
      }
      return { npcInteractionState: next };
    }),

  // R8 #2 — HandSlotDrawer
  handSlotDrawerOpen: false,
  toggleHandSlotDrawer: () => set((s) => ({ handSlotDrawerOpen: !s.handSlotDrawerOpen })),

  // R8 #6 — Post-cutscene dialogue + grand transition
  pendingPostCutsceneDialogue: null,
  grandTransitionActive: null,
  startPostCutsceneDialogue: (groupId, era) =>
    set((s) => {
      // R8 codex MED: no-op if a dialogue queue is already in-flight OR a
      // grand transition is currently playing. Prevents back-to-back
      // cutscene completions from overwriting the active speaker sequence.
      if (s.pendingPostCutsceneDialogue !== null) {
        console.info(
          `[startPostCutsceneDialogue] skipped — queue already active for ${s.pendingPostCutsceneDialogue.groupId}`,
        );
        return {};
      }
      if (s.grandTransitionActive !== null) {
        console.info("[startPostCutsceneDialogue] skipped — grand transition active");
        return {};
      }
      return {
        pendingPostCutsceneDialogue: {
          groupId,
          era,
          // PRESENT seats speak in order — absent seats (>= numPlayers) must NOT
          // get a dialogue turn in 2/3-player games (they have no real player).
          queue: seatsInGame(s.numPlayers),
        },
      };
    }),
  advancePostCutsceneDialogue: () =>
    set((s) => {
      const d = s.pendingPostCutsceneDialogue;
      if (d === null) return {};
      const remaining = d.queue.slice(1);
      if (remaining.length === 0) {
        // All speakers done — clear dialogue, fire grand transition.
        return {
          pendingPostCutsceneDialogue: null,
          grandTransitionActive: { era: d.era },
        };
      }
      return { pendingPostCutsceneDialogue: { ...d, queue: remaining as SeatIndex[] } };
    }),
  dismissGrandTransition: () =>
    set((s) => {
      // R10 #10 — BGM stall fix: after grand transition, resume era BGM.
      // CutscenePlayer's cleanup called stopBGM(); HexBoard's era/round useEffect
      // won't re-fire because era+round deps haven't changed since endTurn.
      // Call crossfadeBGM explicitly here so the board music always resumes.
      if (!s.audioMuted) {
        crossfadeBGM(bgmKeyForEraRound(s.era, s.round), 800);
      }
      // CS.4 Trigger 1 — Era transition Mae cameo (joyful, SDG 13 fact).
      // Fires every time GrandTransitionOverlay dismisses (i.e. after each
      // era cutscene + dialogue chain). Not one-shot — each era gets its own beat.
      const eraTransitionCameo: GameState["pendingMaeCameo"] = {
        mood: "joyful",
        line: "Each era we adapt — like SDG 13: act on climate, build resilience.",
      };
      // R10 #6 — apply deferred jail warp now that the full
      // cutscene+dialogue+grand-transition sequence is complete.
      if (s.pendingJailWarpSeat !== null) {
        return {
          grandTransitionActive: null,
          pendingJailWarpSeat: null,
          turnPhase: "landed" as const,
          pendingMaeCameo: eraTransitionCameo,
        };
      }
      return { grandTransitionActive: null, pendingMaeCameo: eraTransitionCameo };
    }),

  // ---------------------------------------------------------------------------
  // M-1 SDG Trivia Quiz — initial state + actions
  // ---------------------------------------------------------------------------

  pendingQuiz: null as { questionIdx: number } | null,
  quizCompletedThisRound: {} as Record<string, boolean>,
  quizUsedIndices: new Set<number>(),

  offerQuiz: () =>
    set((s) => {
      // Gate: boardAnimationActive must be false (R11.2 invariant).
      if (s.boardAnimationActive) return {};
      // Gate: only on Event tile during landed phase.
      if (s.turnPhase !== "landed") return {};
      const tileType = _tileTypeByIndex[s.playerTiles[s.currentSeat] ?? 0] ?? "";
      if (tileType !== "Event") return {};
      // Gate: seat hasn't taken quiz this round.
      if (s.quizCompletedThisRound[String(s.currentSeat)]) return {};
      // Gate: no pending quiz already offered.
      if (s.pendingQuiz !== null) return {};
      // R16 pre-pass: mutual-exclude with new minigames.
      if (s.pendingEnergyDilemma !== null) return {};
      if (s.pendingTrade !== null) return {};
      if (s.pendingPollutionTax !== null) return {};
      // Pick a question index, avoiding used ones; wrap around when exhausted.
      const total = (quizBankJson.questions as unknown[]).length;
      let used = s.quizUsedIndices;
      if (used.size >= total) {
        // All used — reset the pool.
        used = new Set<number>();
      }
      let questionIdx = Math.floor(_rng.next() * total);
      // Linear probe to find an unused index.
      let probe = 0;
      while (used.has(questionIdx) && probe < total) {
        questionIdx = (questionIdx + 1) % total;
        probe++;
      }
      // CS.4 Trigger 2 — first quiz cameo (idle, SDG 4 fact). One-shot per game.
      return {
        pendingQuiz: { questionIdx },
        quizUsedIndices: new Set([...used, questionIdx]),
        ...(s.firstQuizShown
          ? {}
          : {
              pendingMaeCameo: {
                mood: "idle" as const,
                line: "SDG 4: quality education helps every generation make better choices.",
              },
              firstQuizShown: true,
            }),
      };
    }),

  answerQuiz: (choiceIdx) =>
    set((s) => {
      if (s.pendingQuiz === null) return {};
      const { questionIdx } = s.pendingQuiz;
      const question = (
        quizBankJson.questions as Array<{
          correctIdx: number;
        }>
      )[questionIdx];
      if (question === undefined) return {};
      const correct = choiceIdx === question.correctIdx;
      let newTokens = s.tokens;
      if (correct) {
        const QUIZ_REWARD = 15;
        const t = [...s.tokens] as [number, number, number, number];
        t[s.currentSeat] = (t[s.currentSeat] ?? 0) + QUIZ_REWARD;
        newTokens = t;
        console.info(
          `[answerQuiz] Seat ${s.currentSeat} answered correctly — +${QUIZ_REWARD} tokens.`,
        );
      } else {
        console.info(`[answerQuiz] Seat ${s.currentSeat} answered incorrectly — no reward.`);
      }
      // BA-1: minigame-result pattern — Builders BX/BY/BZ copy this call into your minigame action.
      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "quiz",
        seat: s.currentSeat,
        outcome: correct ? "correct" : "incorrect",
        tokensDelta: correct ? 15 : 0,
      });
      return {
        tokens: newTokens,
        pendingQuiz: null,
        quizCompletedThisRound: {
          ...s.quizCompletedThisRound,
          [String(s.currentSeat)]: true,
        },
      };
    }),

  skipQuiz: () =>
    set((s) => {
      if (s.pendingQuiz === null) return {};
      return { pendingQuiz: null };
    }),

  triggerEventCardDraw: () => {
    // R13 Bug #9: called from QuizModal "Draw News Card" button.
    // Fires the normal newsgirl event-card-draw flow without requiring a second
    // SidescrollerScene interact. Does NOT re-open QuizModal (pendingQuiz already null).
    const s = useGameStore.getState();
    if (s.boardAnimationActive) return;
    if (_isGameInputBlocked(s)) return;
    s.handleNpcCardDraw(s.currentSeat as SeatIndex, "event");
  },

  // ---------------------------------------------------------------------------
  // T-2 Policy Auction — initial state + actions
  // ---------------------------------------------------------------------------

  auctionLandingCount: [0, 0, 0, 0] as [number, number, number, number],
  pendingAuction: null as GameState["pendingAuction"],
  pendingAuctionHandFull: null as GameState["pendingAuctionHandFull"],

  triggerAuctionIfDue: (seat) => {
    const s = useGameStore.getState();

    // Gates
    if (s.boardAnimationActive) return false;
    if (_isGameInputBlocked(s)) return false;
    if (s.gameStatus === "ended") return false;
    if (s.pendingAuction !== null) return false;
    if (s.pendingQuiz !== null) return false;
    if (s.pendingClimateVote !== null) return false;
    // R16 pre-pass: mutual-exclude with new minigames.
    if (s.pendingEnergyDilemma !== null) return false;
    if (s.pendingTrade !== null) return false;
    if (s.pendingPollutionTax !== null) return false;

    // Increment counter
    const newCount = [...s.auctionLandingCount] as [number, number, number, number];
    newCount[seat] = (newCount[seat] ?? 0) + 1;
    useGameStore.setState({ auctionLandingCount: newCount });

    // Auction triggers when count hits a multiple of 3 (3rd, 6th, 9th…)
    if (newCount[seat] % 3 !== 0) return false;

    // DD Bug#9a: draw 1 card (spec: all players bid on the same single card)
    // R18-FA-1: filter by spentPolicyIds so the same policy can't appear in auction twice.
    const allPolicyPool = _cardsByType("Policy", s.era);
    if (allPolicyPool.length < 1) return false;

    const spentAuctionSet = new Set(s.spentPolicyIds);
    const auctionRemaining = allPolicyPool.filter((c) => !spentAuctionSet.has(c.name));
    // If pool is exhausted, reshuffle (use full pool) — same pattern as drawCard.
    const auctionPool = auctionRemaining.length > 0 ? auctionRemaining : allPolicyPool;
    const nextSpentAuction = auctionRemaining.length > 0 ? s.spentPolicyIds : ([] as string[]);

    const idx0 = _rng.nextInt(0, auctionPool.length - 1);
    // biome-ignore lint/style/noNonNullAssertion: idx0 is within auctionPool bounds
    const auctionPicked = auctionPool[idx0]!;
    const auctionCards: [CardInHand] = [
      { id: auctionPicked.name, instanceId: String(_rng.nextInt(0, 2 ** 31 - 1)) },
    ];

    // Not-in-game seats (>= numPlayers) are pre-marked as forfeit (sentinel -1) so the
    // modal's bids.every(b=>b!==null) auto-reveal completes without waiting on them, and the
    // winner search (amount<0 → skip) never awards them. Default 4 → all four start null,
    // byte-identical to before. activeBidder starts at 0 (always present, numPlayers>=2).
    type BidSlot = { amount: number } | null;
    const forfeitIfAbsent = (seatIdx: number): BidSlot =>
      seatIdx >= s.numPlayers ? { amount: -1 } : null;
    const initialBids: [BidSlot, BidSlot, BidSlot, BidSlot] = [
      forfeitIfAbsent(0),
      forfeitIfAbsent(1),
      forfeitIfAbsent(2),
      forfeitIfAbsent(3),
    ];

    // R18-FA-1: mark the auctioned card spent regardless of outcome (win or forfeit).
    useGameStore.setState({
      spentPolicyIds: [...nextSpentAuction, auctionPicked.name],
      pendingAuction: {
        tier: s.era,
        cards: auctionCards,
        deadline: Date.now() + 30_000,
        drawerSeat: seat,
        activeBidder: 0 as SeatIndex,
        bids: initialBids,
        revealed: false,
      },
    });

    return true;
  },

  submitAuctionBid: (amount) =>
    set((s) => {
      if (s.pendingAuction === null || s.pendingAuction.revealed) return {};
      if (_isGameInputBlocked(s)) return {};
      // Codex Phase 2b HIGH 1 fix: bidder identity comes from pendingAuction.activeBidder,
      // NOT s.currentSeat (which stays as the drawer for the whole turn). Without
      // this, the auction would deadlock on bid #2 because currentSeat !== activeSeat.
      const activeSeat = s.pendingAuction.activeBidder;
      // DD Bug#9b: guard — if this seat already bid, ignore (double-submit race guard).
      if (s.pendingAuction.bids[activeSeat] !== null) return {};
      const balance = s.tokens[activeSeat] ?? 0;
      const clampedAmount = Math.max(0, Math.min(amount, balance));
      const newBids = [...s.pendingAuction.bids] as typeof s.pendingAuction.bids;
      newBids[activeSeat] = { amount: clampedAmount };
      // DD Bug#9b: do NOT advance activeBidder here — passAuctionTurn does that
      // so the "Pass Device" button appears before the device changes hands.
      return {
        pendingAuction: { ...s.pendingAuction, bids: newBids },
      };
    }),

  skipAuctionBid: () =>
    set((s) => {
      if (s.pendingAuction === null || s.pendingAuction.revealed) return {};
      if (_isGameInputBlocked(s)) return {};
      // Codex Phase 2b HIGH 1 fix: same as submitAuctionBid — bidder = activeBidder.
      const activeSeat = s.pendingAuction.activeBidder;
      // DD Bug#9b: guard — if this seat already bid, ignore.
      if (s.pendingAuction.bids[activeSeat] !== null) return {};
      // sentinel -1 = skipped (forfeit; never wins even if "highest")
      const newBids = [...s.pendingAuction.bids] as typeof s.pendingAuction.bids;
      newBids[activeSeat] = { amount: -1 };
      // DD Bug#9b: do NOT advance activeBidder here — passAuctionTurn does that.
      return {
        pendingAuction: { ...s.pendingAuction, bids: newBids },
      };
    }),

  passAuctionTurn: () =>
    set((s) => {
      if (s.pendingAuction === null || s.pendingAuction.revealed) return {};
      // DD Bug#9b: passAuctionTurn is now the ONLY place that advances activeBidder.
      // Guard: current activeBidder must have already bid (submit or skip).
      const activeSeat = s.pendingAuction.activeBidder;
      if (s.pendingAuction.bids[activeSeat] === null) {
        console.warn(
          `[passAuctionTurn] Seat ${activeSeat} has not submitted or skipped yet — ignoring pass.`,
        );
        return {};
      }
      // DD Bug#9c: skip jailed seats automatically (they cannot bid).
      // Advance to next non-jailed seat that hasn't bid yet (or stop at all-bid).
      // Codex R17d HIGH fix: auto-record jail-skip into bids array (sentinel -1)
      // before continuing the scan, so jailed seats don't end up as activeBidder
      // with no bid recorded (which deadlocked the auction).
      const newBids = [...s.pendingAuction.bids] as typeof s.pendingAuction.bids;
      let nextBidder = ((activeSeat + 1) % 4) as SeatIndex;
      let iterations = 0;
      // Skip seats that already bid, jailed seats, AND not-in-game seats (>= numPlayers).
      // Default 4 → no seat is "absent", byte-identical. (Absent seats are also pre-filled
      // non-null at auction-fire time, so the `!== null` clause already covers them; this
      // explicit guard is defense-in-depth.)
      while (
        iterations < 4 &&
        (nextBidder >= s.numPlayers ||
          newBids[nextBidder] !== null ||
          (s.jailTurnsRemaining[nextBidder] ?? 0) > 0)
      ) {
        if ((s.jailTurnsRemaining[nextBidder] ?? 0) > 0 && newBids[nextBidder] === null) {
          newBids[nextBidder] = { amount: -1 };
        }
        nextBidder = ((nextBidder + 1) % 4) as SeatIndex;
        iterations++;
      }
      // R18-FA-2: reset deadline for each player turn (30s per-player, not global).
      return {
        pendingAuction: {
          ...s.pendingAuction,
          bids: newBids,
          activeBidder: nextBidder,
          deadline: Date.now() + 30_000,
        },
      };
    }),

  revealAuction: () =>
    set((s) => {
      if (s.pendingAuction === null || s.pendingAuction.revealed) return {};

      const { bids, cards, drawerSeat } = s.pendingAuction;

      // Find winner: highest bid (amount >= 0); sentinel -1 = forfeit.
      // Ties → drawerSeat wins.
      let winnerSeat: SeatIndex | null = null;
      let winnerBid = -1;
      for (let i = 0; i < 4; i++) {
        const bid = bids[i as 0 | 1 | 2 | 3];
        if (bid == null || bid.amount < 0) continue;
        if (bid.amount > winnerBid) {
          winnerBid = bid.amount;
          winnerSeat = i as SeatIndex;
        } else if (bid.amount === winnerBid) {
          if ((i as SeatIndex) === drawerSeat) winnerSeat = i as SeatIndex;
        }
      }

      const revealedAuction = { ...s.pendingAuction, revealed: true };

      if (winnerSeat === null) {
        return { pendingAuction: revealedAuction };
      }

      // Codex Phase 2b HIGH 2 fix: hand-full edge — existing HandFullModal
      // resolves against currentSeat, NOT the auction winner. Threading
      // winner-seat through HandFullModal would be invasive; pragmatic call:
      // if winner's hand is full, the card is FORFEIT (no card, no payment,
      // no aura registered). Bid is NOT debited; aura registration deferred
      // until card actually lands in hand below.
      const wonCard = cards[0];
      const winnerHand = s.hands[winnerSeat] ?? [];

      if (winnerHand.length >= MAX_HAND_SIZE) {
        // R20-GE-10: hand full — set pendingAuctionHandFull so HandFullModal offers swap.
        // Debit + aura registration deferred until commitAuctionHandFull resolves.
        console.info(
          `[revealAuction] Winner seat ${winnerSeat} hand full (${winnerHand.length}/${MAX_HAND_SIZE}). Opening auction hand-full swap modal.`,
        );
        return {
          pendingAuction: revealedAuction,
          pendingAuctionHandFull: {
            winnerSeat,
            wonCard,
            bidAmount: winnerBid,
          },
        };
      }

      // R18-FA-3: NextPaidDrawFree waives the winner's bid (per-seat only, NO bySeat[-1]).
      // Only applies when winner has a NextPaidDrawFree PEQ entry — consumed on use.
      const peqLog = createActionLog();
      const freeDrawResult = tryConsumePending(
        s.peqState,
        peqLog,
        winnerSeat,
        "NextPaidDrawFree",
        null,
        null,
      );
      const effectiveBid = freeDrawResult.ok ? 0 : winnerBid;
      const peqAfterFree = freeDrawResult.ok ? freeDrawResult.newState : s.peqState;

      // Debit winner's tokens (only when card actually enters hand; waived if NextPaidDrawFree)
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[winnerSeat] = Math.max(0, (newTokens[winnerSeat] ?? 0) - effectiveBid);

      // Register auras if applicable (only when card actually enters hand)
      const wonCardDef = getCardById(wonCard.id);
      const nextAura =
        wonCardDef !== undefined && _isAuraCard(wonCardDef)
          ? _autoRegisterCardAuras(s.auraState, wonCardDef, winnerSeat, wonCard.instanceId)
          : s.auraState;
      // Bug #14: Whenever BuildCostMod cards must enqueue PEQ on hand entry.
      // Chain off peqAfterFree so the NextPaidDrawFree consumption is preserved.
      const nextPeqAuction =
        wonCardDef !== undefined
          ? _enqueueWheneverBuildCostMod(peqAfterFree, wonCardDef, winnerSeat, wonCard.instanceId)
          : peqAfterFree;

      const newHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      newHands[winnerSeat] = [...winnerHand, wonCard];
      return {
        pendingAuction: revealedAuction,
        tokens: newTokens,
        hands: newHands,
        auraState: nextAura,
        peqState: nextPeqAuction,
      };
    }),

  closeAuction: () =>
    set((s) => {
      if (s.pendingAuction === null) return {};
      const nextNpc = _markCardDrawNpcCompleted(
        s.npcInteractionState,
        s.currentSeat,
        s.playerTiles,
      );
      return { pendingAuction: null, npcInteractionState: nextNpc };
    }),

  // R20-GE-10 — commitAuctionHandFull
  commitAuctionHandFull: (action) =>
    set((s) => {
      const ahf = s.pendingAuctionHandFull;
      if (ahf === null) return {};
      const { winnerSeat, wonCard, bidAmount } = ahf;
      const winnerHand = s.hands[winnerSeat] ?? [];

      if (action === "forfeit") {
        console.info(`[commitAuctionHandFull] Seat ${winnerSeat} forfeited auction win. No debit.`);
        return { pendingAuctionHandFull: null };
      }

      const replaceIdx = action as number;
      if (replaceIdx < 0 || replaceIdx >= winnerHand.length) {
        console.warn("[commitAuctionHandFull] replaceIdx out of range:", replaceIdx);
        return {};
      }

      // Debit bid (may be 0 if NextPaidDrawFree consumed — but we don't re-consume
      // PEQ here since revealAuction skipped it when hand was full; charge full bid).
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[winnerSeat] = Math.max(0, (newTokens[winnerSeat] ?? 0) - bidAmount);

      // Swap slot
      const evictedCard = winnerHand[replaceIdx];
      const newHand = [...winnerHand];
      newHand[replaceIdx] = wonCard;
      const newHands = [...s.hands] as [CardInHand[], CardInHand[], CardInHand[], CardInHand[]];
      newHands[winnerSeat] = newHand;

      // Aura + PEQ housekeeping (mirrors resolveHandFull)
      const wonCardDef = getCardById(wonCard.id);
      const evictedDef = evictedCard !== undefined ? getCardById(evictedCard.id) : undefined;
      let nextPeq = s.peqState;
      if (evictedDef !== undefined && evictedCard !== undefined) {
        nextPeq = _dequeueWheneverBuildCostMod(
          nextPeq,
          evictedDef,
          winnerSeat,
          evictedCard.instanceId,
        );
      }
      if (wonCardDef !== undefined) {
        nextPeq = _enqueueWheneverBuildCostMod(nextPeq, wonCardDef, winnerSeat, wonCard.instanceId);
      }
      let nextAura = s.auraState;
      if (wonCardDef !== undefined && _isAuraCard(wonCardDef)) {
        nextAura = _autoRegisterCardAuras(nextAura, wonCardDef, winnerSeat, wonCard.instanceId);
      }
      if (evictedCard !== undefined) {
        nextAura = _autoRemoveCardAuras(nextAura, evictedCard.instanceId);
      }

      console.info(
        `[commitAuctionHandFull] Seat ${winnerSeat} replaced slot ${replaceIdx} with ${wonCard.id}. Bid ${bidAmount} debited.`,
      );
      return {
        pendingAuctionHandFull: null,
        tokens: newTokens,
        hands: newHands,
        peqState: nextPeq,
        auraState: nextAura,
      };
    }),

  // ---------------------------------------------------------------------------
  // T-1 Universal Scout-the-Tile — initial state + actions
  // ---------------------------------------------------------------------------

  pendingScout: null as GameState["pendingScout"],
  scoutCompletedThisLanding: {} as Record<string, boolean>,
  tourismBonusTilesRemaining: [0, 0, 0, 0] as [number, number, number, number],

  // R16 pre-pass scaffold (builders BX/BY/BZ fill actions; fields exist now
  // so the 4 existing trigger guards reference them without merge conflict).
  pendingEnergyDilemma: null as GameState["pendingEnergyDilemma"],
  energyDilemmaShownThisLanding: {} as Record<string, boolean>,
  recentEnergyDilemmaScenarios: [] as string[], // R21-GF-7: last-3 seen for RNG exclusion
  pendingTrade: null as GameState["pendingTrade"],
  tradeOfferedThisGame: [false, false, false, false] as [boolean, boolean, boolean, boolean],
  tradeOfferRejectionReason: null as GameState["tradeOfferRejectionReason"],
  pendingGiveCard: null as GameState["pendingGiveCard"],
  pendingPollutionTax: null as GameState["pendingPollutionTax"],

  offerScout: () =>
    set((s) => {
      if (s.boardAnimationActive) return {};
      if (_isGameInputBlocked(s)) return {};
      if (s.gameStatus === "ended") return {};
      if (s.turnPhase !== "landed") return {};
      const seat = s.currentSeat;
      const tileIdx = s.playerTiles[seat] ?? 0;
      const terrain = _tileTypeByIndex[tileIdx] ?? "";
      if (!SCOUT_TERRAIN_TYPES.has(terrain)) return {};
      const landingKey = `${tileIdx}:${seat}`;
      if (s.scoutCompletedThisLanding[landingKey]) return {};
      if (s.pendingScout !== null) return {};
      // R16 pre-pass: mutual-exclude with new minigames.
      if (s.pendingEnergyDilemma !== null) return {};
      if (s.pendingTrade !== null) return {};
      if (s.pendingPollutionTax !== null) return {};
      return { pendingScout: { tileIdx, terrain, roll: null, outcome: null } };
    }),

  // ---------------------------------------------------------------------------
  // T-1 R19-GB-4: Two-step scout flow.
  //   Step 1 — payScoutFee: deducts fee + rolls + stores result in pendingScout.
  //   Step 2 — applyScoutOutcome: applies stored result + clears pendingScout.
  // ---------------------------------------------------------------------------

  payScoutFee: () =>
    set((s) => {
      if (s.pendingScout === null) return {};
      if (_isGameInputBlocked(s)) return {};
      if (s.gameStatus === "ended") return {};
      // Prevent double-pay: if fee was already deducted, roll is already set.
      // Use != null (loose) to catch both null and undefined (backward compat
      // with test setState calls that omit the roll field).
      if (s.pendingScout.roll != null) return {};
      const seat = s.currentSeat;
      const cost = scoutCostForEra(s.era);
      const balance = s.tokens[seat] ?? 0;
      if (balance < cost) {
        console.warn(`[payScoutFee] Seat ${seat} cannot afford ${cost}T (balance=${balance}).`);
        return {};
      }
      // Deduct fee immediately.
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[seat] = balance - cost;
      // Roll d6 and resolve outcome (stored; not applied yet).
      const roll = _rng.nextInt(1, 6);
      const outcome = _scoutOutcomeForRoll(s.pendingScout.terrain, roll);
      console.info(
        `[payScoutFee] Seat ${seat} terrain=${s.pendingScout.terrain} fee=${cost}T roll=${roll} outcome="${outcome === "nothing" ? "nothing" : outcome.text}"`,
      );
      return {
        tokens: newTokens,
        pendingScout: { ...s.pendingScout, roll, outcome },
      };
    }),

  applyScoutOutcome: () =>
    set((s) => {
      if (s.pendingScout === null) return {};
      // Use == null (loose) to catch both null and undefined.
      if (s.pendingScout.roll == null || s.pendingScout.outcome == null) return {};
      const seat = s.currentSeat;
      const { tileIdx, terrain, outcome } = s.pendingScout;

      const newTokens = [...s.tokens] as [number, number, number, number];
      const newPollution = [...s.pollution] as [number, number, number, number];
      let newPeq = s.peqState;
      const newTourism = [...s.tourismBonusTilesRemaining] as [number, number, number, number];
      let policyDiscountApplied = false;
      // R21-GF-6: era scaling for scout outcome deltas.
      const _scoutEra = Math.max(1, Math.min(4, s.era)) as 1 | 2 | 3 | 4;
      const _scoutScale = _eraScale(_scoutEra);

      if (outcome !== "nothing") {
        // Token delta (positive or negative) — scaled by era
        if (typeof outcome.tokens === "number") {
          const scaled = Math.round(outcome.tokens * _scoutScale);
          if (scaled >= 0) {
            newTokens[seat] = (newTokens[seat] ?? 0) + scaled;
          } else {
            newTokens[seat] = Math.max(0, (newTokens[seat] ?? 0) + scaled);
          }
        }
        // Pollution delta — scaled by era (negative = cleanup scales too)
        if (typeof outcome.pollution === "number") {
          const cur = newPollution[seat] ?? 0;
          const scaledPoll = Math.round(outcome.pollution * _scoutScale);
          newPollution[seat] = Math.max(0, cur + scaledPoll);
        }
        // Nearest opponent penalty
        if (typeof outcome.opponentPenalty === "number" && outcome.opponentPenalty < 0) {
          const opponentSeat = _resolveNearestPlayerSeat(
            s.playerTiles,
            seat as SeatIndex,
            s.lostSeats,
            s.numPlayers,
          );
          if (opponentSeat !== (-1 as SeatIndex)) {
            newTokens[opponentSeat] = Math.max(
              0,
              (newTokens[opponentSeat] ?? 0) + outcome.opponentPenalty,
            );
          }
        }
        // Queue discount into PEQ.
        // Codex Phase 2b HIGH 3 fix: BuildMenu's discount picker (R11.3) selects
        // by PendingKind only — multiple NextBuildDiscount entries collide and
        // the wrong one can be previewed/consumed. To avoid the ambiguity, when
        // the player already has a NextBuildDiscount queued from a previous
        // scout, we DO NOT queue another one — convert it to a flat token gain
        // (5T per discount-N) so the educational beat still happens. This keeps
        // the picker's PendingKind-based logic correct without a deeper refactor.
        if (typeof outcome.queueDiscount === "string") {
          const qd = outcome.queueDiscount;
          const log = createActionLog();
          const existing = peekIncludingGlobal(newPeq, seat);
          const hasNextBuildDiscount = existing.some((p) => p.kind === "NextBuildDiscount");
          const hasNextSolarFree = existing.some((p) => p.kind === "NextSolarFree");
          if (qd === "NextSolarFree") {
            if (hasNextSolarFree) {
              newTokens[seat] = (newTokens[seat] ?? 0) + 10;
              console.info(
                `[applyScoutOutcome] Seat ${seat}: NextSolarFree already queued; converted to +10T.`,
              );
            } else {
              const res = enqueuePending(newPeq, log, {
                ownerSeat: seat,
                kind: "NextSolarFree",
                valueInt: 0,
                sourceCardName: "ScoutReward",
                instanceId: 0,
                categoryFilter: null,
                facilityFilter: null,
              });
              newPeq = res.newState;
            }
          } else if (
            qd.startsWith("NextBuildDiscount:") ||
            qd.startsWith("NextGeothermalDiscount:")
          ) {
            const isGeothermal = qd.startsWith("NextGeothermalDiscount:");
            const discVal = Number(qd.split(":")[1] ?? (isGeothermal ? "10" : "1"));
            if (hasNextBuildDiscount) {
              newTokens[seat] = (newTokens[seat] ?? 0) + Math.max(1, discVal);
              console.info(
                `[applyScoutOutcome] Seat ${seat}: NextBuildDiscount already queued; converted to +${discVal}T.`,
              );
            } else {
              const res = enqueuePending(newPeq, log, {
                ownerSeat: seat,
                kind: "NextBuildDiscount",
                valueInt: discVal,
                sourceCardName: "ScoutReward",
                instanceId: 0,
                categoryFilter: isGeothermal ? "Renewable" : null,
                facilityFilter: isGeothermal ? "Geothermal" : null,
              });
              newPeq = res.newState;
            }
          }
        }
        // Tourism bonus (Mountain 6): next N landings give +3T
        if (typeof outcome.tourismBonus === "number" && outcome.tourismBonus > 0) {
          newTourism[seat] = (newTourism[seat] ?? 0) + outcome.tourismBonus;
        }
        // policyDiscount applied as plain token gain until PolicyAuction implements a
        // per-card discount hook. SDG educational text still surfaces in ScoutModal.
        if (typeof outcome.policyDiscount === "number" && outcome.policyDiscount > 0) {
          newTokens[seat] = (newTokens[seat] ?? 0) + outcome.policyDiscount;
          policyDiscountApplied = true;
        }
      }

      const landingKey = `${tileIdx}:${seat}`;
      const scoutNpcKey = `${tileIdx}:scout-e1.png`;
      const _scoutTokenDelta = (newTokens[seat] ?? 0) - (s.tokens[seat] ?? 0);
      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "scout",
        seat,
        outcome: outcome === "nothing" ? "nothing" : outcome.text,
        tokensDelta: _scoutTokenDelta,
      });
      console.info(
        `[applyScoutOutcome] Seat ${seat} terrain=${terrain} outcome="${outcome === "nothing" ? "nothing" : outcome.text}"${policyDiscountApplied ? " (policyDiscount→tokenFallback)" : ""}`,
      );
      return {
        tokens: newTokens,
        pollution: newPollution,
        peqState: newPeq,
        tourismBonusTilesRemaining: newTourism,
        pendingScout: null,
        scoutCompletedThisLanding: { ...s.scoutCompletedThisLanding, [landingKey]: true },
        npcInteractionState: { ...s.npcInteractionState, [scoutNpcKey]: "completed" as const },
      };
    }),

  /**
   * Compatibility wrapper: deducts fee + applies outcome in one shot.
   * Existing tests (AL-6, AL-7) and any non-modal callers continue to work.
   */
  executeScout: () => {
    useGameStore.getState().payScoutFee();
    useGameStore.getState().applyScoutOutcome();
  },

  cancelScout: () =>
    set((s) => {
      if (s.pendingScout === null) return {};
      return { pendingScout: null };
    }),

  // ---------------------------------------------------------------------------
  // N-1 Energy Dilemma actions (R16 Builder-BX)
  // ---------------------------------------------------------------------------

  triggerEnergyDilemmaIfEligible: (seat) => {
    const s = useGameStore.getState();
    // Conflict matrix (spec §0.1 rule 6): boardAnimationActive →
    // _isGameInputBlocked → gameStatus → turnPhase → era → terrain →
    // no pending* → not already shown this landing.
    if (s.boardAnimationActive) return;
    if (_isGameInputBlocked(s)) return;
    if (s.gameStatus === "ended") return;
    if (s.turnPhase !== "landed") return;
    if (s.era < 2) return;
    const tileIdx = s.playerTiles[seat] ?? 0;
    const terrain = _tileTypeByIndex[tileIdx] ?? "";
    if (!SCOUT_TERRAIN_TYPES.has(terrain)) return;
    // Mutual-exclusion with ALL other modals / pending* fields.
    if (s.pendingQuiz !== null) return;
    if (s.pendingAuction !== null) return;
    if (s.pendingClimateVote !== null) return;
    if (s.pendingScout !== null) return;
    if (s.pendingEnergyDilemma !== null) return;
    if (s.pendingTrade !== null) return;
    if (s.pendingPollutionTax !== null) return;
    // Per-landing one-shot gate.
    const landingKey = `${tileIdx}:${seat}`;
    if (s.energyDilemmaShownThisLanding[landingKey]) return;
    // R21-GF-7: RNG-driven scenario, excluding last 3 seen.
    const optionsKey = _pickEnergyDilemmaScenario(s.recentEnergyDilemmaScenarios);
    const nextRecent = [optionsKey, ...s.recentEnergyDilemmaScenarios].slice(0, 3);
    useGameStore.setState({
      pendingEnergyDilemma: { tileIdx, terrain, optionsKey },
      recentEnergyDilemmaScenarios: nextRecent,
    });
  },

  offerEnergyDilemma: () => {
    // Thin alias — canonical path is triggerEnergyDilemmaIfEligible.
    const s = useGameStore.getState();
    useGameStore.getState().triggerEnergyDilemmaIfEligible(s.currentSeat as SeatIndex);
  },

  submitEnergyDilemma: (choiceIdx) =>
    set((s) => {
      if (s.pendingEnergyDilemma === null) return {};
      const { tileIdx, optionsKey } = s.pendingEnergyDilemma;
      const seat = s.currentSeat;
      const option =
        ENERGY_DILEMMA_OPTIONS[optionsKey as keyof typeof ENERGY_DILEMMA_OPTIONS]?.[choiceIdx];
      if (option === undefined) {
        console.error(`[submitEnergyDilemma] Invalid choiceIdx ${choiceIdx} for ${optionsKey}`);
        return { pendingEnergyDilemma: null };
      }

      // R21-GF-6: era scaling for energy dilemma deltas (tokens + pollution scale; energy does not).
      const _edEra = Math.max(1, Math.min(4, s.era)) as 1 | 2 | 3 | 4;
      const _edScale = _eraScale(_edEra);
      const scaledTokens = Math.round(option.tokens * _edScale);
      const scaledPollution = Math.round(option.pollution * _edScale);

      // Apply deltas — clamp at 0, no negative balances/energy.
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[seat] = Math.max(0, (newTokens[seat] ?? 0) + scaledTokens);

      const newPollution = [...s.pollution] as [number, number, number, number];
      newPollution[seat] = Math.max(0, (newPollution[seat] ?? 0) + scaledPollution);

      const newEnergy = [...s.energy] as [number, number, number, number];
      newEnergy[seat] = Math.max(0, (newEnergy[seat] ?? 0) + option.energy);

      const landingKey = `${tileIdx}:${seat}`;
      console.info(
        `[submitEnergyDilemma] Seat ${seat} era=${s.era} scale=×${_edScale} scenario=${optionsKey} choice=${choiceIdx} label="${option.label}" Δtokens=${scaledTokens} (base ${option.tokens}) Δpollution=${scaledPollution} (base ${option.pollution}) Δenergy=${option.energy}`,
      );

      // R16 /goal Fix A: emit analytics event for N-1 result.
      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "energy-dilemma",
        seat,
        outcome: `${optionsKey}:${option.label}`,
        tokensDelta: scaledTokens,
      });

      return {
        tokens: newTokens,
        pollution: newPollution,
        energy: newEnergy,
        pendingEnergyDilemma: null,
        energyDilemmaShownThisLanding: {
          ...s.energyDilemmaShownThisLanding,
          [landingKey]: true,
        },
      };
    }),

  // ---------------------------------------------------------------------------
  // T-3 Trade Window actions (R16 Builder-BY)
  // ---------------------------------------------------------------------------

  offerTrade: () =>
    set((s) => {
      // Conflict matrix (spec §0.1 rule 6): boardAnimationActive →
      // _isGameInputBlocked → gameStatus → turnPhase → tile=Trading →
      // drawer-hand>=2 → eligible-opponent → no pending* → one-shot.
      if (s.boardAnimationActive) {
        console.warn("[offerTrade] blocked: boardAnimationActive");
        return { tradeOfferRejectionReason: "Wait for the board to settle" };
      }
      if (_isGameInputBlocked(s)) {
        console.warn("[offerTrade] blocked: _isGameInputBlocked");
        return { tradeOfferRejectionReason: "Wait for the board to settle" };
      }
      if (s.gameStatus === "ended") {
        console.warn("[offerTrade] blocked: game ended");
        return { tradeOfferRejectionReason: "Game is over" };
      }
      if (s.turnPhase !== "landed") {
        console.warn(`[offerTrade] blocked: turnPhase=${s.turnPhase} (need 'landed')`);
        return { tradeOfferRejectionReason: "Wait for the board to settle" };
      }
      const seat = s.currentSeat;
      const tileType = _tileTypeByIndex[s.playerTiles[seat] ?? 0] ?? "";
      if (tileType !== "Trading") {
        console.warn(`[offerTrade] blocked: tile is '${tileType}' not 'Trading' (seat ${seat})`);
        return { tradeOfferRejectionReason: "Card swaps are only available on a Trading tile" };
      }
      // R20-GE-8: lowered minimum from 2 to 1 (1-for-1 swap is valid).
      if ((s.hands[seat]?.length ?? 0) < 1) {
        console.warn(`[offerTrade] blocked: seat ${seat} hand is empty`);
        return { tradeOfferRejectionReason: "You need at least 1 card to offer a swap" };
      }
      // At least one opponent: present (< numPlayers), hand>=1 and not bankrupt.
      const hasEligibleOpponent = ([0, 1, 2, 3] as SeatIndex[]).some(
        (opp) =>
          opp !== seat &&
          opp < s.numPlayers &&
          !s.lostSeats.has(opp) &&
          (s.hands[opp]?.length ?? 0) >= 1,
      );
      if (!hasEligibleOpponent) {
        console.warn(`[offerTrade] No eligible opponent for seat ${seat}.`);
        return {
          tradeOfferRejectionReason: "No opponent has cards to swap with",
        };
      }
      // Mutual-exclusion with all other pending*.
      if (s.pendingScout !== null) {
        console.warn("[offerTrade] blocked: pendingScout active");
        return { tradeOfferRejectionReason: "Close the scouting window first" };
      }
      if (s.pendingQuiz !== null) {
        console.warn("[offerTrade] blocked: pendingQuiz active");
        return { tradeOfferRejectionReason: "Close the quiz window first" };
      }
      if (s.pendingAuction !== null) {
        console.warn("[offerTrade] blocked: pendingAuction active");
        return { tradeOfferRejectionReason: "Close the auction window first" };
      }
      if (s.pendingClimateVote !== null) {
        console.warn("[offerTrade] blocked: pendingClimateVote active");
        return { tradeOfferRejectionReason: "Close the climate vote window first" };
      }
      if (s.pendingEnergyDilemma !== null) {
        console.warn("[offerTrade] blocked: pendingEnergyDilemma active");
        return { tradeOfferRejectionReason: "Close the energy dilemma window first" };
      }
      if (s.pendingTrade !== null) {
        console.warn("[offerTrade] blocked: pendingTrade already active");
        return { tradeOfferRejectionReason: "A card swap is already in progress" };
      }
      if (s.pendingPollutionTax !== null) {
        console.warn("[offerTrade] blocked: pendingPollutionTax active");
        return { tradeOfferRejectionReason: "Close the pollution tax window first" };
      }
      // One-shot per game per drawer.
      if (s.tradeOfferedThisGame[seat]) {
        console.warn(`[offerTrade] Seat ${seat} already offered trade this game.`);
        return {
          tradeOfferRejectionReason: "You've already used your card swap this game",
        };
      }
      return {
        pendingTrade: {
          offerSeat: seat,
          responderSeat: null,
          offeredInstanceIds: null,
          requestedInstanceIds: null,
          phase: "select" as const,
          decision: null,
        },
        // DD Bug#4: close TradingPrompt when trade window opens (was staying visible
        // at z:220 underneath TradeWindowModal at z:1300, blocking interaction)
        tradingPromptOpen: false,
        // R19-GC-2: clear rejection reason on successful offer
        tradeOfferRejectionReason: null,
      };
    }),

  commitTradeOffer: (offered, responderSeat, requested) =>
    set((s) => {
      if (s.pendingTrade === null) return {};
      if (s.pendingTrade.phase !== "select") return {};
      const { offerSeat } = s.pendingTrade;
      // Validate: offered cards must be in offerSeat's hand.
      const drawerHand = s.hands[offerSeat] ?? [];
      const [oid1, oid2] = offered;
      const has1 = drawerHand.some((c) => c.instanceId === oid1);
      const has2 = drawerHand.some((c) => c.instanceId === oid2);
      if (!has1 || !has2) {
        console.warn("[commitTradeOffer] Offered card(s) not in drawer hand.");
        return {};
      }
      // Validate: requested cards must be in responderSeat's hand.
      const responderHand = s.hands[responderSeat] ?? [];
      const [rid1, rid2] = requested;
      const has3 = responderHand.some((c) => c.instanceId === rid1);
      const has4 = responderHand.some((c) => c.instanceId === rid2);
      if (!has3 || !has4) {
        console.warn("[commitTradeOffer] Requested card(s) not in responder hand.");
        return {};
      }
      return {
        pendingTrade: {
          ...s.pendingTrade,
          responderSeat,
          offeredInstanceIds: offered,
          requestedInstanceIds: requested,
          phase: "respond" as const,
        },
      };
    }),

  acceptTrade: () =>
    set((s) => {
      if (s.pendingTrade === null) return {};
      if (s.pendingTrade.phase !== "respond") return {};
      const { offerSeat, responderSeat, offeredInstanceIds, requestedInstanceIds } = s.pendingTrade;
      if (responderSeat === null || offeredInstanceIds === null || requestedInstanceIds === null) {
        console.warn("[acceptTrade] pendingTrade state incomplete.");
        return {};
      }
      // Swap card instances across hands[] (IMMUTABLE rule: only swap ids+instanceIds).
      const newHands = s.hands.map((h) => [...h]) as [
        CardInHand[],
        CardInHand[],
        CardInHand[],
        CardInHand[],
      ];
      const drawerHand = newHands[offerSeat];
      const responderHand = newHands[responderSeat];
      const [oid1, oid2] = offeredInstanceIds;
      const [rid1, rid2] = requestedInstanceIds;

      // Extract offered cards from drawer, requested from responder.
      const offeredCards = drawerHand.filter((c) => oid1 === c.instanceId || oid2 === c.instanceId);
      const remainingDrawer = drawerHand.filter(
        (c) => c.instanceId !== oid1 && c.instanceId !== oid2,
      );
      const requestedCards = responderHand.filter(
        (c) => rid1 === c.instanceId || rid2 === c.instanceId,
      );
      const remainingResponder = responderHand.filter(
        (c) => c.instanceId !== rid1 && c.instanceId !== rid2,
      );

      newHands[offerSeat] = [...remainingDrawer, ...requestedCards];
      newHands[responderSeat] = [...remainingResponder, ...offeredCards];

      // Mark tradeOfferedThisGame for the drawer.
      const newTradeOffered = [...s.tradeOfferedThisGame] as [boolean, boolean, boolean, boolean];
      newTradeOffered[offerSeat] = true;

      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "trade",
        seat: offerSeat,
        outcome: "accept",
        tokensDelta: 0,
      });

      return {
        hands: newHands,
        tradeOfferedThisGame: newTradeOffered,
        tradeOfferRejectionReason: null,
        pendingTrade: {
          ...s.pendingTrade,
          phase: "resolved" as const,
          decision: "accept" as const,
        },
      };
    }),

  rejectTrade: () =>
    set((s) => {
      if (s.pendingTrade === null) return {};
      if (s.pendingTrade.phase !== "respond") return {};
      const { offerSeat } = s.pendingTrade;

      // Penalise drawer (floor at 0).
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[offerSeat] = Math.max(0, (newTokens[offerSeat] ?? 0) - TRADE_REJECT_PENALTY);

      // Mark tradeOfferedThisGame for the drawer.
      const newTradeOffered = [...s.tradeOfferedThisGame] as [boolean, boolean, boolean, boolean];
      newTradeOffered[offerSeat] = true;

      console.info(
        `[rejectTrade] Seat ${s.pendingTrade.responderSeat} rejected. Seat ${offerSeat} loses ${TRADE_REJECT_PENALTY}T.`,
      );

      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "trade",
        seat: offerSeat,
        outcome: "reject",
        tokensDelta: -TRADE_REJECT_PENALTY,
      });

      return {
        tokens: newTokens,
        tradeOfferedThisGame: newTradeOffered,
        tradeOfferRejectionReason: null,
        pendingTrade: {
          ...s.pendingTrade,
          phase: "resolved" as const,
          decision: "reject" as const,
        },
      };
    }),

  dismissTrade: () =>
    set((s) => {
      if (s.pendingTrade === null) return {};
      if (s.pendingTrade.phase !== "resolved") return {};
      return { pendingTrade: null, tradeOfferRejectionReason: null };
    }),

  // ---------------------------------------------------------------------------
  // R19-GC Policy_Exchange GiveCard arm — pick a card from own hand to give
  // ---------------------------------------------------------------------------

  commitGiveCard: (giveInstanceId) =>
    set((s) => {
      if (s.pendingGiveCard === null) return {};
      const { victimSeat } = s.pendingGiveCard;
      const seat = s.currentSeat;
      const drawerHand = s.hands[seat] ?? [];
      const recipientHand = s.hands[victimSeat] ?? [];

      // Hand-full forfeit: if opponent is at MAX_HAND_SIZE, skip transfer silently.
      if (recipientHand.length >= MAX_HAND_SIZE) {
        console.info(
          `[commitGiveCard] Forfeit — seat ${victimSeat} hand full (${recipientHand.length}/${MAX_HAND_SIZE}).`,
        );
        return { pendingGiveCard: null };
      }

      // Validate: the card to give must still be in drawer's hand.
      const giveCard = drawerHand.find((c) => c.instanceId === giveInstanceId);
      if (giveCard === undefined) {
        console.warn(
          `[commitGiveCard] instanceId ${giveInstanceId} not found in seat ${seat} hand.`,
        );
        return { pendingGiveCard: null };
      }

      // Transfer.
      const newHands = s.hands.map((h) => [...h]) as [
        CardInHand[],
        CardInHand[],
        CardInHand[],
        CardInHand[],
      ];
      newHands[seat] = drawerHand.filter((c) => c.instanceId !== giveInstanceId);
      newHands[victimSeat] = [...recipientHand, giveCard];

      console.info(
        `[commitGiveCard] Seat ${seat} gave "${giveCard.id}" (${giveInstanceId}) to seat ${victimSeat}.`,
      );

      return { hands: newHands, pendingGiveCard: null };
    }),

  cancelGiveCard: () => set({ pendingGiveCard: null }),

  // ---------------------------------------------------------------------------
  // T-4 Pollution Tax actions (R16 Builder-BZ)
  // ---------------------------------------------------------------------------

  triggerPollutionTaxIfRolled: (seat) => {
    const s = useGameStore.getState();
    // Conflict matrix: boardAnimationActive → _isGameInputBlocked → gameStatus
    // → turnPhase → era → no pending* → drawer can afford → RNG roll.
    if (s.boardAnimationActive) return false;
    if (_isGameInputBlocked(s)) return false;
    if (s.gameStatus === "ended") return false;
    if (s.turnPhase !== "landed") return false;
    if (s.era < 3) return false;
    // R16 inline-fix: defensive tile-type check (caller is bureaucrat handler
    // which is Policy-tile-only, but guard anyway for direct-call safety).
    const tileIdx = s.playerTiles[seat] ?? 0;
    const tileType = _tileTypeByIndex[tileIdx] ?? "";
    if (tileType !== "Policy") return false;
    // Mutual-exclusion with all other minigames / event modals.
    if (s.pendingQuiz !== null) return false;
    if (s.pendingAuction !== null) return false;
    if (s.pendingClimateVote !== null) return false;
    if (s.pendingScout !== null) return false;
    if (s.pendingEnergyDilemma !== null) return false;
    if (s.pendingTrade !== null) return false;
    if (s.pendingPollutionTax !== null) return false;
    // Drawer must be able to afford (informational gate — they can still decline).
    if ((s.tokens[seat] ?? 0) < POLLUTION_TAX_AMOUNT) return false;
    // 10% RNG roll.
    if (_rng.next() >= POLLUTION_TAX_TRIGGER_CHANCE) return false;
    // Pre-compute proportional cuts so the modal can display a preview.
    const proportionalCuts = computeProportionalPollutionReduction(
      s.pollution,
      POLLUTION_TAX_REDUCTION,
      s.numPlayers,
    ) as [number, number, number, number];
    console.info(
      `[triggerPollutionTaxIfRolled] Tax fired for seat ${seat}. Proportional cuts: ${proportionalCuts.join(",")}`,
    );
    useGameStore.setState({
      pendingPollutionTax: {
        drawerSeat: seat,
        taxAmount: POLLUTION_TAX_AMOUNT,
        reductionAmount: POLLUTION_TAX_REDUCTION,
        proportionalCuts,
      },
    });
    return true;
  },

  payPollutionTax: () =>
    set((s) => {
      if (s.pendingPollutionTax === null) return {};
      const { drawerSeat, taxAmount, proportionalCuts } = s.pendingPollutionTax;
      // Deduct tax from drawer (floor 0).
      const newTokens = [...s.tokens] as [number, number, number, number];
      newTokens[drawerSeat] = Math.max(0, (newTokens[drawerSeat] ?? 0) - taxAmount);
      // Reduce per-seat pollution by the pre-computed proportional cuts.
      const newPollution = s.pollution.map((p, i) =>
        Math.max(0, p - (proportionalCuts[i as SeatIndex] ?? 0)),
      ) as [number, number, number, number];
      console.info(
        `[payPollutionTax] Seat ${drawerSeat} paid ${taxAmount}T. New pollution: ${newPollution.join(",")}`,
      );
      // R16 /goal Fix A: emit analytics event for T-4 result.
      _recordAnalyticsEvent({
        type: "minigame-result",
        t: Date.now(),
        minigame: "pollution-tax",
        seat: drawerSeat,
        outcome: "paid",
        tokensDelta: -taxAmount,
      });
      // Re-run game-end detection in case the tax-induced token loss causes bankruptcy.
      const endState = detectGameEnd(
        {
          tokens: newTokens,
          energy: s.energy,
          pollution: newPollution,
          round: s.round,
          lostSeats: s.lostSeats,
          builtFacilities: s.builtFacilities as Record<number, string | undefined>,
          ownedBy: s.ownedBy as Record<number, number | undefined>,
        },
        s.lostSeats,
        s.numPlayers,
      );
      return {
        pendingPollutionTax: null,
        tokens: newTokens,
        pollution: newPollution,
        gameStatus: endState.status,
        endReason: endState.reason,
        winnerSeat: endState.winnerSeat,
        winnerSeats: endState.winnerSeats,
        lostSeats: endState.lostSeats,
      };
    }),

  declinePollutionTax: () =>
    set((s) => {
      if (s.pendingPollutionTax === null) return {};
      console.info(`[declinePollutionTax] Seat ${s.pendingPollutionTax.drawerSeat} declined.`);
      return { pendingPollutionTax: null };
    }),

  // ---------------------------------------------------------------------------
  // CS.4 — Mae Cameo cute layer (R15 Builder-AU) — initial state + actions
  // ---------------------------------------------------------------------------

  pendingMaeCameo: null as GameState["pendingMaeCameo"],
  firstQuizShown: false,
  firstClimateVoteShown: false,

  setMaeCameo: (mood, line) => set({ pendingMaeCameo: { mood, line } }),
  dismissMaeCameo: () => set({ pendingMaeCameo: null }),
}));

// Dev-only: expose store on window for Playwright/manual debugging.
if (typeof window !== "undefined" && import.meta.env.DEV) {
  (window as unknown as { useGameStore?: typeof useGameStore }).useGameStore = useGameStore;
}
